import { FormEvent, forwardRef, type MouseEvent, type ReactNode, type RefObject, type UIEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ArrowDown,
  ArrowLeft,
  AlertCircle,
  BookMarked,
  BookOpen,
  Bell,
  Check,
  CheckCircle2,
  ChevronRight,
  CirclePause,
  Compass,
  Copy,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Flag,
  Heart,
  HeartHandshake,
  Home,
  Image as ImageIcon,
  Info,
  LockKeyhole,
  Loader2,
  LogOut,
  Menu,
  MailCheck,
  MessageCircle,
  Moon,
  Reply,
  Rocket,
  Send,
  Settings,
  Share,
  Shield,
  Smartphone,
  Sparkles,
  Sun,
  Trash2,
  User,
  X,
} from "lucide-react";
import { api, ApiError, formatApiError, getWebSocketUrl } from "./api";
import { useAuth } from "./auth";
import { confirmSignedInEmailIsVerified } from "./emailVerification";
import { saveProfileWithPhotoRollback } from "./profilePhotoWorkflow";
import { protectedPhotoAccessTracker } from "./protectedPhotoCache";
import {
  InstallPromptProvider,
  resolveInstallPromptSurface,
  shouldPrioritizeInstalledNotificationPrompt,
  useInstallPrompt,
  type InstallPlatform,
  type InstallPromptContextValue,
  type InstallPromptSurface,
} from "./installPrompt";
import { ONBOARDING_STATUS_QUERY_KEY, isUnauthorizedApiError, onboardingPath, onboardingStatusQueryOptions, resolveAuthenticatedPath } from "./onboarding";
import { buildOnboardingPreferenceDefaults } from "./onboardingPreferenceDefaults";
import { inviteEmailStatusLabel } from "./inviteEmailStatus";
import { inviteCanCancel, inviteCanShowResend, inviteResendButtonLabel } from "./inviteResend";
import { refreshWebPushSubscription, subscribeToWebPush } from "./pwa";
import { parseSignupInvitePrefill } from "./signupPrefill";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, passwordRequirements, validatePasswordResetInput } from "./passwordRecovery";
import { applyThemeColorSurface, getAppliedTheme, setThemePreference, subscribeToThemeChanges } from "./theme";
import {
  APP_NAME,
  APP_TAGLINE,
  DRINKING_OPTIONS,
  EDUCATION_LEVELS,
  MIN_EDUCATION_LEVELS,
  FOOD_PREFERENCES,
  INTERESTS,
  MARITAL_STATUSES,
  RELIGIONS,
  SMOKING_OPTIONS,
  WANTS_CHILDREN_OPTIONS,
} from "./constants";
import type { CurrentMatch, DeclineFeedbackCategory, FeedbackType, Language, MatchProfile, NotificationSettings, Preferences, Profile, WSChatMessage } from "./types";
import { ChatMessageList } from "./components/chat";
import { ModerationChatReviewPage } from "./admin/ModerationChatReviewPage";
import { AdminUserDetailPage, AdminUsersPage } from "./admin/AdminPages";
import { MatchQualityPage } from "./admin/MatchQualityPage";
import { InitialMatchingLaunchPage } from "./admin/InitialMatchingLaunchPage";
import { ManualMatchingPage } from "./admin/ManualMatchingPage";
import packageJson from "../package.json";
import {
  AppHeader,
  AppHero,
  AppScreenContent,
  AppSelectField,
  AppStateScreen,
  AgeRangePicker,
  ageFromDateOfBirth,
  HeightRangePicker,
  BrandedLoading,
  ConnectedCelebration,
  formatLocationLine,
  getMatchNavIconState,
  MatchNavIcon,
  NoMatchView,
  OnboardingFormLayout,
  OnboardingReadyMoment,
  PendingMatchHeader,
  PhotoViewerScreen,
  ProfilePhoto,
  ProtectedImage,
  ProfileDetails,
  ProfileEditorScreen,
  RejectedMatchView,
  StackNavLink,
  WaitingMatchView,
  buildPhotoViewerUrl,
  navigateWithStackDirection,
  getNavBackTarget,
  parsePhotoViewerParams,
  BRAND_ASSETS,
} from "./components/app";
import { useIsMobile } from "./components/app/useIsMobile";
import {
  profileGalleryPhotoItems,
  profilePhotoItems,
  profilePicturePhotoId,
} from "./components/app/profilePhotoPresentation";

const APP_VERSION = packageJson.version;
const NOTIFICATION_ONBOARDING_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const HOW_YARA_WORKS_STEPS = [
  {
    title: "Invite-Only Community",
    body: "Members invite trusted people, helping Yara stay safer, warmer, and more intentional.",
    icon: Shield,
    tone: "invite",
  },
  {
    title: "Create Your Profile",
    body: "Share your values, interests, photos, and what you are hoping to build with someone.",
    icon: User,
    tone: "profile",
  },
  {
    title: "Thoughtful Matching",
    body: "Yara offers one potential introduction at a time, so each person gets real attention.",
    icon: Heart,
    tone: "match",
  },
  {
    title: "Private Choice",
    body: "Answer privately. A no stays quiet; chat opens only if both people choose yes.",
    icon: Check,
    tone: "respond",
  },
  {
    title: "Meaningful Conversations",
    body: "Once connected, full profiles and private chat help you get to know each other.",
    icon: MessageCircle,
    tone: "chat",
  },
] as const;

const UNOFFICIAL_DISCLAIMER =
  "Yara is an individual, unofficial initiative. It is not an official Baha'i website and is not affiliated with or endorsed by any Baha'i institution. The official Baha'i website is www.bahai.org.";

const UNOFFICIAL_FAQ_ANSWER =
  "Yara is an individual, unofficial initiative. It is not an official Baha'i website and is not affiliated with or endorsed by any Baha'i institution. We are trying, humbly and imperfectly, to design Yara in light of the Baha'i writings and principles around character, chastity, consultation, family, and marriage. The official Baha'i website is www.bahai.org.";

const PUBLIC_FOOTER_NOTE = "Not an official Baha'i website.";

const FAITH_GUIDED_NOTE =
  "Yara is being built by Baha'is who are trying, humbly and imperfectly, to design a safer flow in light of the Baha'i writings and guidance around consent, chastity, consultation, family, and marriage.";

const SIGNUP_COMMUNITY_NOTE =
  "Yara is an unofficial Baha'i-inspired community effort for respectful, character-led, marriage-minded introductions.";

const LANDING_HEADLINES = [
  "Thoughtful introductions",
  "One introduction at a time. No endless swiping.",
  "Private, paced, and character-led matching",
] as const;

const LANDING_HERO_LEDE_SHORT =
  "Friend-of-a-friend introductions shaped around character, family, service, and a calmer path toward marriage.";

const LANDING_HERO_PROOF_SHORT = "One introduction at a time—character before chemistry.";

const LANDING_HERO_GLANCE = [
  {
    title: "Join by invite",
    body: "Start through someone trusted—no open sign-up or public browsing.",
    icon: LockKeyhole,
  },
  {
    title: "One introduction at a time",
    body: "When you're eligible, Yara introduces one person—not a feed to swipe.",
    icon: Compass,
  },
  {
    title: "You both choose",
    body: "Chat opens only if you both accept. A no stays private.",
    icon: HeartHandshake,
  },
] as const;

const LANDING_ROADMAP_STEPS = [
  {
    phase: "Inside Yara",
    title: "Trusted invite",
    body: "Use a code from someone already in the community.",
    icon: LockKeyhole,
  },
  {
    phase: "Inside Yara",
    title: "Character-led profile",
    body: "Share faith, family, service, daily life, and hopes.",
    icon: User,
  },
  {
    phase: "Inside Yara",
    title: "One introduction",
    body: "Review one person at a time, not a feed.",
    icon: Compass,
  },
  {
    phase: "Private choice",
    title: "Private yes or no",
    body: "Both answer privately; a no stays quiet.",
    icon: CheckCircle2,
  },
  {
    phase: "Mutual connection",
    title: "Chat if mutual",
    body: "Chat opens only after both people say yes.",
    icon: MessageCircle,
  },
  {
    phase: "Take it forward",
    title: "Discern beyond Yara",
    body: "If interest grows, consult, pray, and move with care.",
    icon: HeartHandshake,
  },
] as const;

const LANDING_AUDIENCE = [
  "Baha'is seeking marriage-minded introductions",
  "Friends of the Faith who share this purpose",
  "A friend-of-a-friend community, grown by trusted invites",
] as const;

const LANDING_PRINCIPLES = [
  {
    title: "Character before chemistry",
    body: "Profiles and prompts are designed to reveal patterns: truthfulness, steadiness, service, family relationships, and how someone handles pressure.",
    icon: BookOpen,
  },
  {
    title: "Consultation without pressure",
    body: "The first yes or no stays private. If interest grows, Yara points people toward prayer, trusted counsel, and thoughtful family conversations.",
    icon: MessageCircle,
  },
  {
    title: "A home that serves",
    body: "The goal is not attention inside an app. It is helping people consider whether they could build a warm, hospitable, service-oriented family life.",
    icon: HeartHandshake,
  },
] as const;

const LANDING_FAQ = [
  {
    question: "How do I join Yara?",
    answer: "Join with a code from someone already in the community. Yara grows through trusted invitations, not open registration.",
  },
  {
    question: "Who is Yara for?",
    answer:
      "Baha'is and friends of the Faith who want marriage-minded introductions—not casual dating or endless browsing. Members join by invite and complete a thoughtful profile before matching begins.",
  },
  {
    question: "Is Yara an official Baha'i website?",
    answer: UNOFFICIAL_FAQ_ANSWER,
  },
  {
    question: "How does an introduction work?",
    answer:
      "When you are eligible, Yara sends one introduction at a time—never a feed to swipe. You each answer in private, with space to consider character rather than react to a queue. Chat opens only if you both choose yes.",
  },
  {
    question: "What happens if one of us is not interested?",
    answer:
      "A no stays quiet—the other person is not told how you answered. There is no public rejection feed, and Yara moves on when the time is right.",
  },
  {
    question: "When will I receive an introduction?",
    answer:
      "After your profile is complete and you are eligible for a matching round, Yara introduces one person at a time—not a queue you swipe through.",
  },
  {
    question: "What happens after we both accept?",
    answer:
      "You can chat inside Yara about faith, character, family, service, and practical life. If interest grows, discernment continues beyond the app—with consultation, prayer, and trusted people involved at a healthy pace.",
  },
] as const;

const LANDING_RESOURCE_CHIPS = [
  { label: "Faith & purpose", slug: "faith-purpose" },
  { label: "Character & patterns", slug: "character-patterns" },
  { label: "Practical life", slug: "practical-life" },
  { label: "Safety & boundaries", slug: "safety-boundaries" },
] as const;

const SAFETY_RAILS = [
  { label: "Private first", icon: LockKeyhole },
  { label: "Report tools", icon: Flag },
  { label: "Clear limits", icon: Shield },
] as const;

const SAFETY_GUIDELINES = [
  {
    title: "Stay in Yara at first",
    body: "Keep early conversations in the app until trust has had time to grow. Be cautious if someone quickly pushes you elsewhere.",
  },
  {
    title: "Never send money",
    body: "Do not send money, gift cards, crypto, bank details, identity documents, or verification codes to someone you have only met online.",
  },
  {
    title: "Share private details carefully",
    body: "Protect your address, workplace, travel plans, family details, and intimate photos. Trust should be proven over time.",
  },
  {
    title: "Meet with care",
    body: "If you meet in person, choose a public place, tell someone you trust, arrange your own transportation, and keep your phone available.",
  },
  {
    title: "Report pressure or harm",
    body: "Report harassment, impersonation, explicit content, scams, threats, or any behavior that feels unsafe or inconsistent with Yara's purpose.",
  },
] as const;

const SAFETY_NAV_ITEMS = [
  { id: "safety-limits", label: "Limits" },
  { id: "safety-habits", label: "Habits" },
  { id: "safety-report", label: "Report" },
] as const;

const SAFETY_SECTION_IDS = SAFETY_NAV_ITEMS.map((item) => item.id);

const SAFETY_LIMIT_NOTES = [
  "Yara does not verify every claim a member makes about identity, faith status, relationship status, character, location, work, education, or intentions.",
  "Yara does not promise matches, relationships, compatibility, marriage, or offline safety.",
] as const;

const LANDING_SAFETY_FEATURES = [
  {
    icon: LockKeyhole,
    title: "Private at first",
    body: "Both people choose before chat opens, and early conversation can stay in Yara.",
  },
  {
    icon: Flag,
    title: "Report pressure",
    body: "Flag scams, harassment, or behavior that does not fit Yara's purpose.",
  },
  {
    icon: Shield,
    title: "Protect details",
    body: "Share contact, location, and family details only when trust has had time to grow.",
  },
] as const;

const RESOURCE_REF_GROUPS = [
  {
    id: "posture",
    title: "Start with the right posture",
    lede: "Becoming thoroughly acquainted with character should be marriage-minded and dignified—not a game, a feed, or a performance.",
  },
  {
    id: "know",
    title: "Learn who someone is",
    lede: "Look beyond attraction to character, habits of consultation, unity of purpose, and the life you could build.",
  },
  {
    id: "forward",
    title: "Move toward clarity together",
    lede: "As interest grows, honor consent, family harmony, consultation, and the home you hope to create.",
  },
] as const;

const RESOURCE_REFERENCES = [
  {
    group: "posture",
    principle: "Marriage as a law of protection",
    quote: "And when He desired to manifest grace and beneficence to men, and to set the world in order, He revealed observances and created laws; among them He established the law of marriage, made it as a fortress for well-being and salvation, and enjoined it upon us in that which was sent down out of the heaven of sanctity in His Most Holy Book.",
    resourceNote: "Marriage is presented as a divine law meant to protect well-being, spiritual growth, and social order. It asks people to approach marriage as a serious, life-shaping institution rather than a private impulse or casual preference.",
    citation: "Bahá’u’lláh, Bahá’í Prayers: A Selection of Prayers Revealed by Bahá’u’lláh, the Báb, and ‘Abdu’l-Bahá, Marriage",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/prayers/bahai-prayers/3#939569264",
    guidance: "Treat marriage-minded search as dignified and protective, not as entertainment, status, or performance.",
  },
  {
    group: "posture",
    principle: "Begin from equality",
    quote: "All should know, and in this regard attain the splendours of the sun of certitude, and be illumined thereby: Women and men have been and will always be equal in the sight of God. The Dawning-Place of the Light of God sheddeth its radiance upon all with the same effulgence.",
    resourceNote: "Men and women have the same spiritual worth before God. A marriage-minded conversation should begin from mutual dignity, not from superiority, control, or gendered entitlement.",
    citation: "Bahá’u’lláh, from a Tablet translated from Arabic and Persian; Family Life, no. 3",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/2#636565884",
    guidance: "Mutual dignity belongs at the foundation—not dominance, entitlement, or possessiveness.",
  },
  {
    group: "posture",
    principle: "Character is central",
    quote: "A good character is, verily, the best mantle for men from God. With it He adorneth the temples of His loved ones. By My life! The light of a good character surpasseth the light of the sun and the radiance thereof. Whoso attaineth unto it is accounted as a jewel among men.",
    resourceNote: "Character is not a decorative extra; it is one of the most important signs of spiritual maturity. The passage points you toward steady virtues like honesty, trustworthiness, humility, and service.",
    citation: "Bahá’u’lláh, Tablets of Bahá’u’lláh Revealed after the Kitáb-i-Aqdas, Ṭarázát, no. 4.12",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/bahaullah/tablets-bahaullah/2#618056629",
    guidance: "Pay attention to truthfulness, humility, patience, service, and how someone acts when no one is performing.",
  },
  {
    group: "know",
    principle: "Know character before commitment",
    quote: "Bahá’í marriage is the commitment of the two parties one to the other, and their mutual attachment of mind and heart. Each must, however, exercise the utmost care to become thoroughly acquainted with the character of the other, that the binding covenant between them may be a tie that will endure forever. Their purpose must be this: to become loving companions and comrades and at one with each other for time and eternity.",
    resourceNote: "Affection is not enough by itself. Before making a binding commitment, two people should take real care to learn each other's character and whether they can become lasting companions.",
    citation: "‘Abdu’l-Bahá, Selections from the Writings of ‘Abdu’l-Bahá, no. 86.1",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/abdul-baha/selections-writings-abdul-baha/5#203225772",
    guidance: "Take time to notice truthfulness, patience, service, family relationships, emotional steadiness, and the ability to consult lovingly.",
  },
  {
    group: "know",
    principle: "A true bond is spiritual and physical",
    quote: "The true marriage of Bahá’ís is this, that husband and wife should be united both physically and spiritually, that they may ever improve the spiritual life of each other, and may enjoy everlasting unity throughout all the worlds of God. This is Bahá’í marriage.",
    resourceNote: "Marriage is meant to unite more than attraction or daily logistics. The relationship should help both people grow spiritually and support one another across the whole course of life.",
    citation: "‘Abdu’l-Bahá, Selections from the Writings of ‘Abdu’l-Bahá, no. 86.2",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/abdul-baha/selections-writings-abdul-baha/5#168540347",
    guidance: "Attraction matters, but also notice shared spiritual orientation and whether you can help each other grow.",
  },
  {
    group: "know",
    principle: "Choice is personal and serious",
    quote: "As for the question regarding marriage under the Law of God: first thou must choose one who is pleasing to thee, and then the matter is subject to the consent of father and mother. Before thou makest thy choice, they have no right to interfere.",
    resourceNote: "The initial choice belongs to the person considering marriage. Parents are not meant to choose for them before that choice is made, but their consent becomes part of the Baha'i marriage process afterward.",
    citation: "‘Abdu’l-Bahá, Selections from the Writings of ‘Abdu’l-Bahá, no. 85",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/abdul-baha/selections-writings-abdul-baha/5#463862684",
    guidance: "Discern directly and honestly before involving family consent, advice, or next steps.",
  },
  {
    group: "forward",
    principle: "Respect consent and family harmony",
    quote: "It hath been laid down in the Bayán that marriage is dependent upon the consent of both parties. Desiring to establish love, unity and harmony amidst Our servants, We have conditioned it, once the couple's wish is known, upon the permission of their parents, lest enmity and rancor should arise amongst them. And in this We have yet other purposes. Thus hath Our commandment been ordained.",
    resourceNote: "Marriage requires the consent of both people, and Baha'i law also asks for parental permission once the couple wishes to marry. The purpose is to preserve love, unity, and harmony among the families involved.",
    citation: "Bahá’u’lláh, The Kitáb-i-Aqdas, par. 65",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/bahaullah/kitab-i-aqdas/6#432262177",
    guidance: "Your choice matters—and Baha'i marriage also invites parental consent in a spirit of love, unity, and harmony.",
  },
  {
    group: "forward",
    principle: "Consult toward a sound decision",
    quote: "Now, ye must consult with each other, confer with the utmost love, agree upon a sound decision, and be fully united, for husband and wife must be even as one person, that they may succeed in every matter.",
    resourceNote: "Serious decisions should be made through loving consultation, not pressure, avoidance, or one person dominating. The test is whether both people can discuss hard choices and arrive at unity together.",
    citation: "‘Abdu’l-Bahá, from a Tablet translated from Persian; Family Life, no. 14",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/2#050515710",
    guidance: "As interest grows, ask whether you can confer with love, listen seriously, and move toward wise next steps together.",
  },
  {
    group: "forward",
    principle: "Family life grows through harmony",
    quote: "If love and agreement are manifest in a single family, that family will advance, become illumined and spiritual; but if enmity and hatred exist within it, destruction and dispersion are inevitable.",
    resourceNote: "The emotional and spiritual climate of a family matters. Love and agreement help a family grow, while hostility and resentment damage the home from within.",
    citation: "‘Abdu’l-Bahá, The Promulgation of Universal Peace, pp. 144-145; Family Life, no. 86",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/8#262647825",
    guidance: "Look for agreement without erasing difference, kindness under strain, and a shared desire for spiritual growth.",
  },
  {
    group: "forward",
    principle: "Home can become spiritually alive",
    quote: "I beseech God to graciously make of thy home a centre for the diffusion of the light of divine guidance, for the dissemination of the Words of God and for enkindling at all times the fire of love in the hearts of His faithful servants and maidservants. Know thou of a certainty that every house wherein the anthem of praise is raised to the Realm of Glory in celebration of the Name of God is indeed a heavenly home, and one of the gardens of delight in the Paradise of God.",
    resourceNote: "A home becomes spiritually alive when it remembers God, shares the Word of God, and kindles love in the hearts of others. The passage describes the home as more than a private refuge; it can be a place of warmth, worship, and service.",
    citation: "‘Abdu’l-Bahá, from a Tablet translated from Arabic; Family Life, no. 85",
    citationUrl: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/8#047880199",
    guidance: "Ask what kind of home you hope to build together: prayerful, hospitable, service-oriented, truthful, and warm.",
  },
] as const;

const RESOURCE_QUESTION_GROUPS = [
  {
    title: "Faith and purpose",
    slug: "faith-purpose",
    questions: [
      "What role do prayer, the Writings, service, and community life actually play in your week?",
      "What kind of home culture are you hoping to build: prayerful, hospitable, service-oriented, and warm?",
      "How do you hope marriage would strengthen your ability to serve, not pull you away from service?",
    ],
  },
  {
    title: "Character and patterns",
    slug: "character-patterns",
    questions: [
      "How do you act when you are disappointed, tired, embarrassed, or under pressure?",
      "What would close friends and family say they rely on you for?",
      "Can you name a pattern of thought, speech, or conduct you are actively trying to refine?",
    ],
  },
  {
    title: "Practical life",
    slug: "practical-life",
    questions: [
      "How do you think about money, work, education, relocation, children, and caring for family?",
      "How do you seek coherence between spiritual priorities, material responsibilities, rest, and service?",
      "When should family, trusted friends, or institutions be brought into consultation?",
    ],
  },
  {
    title: "Safety and boundaries",
    slug: "safety-boundaries",
    questions: [
      "Do I feel freer, calmer, and more truthful after talking with this person?",
      "Are they respecting my pace, privacy, family, and conscience?",
      "How do we each understand chastity, purity, modesty, and boundaries in online and offline choices?",
    ],
  },
] as const;

const RESOURCE_NAV_ITEMS = [
  { id: "resource-on-yara", label: "On Yara" },
  { id: "questions", label: "Questions" },
  { id: "faith-purpose", label: "Faith" },
  { id: "character-patterns", label: "Character" },
  { id: "practical-life", label: "Practical" },
  { id: "safety-boundaries", label: "Safety" },
  { id: "references", label: "References" },
] as const;

const RESOURCE_SECTION_IDS = RESOURCE_NAV_ITEMS.map((item) => item.id);

const RESOURCE_SOURCE_LINKS = [
  { label: "Family Life compilation", href: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/" },
  { label: "Marriage bond", href: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/2" },
  { label: "Enhancing family life", href: "https://www.bahai.org/library/authoritative-texts/compilations/family-life/8" },
  { label: "The Kitab-i-Aqdas", href: "https://www.bahai.org/r/434961812" },
  { label: "Baha'i Prayers: Marriage", href: "https://www.bahai.org/library/authoritative-texts/prayers/bahai-prayers/3" },
  { label: "Selections from 'Abdu'l-Baha", href: "https://www.bahai.org/library/authoritative-texts/abdul-baha/selections-writings-abdul-baha/5" },
  { label: "Tablets of Baha'u'llah", href: "https://www.bahai.org/library/authoritative-texts/bahaullah/tablets-bahaullah/2" },
] as const;

const RESOURCE_YARA_TIPS = [
  "One introduction at a time—read and respond with care within your window.",
  "A quiet no is allowed; a yes is permission to begin conversation, not a promise of marriage.",
  "If interest grows, consult trusted people and take discernment off-app at a healthy pace.",
] as const;

const RESOURCE_THEME_ICONS = {
  "faith-purpose": Compass,
  "character-patterns": HeartHandshake,
  "practical-life": Home,
  "safety-boundaries": Shield,
} as const;

const ELIGIBILITY_ATTESTATIONS = [
  "I am at least 18 years old.",
  "I am legally single and using Yara for myself.",
  "I will be honest about my identity, intentions, and relationship status.",
  "I understand Yara is unofficial and invite-only.",
] as const;

type ReportType = "inappropriate_behavior" | "inappropriate_message" | "harassment" | "fake_profile" | "other";

const REPORT_CATEGORIES = [
  { value: "inappropriate_behavior", label: "Behavior" },
  { value: "inappropriate_message", label: "Message" },
  { value: "harassment", label: "Harassment" },
  { value: "fake_profile", label: "Fake profile" },
  { value: "other", label: "Other" },
] as const satisfies readonly { value: ReportType; label: string }[];

const FEEDBACK_CATEGORIES = [
  { value: "general_feedback", label: "General" },
  { value: "bug_report", label: "Bug" },
  { value: "feature_request", label: "Feature" },
  { value: "improvement_suggestion", label: "Idea" },
  { value: "want_to_help", label: "Help" },
] as const satisfies readonly { value: FeedbackType; label: string }[];

function BrandMark({ className }: { className: string }) {
  return (
    <span className={`brand-mark ${className}`} aria-hidden="true">
      <img className="brand-mark-light" src={BRAND_ASSETS.markLight} alt="" />
      <img className="brand-mark-dark" src={BRAND_ASSETS.markDark} alt="" />
    </span>
  );
}

function SidebarLogo() {
  return (
    <span className="sidebar-logo" aria-label={APP_NAME} role="img">
      <BrandMark className="sidebar-logo-mark" />
      <span className="sidebar-wordmark" aria-hidden="true">
        <img className="sidebar-wordmark-light" src={BRAND_ASSETS.sidebarWordmarkLight} alt="" />
        <img className="sidebar-wordmark-dark" src={BRAND_ASSETS.sidebarWordmarkDark} alt="" />
      </span>
    </span>
  );
}

const Button = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; loading?: boolean }>(function Button(props, ref) {
  const { className = "", variant = "primary", loading, children, disabled, ...rest } = props;
  return (
    <button ref={ref} className={`button ${variant} ${className}`} disabled={disabled || loading} {...rest}>
      {loading && <Loader2 className="spin" size={16} />}
      {children}
    </button>
  );
});

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}

function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  preferMobileControls = false,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  preferMobileControls?: boolean;
}) {
  return <AppSelectField label={label} value={value} onChange={onChange} options={options} preferMobileControls={preferMobileControls} />;
}

function ErrorBanner({ error }: { error?: unknown }) {
  if (!error) return null;
  return <div className="banner error" role="alert">{formatApiError(error)}</div>;
}

function useOfflineStatus() {
  const [status, setStatus] = useState(() => ({
    offline: typeof navigator !== "undefined" ? !navigator.onLine : false,
    cached: false,
  }));

  useEffect(() => {
    const syncFromNavigator = () => {
      setStatus((current) => ({
        offline: !navigator.onLine,
        cached: !navigator.onLine ? current.cached : false,
      }));
    };
    const handleConnectionState = (event: Event) => {
      const detail = (event as CustomEvent<{ offline?: boolean; cached?: boolean }>).detail;
      if (!detail) return;
      setStatus({
        offline: Boolean(detail.offline),
        cached: Boolean(detail.cached),
      });
    };

    syncFromNavigator();
    window.addEventListener("online", syncFromNavigator);
    window.addEventListener("offline", syncFromNavigator);
    window.addEventListener("yara:connection-state", handleConnectionState);
    return () => {
      window.removeEventListener("online", syncFromNavigator);
      window.removeEventListener("offline", syncFromNavigator);
      window.removeEventListener("yara:connection-state", handleConnectionState);
    };
  }, []);

  return status;
}

function OfflineStatusBanner() {
  const status = useOfflineStatus();
  if (!status.offline) return null;

  return (
    <aside className="offline-status-banner" role="status" aria-live="polite">
      <AlertCircle size={16} aria-hidden="true" />
      <span>{status.cached ? "Connection issue. Showing your latest saved Yara." : "You are offline. Yara will keep trying to reconnect."}</span>
    </aside>
  );
}

const KEYBOARD_INPUT_TYPES = new Set(["date", "datetime-local", "email", "month", "number", "password", "search", "tel", "text", "time", "url", "week"]);
const CHAT_SCROLL_KEY_PREFIX = "yara:chat-scroll:";
const CHAT_HISTORY_KEY_PREFIX = "yara:chat-history:";
const CHAT_BOTTOM_THRESHOLD = 48;
const CHAT_FADE_BOTTOM_THRESHOLD = 2;
const CHAT_FADE_TOP_THRESHOLD = 2;
const CHAT_SCROLL_LATEST_THRESHOLD = 96;
const KEYBOARD_HEIGHT_THRESHOLD = 80;

function isKeyboardInput(element: Element | null) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (element instanceof HTMLTextAreaElement) return true;
  if (!(element instanceof HTMLInputElement)) return false;
  return KEYBOARD_INPUT_TYPES.has(element.type || "text");
}

function getScrollBottom(element: HTMLElement) {
  return element.scrollHeight - element.scrollTop - element.clientHeight;
}

function readChatScroll(matchId: string) {
  try {
    const value = window.sessionStorage?.getItem(`${CHAT_SCROLL_KEY_PREFIX}${matchId}`);
    return value === undefined ? null : value;
  } catch {
    return null;
  }
}

function writeChatScroll(matchId: string, scrollTop: number) {
  try {
    window.sessionStorage?.setItem(`${CHAT_SCROLL_KEY_PREFIX}${matchId}`, String(scrollTop));
  } catch {
    // Storage can be unavailable in constrained browser contexts.
  }
}

function readCachedChatMessages(matchId: string): WSChatMessage[] {
  try {
    const value = window.localStorage?.getItem(`${CHAT_HISTORY_KEY_PREFIX}${matchId}`);
    const messages = value ? JSON.parse(value) : [];
    return Array.isArray(messages) ? messages : [];
  } catch {
    return [];
  }
}

function writeCachedChatMessages(matchId: string, messages: WSChatMessage[]) {
  try {
    window.localStorage?.setItem(`${CHAT_HISTORY_KEY_PREFIX}${matchId}`, JSON.stringify(messages.slice(-250)));
  } catch {
    // Chat still works without persisted history if storage is constrained.
  }
}

function mergeChatMessages(messages: WSChatMessage[]) {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}

function useKeyboardViewport() {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const mobileViewportQuery = window.matchMedia("(max-width: 719px)");
    let stableViewportHeight = Math.max(window.innerHeight, document.documentElement.clientHeight, viewport?.height || 0);
    let currentKeyboardOpen = false;
    let viewportFrame: number | undefined;
    let immediateFocusTimer: number | undefined;
    const focusTimers: number[] = [];
    const resumeTimers: number[] = [];

    const getViewportMetrics = () => {
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      return { height, offsetTop };
    };

    const update = () => {
      const { height, offsetTop } = getViewportMetrics();
      const focusedInput = isKeyboardInput(document.activeElement);
      const mobileViewport = mobileViewportQuery.matches;
      const layoutHeight = Math.max(window.innerHeight, document.documentElement.clientHeight, stableViewportHeight);
      const viewportDelta = Math.max(0, stableViewportHeight - height);
      const keyboardInset = Math.max(0, layoutHeight - height - offsetTop, viewportDelta);
      const measuredKeyboardOpen = viewport
        ? keyboardInset > KEYBOARD_HEIGHT_THRESHOLD || viewportDelta > KEYBOARD_HEIGHT_THRESHOLD
        : focusedInput;
      const nextKeyboardOpen = mobileViewport && measuredKeyboardOpen && (focusedInput || currentKeyboardOpen || !viewport);

      if (!nextKeyboardOpen && !focusedInput) {
        stableViewportHeight = Math.max(height, window.innerHeight, document.documentElement.clientHeight);
      }

      root.style.setProperty("--app-visual-viewport-height", `${nextKeyboardOpen ? height : stableViewportHeight}px`);
      root.style.setProperty("--app-visual-viewport-top", `${nextKeyboardOpen ? offsetTop : 0}px`);

      currentKeyboardOpen = nextKeyboardOpen;
      setKeyboardOpen((current) => (current === nextKeyboardOpen ? current : nextKeyboardOpen));
    };

    const scheduleUpdate = () => {
      if (viewportFrame !== undefined) window.cancelAnimationFrame(viewportFrame);
      viewportFrame = window.requestAnimationFrame(update);
    };

    const refreshAfterResume = () => {
      if (document.visibilityState === "hidden") return;

      scheduleUpdate();
      resumeTimers.splice(0).forEach((timer) => window.clearTimeout(timer));
      [80, 240, 600].forEach((delay) => {
        resumeTimers.push(window.setTimeout(() => {
          if (document.visibilityState !== "hidden") scheduleUpdate();
        }, delay));
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshAfterResume();
    };

    const updateAfterFocusChange = () => {
      window.clearTimeout(immediateFocusTimer);
      focusTimers.splice(0).forEach((timer) => window.clearTimeout(timer));
      immediateFocusTimer = window.setTimeout(update, 0);
      [80, 180, 320, 520].forEach((delay) => {
        focusTimers.push(window.setTimeout(update, delay));
      });
    };

    refreshAfterResume();
    document.addEventListener("focusin", updateAfterFocusChange);
    document.addEventListener("focusout", updateAfterFocusChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("pageshow", refreshAfterResume);
    window.addEventListener("orientationchange", refreshAfterResume);
    viewport?.addEventListener("resize", scheduleUpdate);
    viewport?.addEventListener("scroll", scheduleUpdate);

    return () => {
      if (viewportFrame !== undefined) window.cancelAnimationFrame(viewportFrame);
      window.clearTimeout(immediateFocusTimer);
      focusTimers.splice(0).forEach((timer) => window.clearTimeout(timer));
      resumeTimers.splice(0).forEach((timer) => window.clearTimeout(timer));
      document.removeEventListener("focusin", updateAfterFocusChange);
      document.removeEventListener("focusout", updateAfterFocusChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("pageshow", refreshAfterResume);
      window.removeEventListener("orientationchange", refreshAfterResume);
      viewport?.removeEventListener("resize", scheduleUpdate);
      viewport?.removeEventListener("scroll", scheduleUpdate);
      root.style.removeProperty("--app-visual-viewport-height");
      root.style.removeProperty("--app-visual-viewport-top");
    };
  }, []);

  return { keyboardOpen };
}

function LoadingScreen({
  message = "Loading",
  help,
  helpVisible = false,
  action,
}: {
  message?: string;
  help?: string;
  helpVisible?: boolean;
  action?: { label: string; onClick: () => void };
}) {
  return <BrandedLoading message={message} help={help} helpVisible={helpVisible} action={action} />;
}

function PublicShell({ children, auth = false }: { children: React.ReactNode; auth?: boolean }) {
  return (
    <main className={`public-shell${auth ? " public-shell--auth" : ""}`}>
      <section className={`public-panel${auth ? " public-panel--auth" : ""}`}>
        {children}
      </section>
    </main>
  );
}

function AuthScreen({
  title,
  subtitle,
  backTo = "/",
  children,
}: {
  title: string;
  subtitle: string;
  backTo?: string;
  children: React.ReactNode;
}) {
  return (
    <PublicShell auth>
      <header className="auth-topbar">
        <Link className="auth-back" to={backTo} aria-label="Back">
          <ArrowLeft size={22} strokeWidth={2.25} />
        </Link>
        <Link className="auth-brand" to="/" aria-label={`${APP_NAME} home`}>
          <BrandMark className="auth-brand-mark" />
        </Link>
        <span className="auth-topbar-spacer" aria-hidden="true" />
      </header>
      <div className="auth-screen-body">
        <div className="auth-hero">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {children}
      </div>
    </PublicShell>
  );
}

function AppShell({ children, frameClassName = "" }: { children: React.ReactNode; frameClassName?: string }) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const install = useInstallPrompt();
  const { data: match } = useQuery({ queryKey: ["match"], queryFn: api.getCurrentMatch });
  const connected = match?.status === "connected";
  const { keyboardOpen } = useKeyboardViewport();
  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const isAdminRoute = location.pathname.startsWith("/admin/");
  const isChatRoute = location.pathname.startsWith("/app/chat/");
  const isOnboardingRoute = location.pathname.startsWith("/onboarding/");
  const showBottomNav = !isOnboardingRoute
    && !isAdminRoute
    && (location.pathname === "/app/match" || location.pathname === "/app/profile" || isChatRoute);
  const collapseBottomNav = showBottomNav && keyboardOpen;
  const matchIconState = getMatchNavIconState(match);
  const installSurface = resolveInstallPromptSurface({
    pathname: location.pathname,
    canPrompt: install.canPrompt,
    isInstalled: install.isInstalled,
    isSnoozed: install.isSnoozed,
    platform: install.platform,
  });
  const primaryNav = [
    { to: "/app/match", label: "Match", icon: () => <MatchNavIcon state={matchIconState} size={22} />, isMatch: true },
    { to: connected && match?.id ? `/app/chat/${match.id}` : "/app/match", label: "Chat", icon: MessageCircle, disabled: !connected },
    { to: "/app/profile", label: "Profile", icon: User },
  ] as const;
  const staffNav = [
    ...(isStaff ? [{ to: "/admin/users", label: "Moderation", icon: Shield }] as const : []),
    ...(user?.role === "admin" ? [{ to: "/admin/matching-launch", label: "Launch readiness", icon: Rocket }] as const : []),
    ...(user?.role === "admin" ? [{ to: "/admin/match-quality", label: "Match quality", icon: Activity }] as const : []),
  ];
  const nav = [
    ...primaryNav,
    ...staffNav,
    { to: "/settings", label: "Settings", icon: Settings },
  ];
  const bottomNav = [...primaryNav, ...staffNav];

  useEffect(() => {
    const root = document.documentElement;
    applyThemeColorSurface(isChatRoute ? "chat" : "default");

    if (isChatRoute) root.dataset.appSurface = "chat";
    else root.removeAttribute("data-app-surface");

    return () => {
      root.removeAttribute("data-app-surface");
      applyThemeColorSurface("default");
    };
  }, [isChatRoute]);

  return (
    <main className={`app-shell ${isChatRoute ? "chat-shell" : ""} ${isOnboardingRoute ? "onboarding-shell" : ""} ${showBottomNav && !collapseBottomNav ? "" : "mobile-nav-hidden"} ${keyboardOpen ? "keyboard-open" : ""}`}>
      {!isOnboardingRoute && (
        <aside className="sidebar">
          <Link className="brand-mini" to="/app/match" aria-label="Yara home">
            <SidebarLogo />
          </Link>
          <nav>
            {nav.map((item) => {
              const Icon = item.icon;
              const isMatchIcon = "isMatch" in item && item.isMatch;
              return "disabled" in item && item.disabled ? (
                <span className="side-link disabled" key={item.label}>
                  {typeof Icon === "function" && isMatchIcon ? <Icon /> : <Icon size={20} />}
                  {item.label}
                </span>
              ) : (
                <NavLink className="side-link" to={item.to} key={item.label}>
                  {typeof Icon === "function" && isMatchIcon ? <Icon /> : <Icon size={20} />}
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
          <Button variant="ghost" onClick={logout}><LogOut size={16} /> Logout</Button>
        </aside>
      )}
      <section className={`phone-frame ${frameClassName}`}>
        <OfflineStatusBanner />
        <AppScreenContent>{children}</AppScreenContent>
      </section>
      {installSurface && <InstallHomeScreenNudge tone={installSurface} />}
      {showBottomNav && (
        <nav className={`bottom-nav ${isStaff ? "bottom-nav--staff" : ""} ${user?.role === "admin" ? "bottom-nav--admin" : ""} ${collapseBottomNav ? "bottom-nav-collapsed" : ""}`}>
          {bottomNav.map((item) => {
            const Icon = item.icon;
            const isMatchIcon = "isMatch" in item && item.isMatch;
            return "disabled" in item && item.disabled ? (
              <span className="bottom-link disabled" key={item.label} aria-label={item.label}>
                {typeof Icon === "function" && isMatchIcon ? <Icon /> : <Icon size={22} />}
              </span>
            ) : (
              <NavLink className="bottom-link" to={item.to} key={item.label} aria-label={item.label}>
                {typeof Icon === "function" && isMatchIcon ? <Icon /> : <Icon size={22} />}
              </NavLink>
            );
          })}
        </nav>
      )}
    </main>
  );
}

function notificationPromptStorageKey(userId: string) {
  return `yara-notification-onboarding-snoozed-until:${userId}`;
}

function getNotificationPromptSnoozedUntil(userId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    return Number(window.localStorage.getItem(notificationPromptStorageKey(userId)) || 0);
  } catch {
    return 0;
  }
}

function setNotificationPromptSnoozedUntil(userId: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    if (value > 0) {
      window.localStorage.setItem(notificationPromptStorageKey(userId), String(value));
    } else {
      window.localStorage.removeItem(notificationPromptStorageKey(userId));
    }
  } catch {
    // Ignore storage failures. Settings still exposes manual notification setup.
  }
}

function browserPushSupported(): boolean {
  return typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "Notification" in window &&
    "PushManager" in window &&
    "serviceWorker" in navigator;
}

function notificationSettingsInstructions(platform: InstallPlatform): string {
  if (platform === "ios-safari" || platform === "ios-other") {
    return "Open iPhone Settings → Notifications → Yara, then turn on Allow Notifications. iOS will not show the permission prompt again after notifications are turned off there.";
  }

  if (platform === "android") {
    return "Open Android Settings → Apps → Yara or your browser → Notifications, then allow notifications for Yara.";
  }

  return "Open your browser's site settings for Yara and allow notifications, then return to Yara and try again.";
}

function NotificationOnboardingPrompt() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const install = useInstallPrompt();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const autoRegisteredUserRef = useRef<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const installSurface = resolveInstallPromptSurface({
    pathname: location.pathname,
    canPrompt: install.canPrompt,
    isInstalled: install.isInstalled,
    isSnoozed: install.isSnoozed,
    platform: install.platform,
  });

  const needsHomeScreenForIosPush = !install.isInstalled && (install.platform === "ios-safari" || install.platform === "ios-other");
  const notificationsBlocked = browserPushSupported() && Notification.permission === "denied";
  const prioritizeInstalledAndroidPrompt = Boolean(user && browserPushSupported() && shouldPrioritizeInstalledNotificationPrompt({
    isInstalled: install.isInstalled,
    platform: install.platform,
    permission: Notification.permission,
  }));

  useEffect(() => {
    const pushSupported = browserPushSupported();
    if (isLoading || !isAuthenticated || !user || (!pushSupported && !needsHomeScreenForIosPush)) {
      setVisible(false);
      return;
    }

    if (pushSupported && Notification.permission === "granted") {
      setVisible(false);
      if (autoRegisteredUserRef.current !== user.id) {
        autoRegisteredUserRef.current = user.id;
        subscribeToWebPush({ enableNotifications: false })
          .then(() => queryClient.invalidateQueries({ queryKey: ["notification-settings"] }))
          .catch((eventError) => {
            console.warn("Could not refresh browser push subscription", eventError);
            autoRegisteredUserRef.current = null;
          });
      }
      return;
    }

    if (installSurface !== null) {
      setVisible(false);
      return;
    }

    if (location.pathname === "/settings/notifications"
      || location.pathname === "/settings/install"
      || location.pathname === "/verify-email"
      || location.pathname === "/reset-password") {
      setVisible(false);
      return;
    }

    if (getNotificationPromptSnoozedUntil(user.id) > Date.now()) {
      setVisible(false);
      return;
    }

    const timer = window.setTimeout(
      () => setVisible(true),
      prioritizeInstalledAndroidPrompt ? 0 : 650,
    );
    return () => window.clearTimeout(timer);
  }, [installSurface, isAuthenticated, isLoading, location.pathname, needsHomeScreenForIosPush, prioritizeInstalledAndroidPrompt, queryClient, user]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user || !browserPushSupported() || Notification.permission !== "granted") {
      return;
    }

    const refreshSubscription = () => {
      if (document.visibilityState === "hidden") return;

      refreshWebPushSubscription()
        .then((refreshed) => {
          if (refreshed) queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
        })
        .catch((eventError) => {
          console.warn("Could not refresh browser push subscription", eventError);
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshSubscription();
    };

    refreshSubscription();
    window.addEventListener("focus", refreshSubscription);
    window.addEventListener("online", refreshSubscription);
    window.addEventListener("pageshow", refreshSubscription);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshSubscription);
      window.removeEventListener("online", refreshSubscription);
      window.removeEventListener("pageshow", refreshSubscription);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, isLoading, queryClient, user]);

  if (!visible || !user || installSurface !== null) return null;

  const dismiss = () => {
    setNotificationPromptSnoozedUntil(user.id, Date.now() + NOTIFICATION_ONBOARDING_SNOOZE_MS);
    setVisible(false);
  };

  const enableNotifications = async () => {
    if (notificationsBlocked) {
      setError(new Error(notificationSettingsInstructions(install.platform)));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const ok = await subscribeToWebPush();
      if (ok) {
        setNotificationPromptSnoozedUntil(user.id, 0);
        await queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
        setVisible(false);
      } else {
        setError(new Error(
          Notification.permission === "denied"
            ? notificationSettingsInstructions(install.platform)
            : "The notification permission request closed without a choice. Tap the button to try again.",
        ));
      }
    } catch (eventError) {
      setError(eventError instanceof Error ? eventError : new Error("Could not enable browser notifications"));
    } finally {
      setBusy(false);
    }
  };

  const goToInstallSteps = () => {
    setVisible(false);
    navigate("/settings/install");
  };

  return (
    <div className="notification-onboarding-backdrop" role="presentation">
      <section className="notification-onboarding-modal" role="dialog" aria-modal="true" aria-labelledby="notification-onboarding-title">
        <button className="notification-onboarding-close" type="button" onClick={dismiss} aria-label="Not now"><X size={18} /></button>
        <div className="notification-onboarding-orbit" aria-hidden="true">
          <span className="notification-onboarding-pulse" />
          <Bell size={26} />
        </div>
        <div className="notification-onboarding-copy">
          <span className="notification-onboarding-kicker">Time-sensitive introductions</span>
          <h2 id="notification-onboarding-title">
            {notificationsBlocked
              ? "Notifications are turned off"
              : needsHomeScreenForIosPush
                ? "Install Yara to receive iPhone notifications"
                : "Turn on Yara notifications"}
          </h2>
          <p>
            {notificationsBlocked
              ? notificationSettingsInstructions(install.platform)
              : needsHomeScreenForIosPush
                ? "On iPhone, notifications work after Yara is added to your Home Screen. Install it once so introduction responses and messages do not get missed."
                : prioritizeInstalledAndroidPrompt
                  ? "Tap Continue and Android will ask you to allow notifications for introductions, responses, and messages."
                : "Introductions and chat can be timely. Yara will alert you when someone responds, when a connection opens, or when a message needs your attention."}
          </p>
          <p className="notification-onboarding-note">You can change this anytime in Settings → Notifications.</p>
        </div>
        <ErrorBanner error={error} />
        <div className="notification-onboarding-actions">
          {notificationsBlocked ? (
            <Button type="button" onClick={dismiss}><Settings size={16} /> I'll update settings</Button>
          ) : needsHomeScreenForIosPush ? (
            <Button type="button" onClick={goToInstallSteps}><Smartphone size={16} /> Show Home Screen steps</Button>
          ) : (
            <Button type="button" loading={busy} onClick={enableNotifications}><Bell size={16} /> {prioritizeInstalledAndroidPrompt ? "Continue" : "Enable notifications"}</Button>
          )}
          <Button type="button" variant="ghost" onClick={dismiss}>Not now</Button>
        </div>
      </section>
    </div>
  );
}

function PublicOrInAppRedirect({
  inAppPath,
  children,
}: {
  inAppPath: string;
  children: React.ReactNode;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) {
    return <Navigate to={`${inAppPath}${location.hash}`} replace />;
  }
  return <>{children}</>;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminModerationListRoute({ children }: { children: React.ReactNode }) {
  return (
    <Protected>
      <AppShell frameClassName="settings-frame admin-dashboard-frame">
        {children}
      </AppShell>
    </Protected>
  );
}

function AdminModerationChatRoute({ children }: { children: React.ReactNode }) {
  return (
    <Protected>
      <AppShell frameClassName="match-frame moderation-chat-frame">
        {children}
      </AppShell>
    </Protected>
  );
}

function AdminModerationDetailRoute({ children }: { children: React.ReactNode }) {
  return (
    <Protected>
      <AppShell frameClassName="match-frame">
        {children}
      </AppShell>
    </Protected>
  );
}

function AdminSettingsDashboardRoute({ children }: { children: React.ReactNode }) {
  return (
    <Protected>
      <AppShell frameClassName="settings-frame admin-dashboard-frame">
        {children}
      </AppShell>
    </Protected>
  );
}

function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const onboarding = useQuery(onboardingStatusQueryOptions({
    authReady: !authLoading,
    isAuthenticated,
  }));

  if (authLoading) {
    return <LoadingScreen message="Opening Yara" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (onboarding.isLoading) {
    return <LoadingScreen message="Opening Yara" />;
  }

  if (onboarding.isError) {
    if (isUnauthorizedApiError(onboarding.error)) {
      return <Navigate to="/login" replace />;
    }
    return (
      <LoadingScreen
        message="Opening Yara"
        help="We could not confirm your setup status. Check your connection and try again."
        helpVisible
        action={{ label: "Try again", onClick: () => onboarding.refetch() }}
      />
    );
  }

  if (onboarding.data && onboarding.data !== "complete") {
    return <Navigate to={onboardingPath(onboarding.data)} replace />;
  }

  return <>{children}</>;
}

function OnboardingOnly({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const onboarding = useQuery(onboardingStatusQueryOptions({
    authReady: !authLoading,
    isAuthenticated,
  }));

  if (authLoading) {
    return <LoadingScreen message="Opening Yara" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (onboarding.isLoading) {
    return <LoadingScreen message="Opening Yara" />;
  }

  if (onboarding.isError) {
    if (isUnauthorizedApiError(onboarding.error)) {
      return <Navigate to="/login" replace />;
    }
    return (
      <LoadingScreen
        message="Opening Yara"
        help="We could not confirm your setup status. Check your connection and try again."
        helpVisible
        action={{ label: "Try again", onClick: () => onboarding.refetch() }}
      />
    );
  }

  if (onboarding.data === "complete") {
    return <Navigate to="/app/match" replace />;
  }

  if (onboarding.data) {
    const expectedPath = onboardingPath(onboarding.data);
    if (location.pathname !== expectedPath) {
      return <Navigate to={expectedPath} replace />;
    }
  }

  return <>{children}</>;
}

function GuestOnly({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const onboarding = useQuery(onboardingStatusQueryOptions({
    authReady: !isLoading,
    isAuthenticated,
  }));

  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) {
    if (onboarding.isLoading) return <LoadingScreen message="Opening Yara" />;
    if (onboarding.isError) {
      if (isUnauthorizedApiError(onboarding.error)) return <>{children}</>;
      return <Navigate to={onboardingPath("profile")} replace />;
    }
    const destination = onboarding.data === "complete"
      ? "/app/match"
      : onboardingPath(onboarding.data ?? "profile");
    return <Navigate to={destination} replace />;
  }
  return <>{children}</>;
}

function HashScroll() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    window.requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      const scrollRoot = target.closest(".settings-document-scroll");
      scrollToResourceTarget(id, scrollRoot instanceof HTMLElement ? scrollRoot : null);
    });
  }, [location.hash, location.pathname]);

  return null;
}

function installPromptIntro(platform: InstallPlatform, canPrompt: boolean) {
  if (platform === "ios-safari") return "Add Yara for introduction and message alerts.";
  if (platform === "ios-other") return "Use your browser's Share menu to add Yara.";
  if (canPrompt) return "Add Yara, then turn on introduction and message alerts.";
  return "Add Yara, then turn on introduction and message alerts.";
}

function InstallGateDialog({
  busy,
  install,
  onDismiss,
  runPrompt,
}: {
  busy: boolean;
  install: InstallPromptContextValue;
  onDismiss: () => void;
  runPrompt: () => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const restoreFocusRef = useRef(true);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    if (!dialog.open) dialog.showModal();

    return () => {
      if (dialog.open) dialog.close();
      if (restoreFocusRef.current && previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, []);

  const isIos = install.platform === "ios-safari" || install.platform === "ios-other";

  const installDescription = isIos
    ? "Add Yara to your Home Screen so your phone can alert you about new introductions, responses, and messages."
    : "Add Yara to your Home Screen for app-like access, then turn on alerts for new introductions, responses, and messages.";
  return (
    <dialog
      ref={dialogRef}
      className="install-gate-dialog"
      aria-labelledby="install-gate-title"
      aria-describedby="install-gate-description"
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
    >
      <section className="install-gate">
        <div className="install-gate-icon" aria-hidden="true"><Smartphone size={30} /></div>
        <div className="install-gate-heading">
          <span className="install-gate-eyebrow">Stay updated</span>
          <h2 id="install-gate-title">Get Yara notifications</h2>
          <p id="install-gate-description">{installDescription}</p>
        </div>

        {isIos && (
          <ol className="install-gate-steps" aria-label="Add Yara to your iPhone Home Screen">
            <li><span aria-hidden="true"><Share size={18} /></span><p>Tap <strong>Share</strong> in your browser.</p></li>
            <li><span aria-hidden="true"><Home size={18} /></span><p>Choose <strong>Add to Home Screen</strong>.</p></li>
            <li><span aria-hidden="true"><CheckCircle2 size={18} /></span><p>Tap <strong>Add</strong> to finish.</p></li>
          </ol>
        )}

        {!isIos && !install.canPrompt && (
          <div className="install-gate-browser-tip"><Menu aria-hidden="true" size={20} /><p>Open your browser menu and choose <strong>Install app</strong> or <strong>Add to Home Screen</strong>.</p></div>
        )}

        <div className="install-gate-actions">
          {install.canPrompt ? (
            <Button autoFocus loading={busy} onClick={runPrompt} type="button"><Download size={17} /> Add Yara to Home Screen</Button>
          ) : (
            <Link
              autoFocus
              className="button primary"
              to="/settings/install"
              onClick={() => {
                restoreFocusRef.current = false;
              }}
            >
              <Home size={17} /> Show me how
            </Link>
          )}
          <button className="install-gate-continue" type="button" onClick={onDismiss}>Not now</button>
        </div>
      </section>
    </dialog>
  );
}

function InstallHomeScreenNudge({ tone }: { tone: Exclude<InstallPromptSurface, null> }) {
  const { user } = useAuth();
  const install = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  const snoozeInstallPrompts = () => {
    install.snooze();
    if (user && (install.platform === "ios-safari" || install.platform === "ios-other")) {
      setNotificationPromptSnoozedUntil(user.id, Date.now() + NOTIFICATION_ONBOARDING_SNOOZE_MS);
    }
  };

  const runPrompt = async () => {
    setBusy(true);
    try {
      const outcome = await install.promptInstall();
      if (outcome !== "accepted") snoozeInstallPrompts();
    } finally {
      setBusy(false);
    }
  };

  if (tone === "gate") {
    return <InstallGateDialog busy={busy} install={install} onDismiss={snoozeInstallPrompts} runPrompt={runPrompt} />;
  }

  const overlayAction = install.canPrompt ? (
    <Button className="install-nudge-primary" loading={busy} onClick={runPrompt} type="button"><Download size={15} /> Add</Button>
  ) : (
    <Link className="button primary install-nudge-primary" to="/settings/install"><Home size={15} /> Steps</Link>
  );

  return (
    <aside className="install-nudge install-nudge--overlay" aria-live="polite" aria-label="Add Yara to Home Screen">
      <div className="install-nudge-icon" aria-hidden="true"><Smartphone size={20} /></div>
      <div className="install-nudge-copy">
        <strong>Get Yara Notifications</strong>
        <p>{installPromptIntro(install.platform, install.canPrompt)}</p>
      </div>
      {overlayAction}
      <button className="install-nudge-dismiss" type="button" onClick={snoozeInstallPrompts} aria-label="Hide Add Yara reminder for now"><X size={17} /></button>
    </aside>
  );
}

function InstallInstructionSteps() {
  const install = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const runPrompt = async () => {
    setBusy(true);
    try {
      await install.promptInstall();
      install.resetSnooze();
    } finally {
      setBusy(false);
    }
  };

  const isIosSafari = install.platform === "ios-safari";
  const isIosOther = install.platform === "ios-other";
  const canUseNativePrompt = install.canPrompt;

  const installDescription = isIosSafari || isIosOther
    ? "Add Yara to your Home Screen so your phone can alert you about new introductions, responses, and messages."
    : "Add Yara to your Home Screen for app-like access, then turn on alerts for new introductions, responses, and messages.";
  if (install.isInstalled) {
    return (
      <article className="install-guide-card install-guide-card--success">
        <div className="install-guide-card-head"><CheckCircle2 aria-hidden="true" size={22} /><h2 ref={headingRef} tabIndex={-1}>Yara Is Already on This Device</h2></div>
        <p>Yara is on your Home Screen. Turn on notifications when prompted so you do not miss matches, responses, or messages.</p>
      </article>
    );
  }

  return (
    <div className="install-guide-stack">
      <article className="install-guide-card install-guide-card--hero">
        <div className="install-guide-card-head"><Smartphone aria-hidden="true" size={24} /><h2 ref={headingRef} tabIndex={-1}>Add Yara to Your Home Screen</h2></div>
        <p>{installDescription}</p>
        {canUseNativePrompt && <Button loading={busy} onClick={runPrompt} type="button"><Download size={16} /> Add Yara to Home Screen</Button>}
        {!canUseNativePrompt && <p className="install-guide-note">Follow the steps below for your device.</p>}
      </article>

      {isIosOther && (
        <article className="install-guide-card install-guide-card--notice">
          <div className="install-guide-card-head"><Info aria-hidden="true" size={21} /><h2>Cannot find Add to Home Screen?</h2></div>
          <p>Check your browser's Share menu first. If Add to Home Screen is not available, open this page in Safari and use Share there.</p>
        </article>
      )}

      <ol className="install-step-list" aria-label="Home Screen install steps">
        {(isIosSafari || isIosOther) ? (
          <>
            <li><span aria-hidden="true"><Share size={18} /></span><div><strong>Tap Share</strong><p>Open your browser's Share menu.</p></div></li>
            <li><span aria-hidden="true"><Home size={18} /></span><div><strong>Choose Add to Home Screen</strong><p>Scroll the share sheet if needed, then tap Add to Home Screen.</p></div></li>
            <li><span aria-hidden="true"><CheckCircle2 size={18} /></span><div><strong>Tap Add</strong><p>Keep the name Yara, then tap Add in the top corner.</p></div></li>
          </>
        ) : (
          <>
            <li><span aria-hidden="true"><Download size={18} /></span><div><strong>Use the Add Button</strong><p>If your browser shows a prompt, tap Add Yara and confirm.</p></div></li>
            <li><span aria-hidden="true"><Menu size={18} /></span><div><strong>Or Open the Browser Menu</strong><p>Tap the menu button, then choose Install app or Add to Home Screen.</p></div></li>
            <li><span aria-hidden="true"><Home size={18} /></span><div><strong>Open from Home Screen</strong><p>After adding it, open Yara from the icon on your phone.</p></div></li>
          </>
        )}
      </ol>
    </div>
  );
}

const PUBLIC_NAV_LINKS = [
  { href: "/#why", label: "Why Yara" },
  { href: "/#principles", label: "Principles" },
  { href: "/#how", label: "How it works" },
  { href: "/#safety", label: "Safety" },
  { href: "/#faq", label: "FAQ" },
  { href: "/resources", label: "Resources" },
] as const;

const PUBLIC_TOP_NAV_LINKS = PUBLIC_NAV_LINKS.filter((item) => item.label !== "FAQ");

function PublicNav() {
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const renderNavLink = (item: (typeof PUBLIC_NAV_LINKS)[number]) => {
    if (item.href.startsWith("/#")) {
      return (
        <a className="public-nav-link" key={item.label} href={item.href} onClick={closeMenu}>
          {item.label}
        </a>
      );
    }
    return (
      <Link className="public-nav-link" key={item.label} to={item.href} onClick={closeMenu}>
        {item.label}
      </Link>
    );
  };

  return (
    <header className={`public-nav-shell${menuOpen ? " menu-open" : ""}`}>
      <div className="public-nav">
        <Link className="public-brand" to="/" aria-label="Yara home" onClick={closeMenu}>
          <span className="public-brand-mark-wrap">
            <BrandMark className="public-brand-mark" />
          </span>
          <span className="public-brand-text">
            <strong>{APP_NAME}</strong>
            <small>{APP_TAGLINE}</small>
          </span>
        </Link>
        <nav className="public-nav-links" aria-label="Public">
          {PUBLIC_TOP_NAV_LINKS.map(renderNavLink)}
        </nav>
        <div className="public-nav-actions">
          <div className="public-nav-actions-pill">
            <Link className="public-nav-signin" to={isAuthenticated ? "/app/match" : "/login"}>
              {isAuthenticated ? "Open App" : "Sign In"}
            </Link>
            <Link className="public-nav-cta" to="/signup">Join with code</Link>
          </div>
          <button
            type="button"
            className="public-nav-menu"
            aria-expanded={menuOpen}
            aria-controls="public-nav-drawer"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
            <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
          </button>
        </div>
      </div>
      <div id="public-nav-drawer" className={`public-nav-drawer${menuOpen ? " open" : ""}`}>
        <nav aria-label="Mobile">{PUBLIC_NAV_LINKS.map(renderNavLink)}</nav>
        <div className="public-nav-drawer-actions">
          <Link className="button primary" to="/signup" onClick={closeMenu}>Join with code</Link>
          <Link className="button ghost" to={isAuthenticated ? "/app/match" : "/login"} onClick={closeMenu}>
            {isAuthenticated ? "Open App" : "Sign In"}
          </Link>
        </div>
      </div>
    </header>
  );
}

function PublicFooter({ children }: { children: ReactNode }) {
  const year = new Date().getFullYear();

  return (
    <footer className="public-footer">
      <p className="public-footer-line">
        <span className="public-footer-copy">© {year} {APP_NAME}</span>
        <span className="public-footer-sep" aria-hidden="true">·</span>
        <span className="public-footer-note">{PUBLIC_FOOTER_NOTE}</span>
      </p>
      <div className="public-footer-links">{children}</div>
    </footer>
  );
}

function LandingHeroIntroCard({ className = "", decorative = false }: { className?: string; decorative?: boolean }) {
  return (
    <div className={`hero-intro-panel${className ? ` ${className}` : ""}`} aria-hidden={decorative || undefined}>
      <div className="hero-intro-top">
        <BrandMark className="hero-intro-logo" />
        <span>One introduction</span>
      </div>
      <div className="hero-intro-window">
        <span className="hero-intro-seal" aria-hidden="true">private note</span>
        <div className="hero-intro-mark"><HeartHandshake size={28} /></div>
        <strong>Respond in private</strong>
        <p>When an introduction is ready, both people answer privately before chat can open.</p>
        <div className="hero-intro-tags">
          <span>Character-led</span>
          <span>Family-minded</span>
          <span>Mutual yes only</span>
        </div>
      </div>
      <div className="hero-intro-flow">
        <span><CheckCircle2 size={15} /> Your answer stays private</span>
        <span><HeartHandshake size={15} /> Chat opens only if you both accept</span>
      </div>
      <div className="hero-intro-actions">
        <span>Decline</span>
        <strong>Accept</strong>
      </div>
    </div>
  );
}

function LandingHeroVisual() {
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hero-geometry" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <LandingHeroIntroCard className="hero-visual-panel" decorative />
      <div className="hero-hearth-note">
        <span>guided by</span>
        <strong>character · consultation · family</strong>
      </div>
    </div>
  );
}

function LandingHeroAtAGlance() {
  return (
    <aside className="hero-at-a-glance" aria-label="How Yara works in three steps">
      <p className="hero-at-a-glance-label">How it works</p>
      <ol className="hero-at-a-glance-steps">
        {LANDING_HERO_GLANCE.map((step) => {
          const Icon = step.icon;
          return (
            <li key={step.title}>
              <span className="hero-at-a-glance-icon"><Icon size={18} /></span>
              <span className="hero-at-a-glance-copy">
                <strong>{step.title}</strong>
                <span>{step.body}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function LandingHeroMobileVisual() {
  return (
    <div className="hero-visual-compact">
      <LandingHeroAtAGlance />
    </div>
  );
}

function LandingAudience() {
  return (
    <ul className="landing-audience" aria-label="Who Yara is for">
      {LANDING_AUDIENCE.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function WhyYaraVisual() {
  return (
    <div className="why-visual-container" aria-label="Yara is built differently from typical dating apps.">
      <p className="why-visual-label">Yara is built differently</p>
      <div className="why-contrast-list">
        <div className="why-contrast-row">
          <span className="why-contrast-icon"><Compass size={15} /></span>
          <div>
            <span>Not endless options</span>
            <strong>One introduction with real attention</strong>
          </div>
        </div>
        <div className="why-contrast-row">
          <span className="why-contrast-icon"><LockKeyhole size={15} /></span>
          <div>
            <span>Not public performance</span>
            <strong>Private yes or no before chat opens</strong>
          </div>
        </div>
        <div className="why-contrast-row why-contrast-row--accent">
          <span className="why-contrast-icon"><HeartHandshake size={15} /></span>
          <div>
            <span>Not return loops</span>
            <strong>Best outcome: a home and life beyond Yara</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPrinciples() {
  return (
    <section className="landing-principles landing-band landing-band--tone-a" id="principles" aria-labelledby="landing-principles-title">
      <div className="section-kicker"><BookOpen size={18} /> Principles woven in</div>
      <div className="landing-principles-head">
        <h2 id="landing-principles-title">Designed around character, consultation, and the home you may build.</h2>
        <p>Inspired by Baha'i guidance on marriage and family life, Yara slows the first step down so a connection can be considered with dignity instead of pressure.</p>
      </div>
      <div className="landing-principle-grid">
        {LANDING_PRINCIPLES.map((item) => {
          const Icon = item.icon;
          return (
            <article className="landing-principle-card" key={item.title}>
              <span className="landing-principle-orbit" aria-hidden="true" />
              <span className="landing-principle-icon"><Icon size={20} /></span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function LandingSafetyFeatures() {
  return (
    <div className="safety-feature-grid">
      {LANDING_SAFETY_FEATURES.map((item) => {
        const Icon = item.icon;
        return (
          <article className="safety-feature-card" key={item.title}>
            <span className="safety-feature-icon"><Icon size={18} /></span>
            <div>
              <strong>{item.title}</strong>
              <p>{item.body}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function SafetyVisual() {
  return (
    <div className="safety-visual" aria-label="In-app reporting preview">
      <p className="safety-visual-label">Inside connected chat</p>
      <div className="safety-mock-chat">
        <div className="safety-mock-chat-head">
          <span className="safety-mock-avatar" aria-hidden="true" />
          <div>
            <strong>Leila</strong>
            <p>Connected</p>
          </div>
          <span className="safety-mock-report"><Flag size={14} /> Report</span>
        </div>
        <div className="safety-mock-bubble theirs">Are you okay keeping messages here for now?</div>
        <div className="safety-mock-bubble mine">Yes—that feels right to me.</div>
      </div>
      <Link className="button secondary safety-visual-cta" to="/safety">Open Safety Center</Link>
    </div>
  );
}

function LandingFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const previewQuestions = LANDING_FAQ.slice(0, 4);

  return (
    <div className="landing-faq-list">
      {previewQuestions.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <details className="landing-faq-item" key={item.question} open={isOpen}>
            <summary
              onClick={(event) => {
                event.preventDefault();
                setOpenIndex(isOpen ? null : index);
              }}
            >
              {item.question}
            </summary>
            <p>{item.answer}</p>
          </details>
        );
      })}
    </div>
  );
}

function LandingCtaBand() {
  return (
    <section className="landing-cta-band" id="join">
      <h2>Ready when you have a code.</h2>
      <p>Join through someone already in the community. No code yet? Ask the friend who sent you here.</p>
      <div className="landing-cta-row landing-cta-band-actions">
        <Link className="button primary" to="/signup">Join with invite code</Link>
      </div>
      <Link className="text-link landing-cta-signin" to="/login">Already registered? Sign in</Link>
    </section>
  );
}

function JourneyStagePreview({ stepIndex }: { stepIndex: number }) {
  return (
    <div className="journey-stage-preview" key={stepIndex}>
      {stepIndex === 0 && (
        <div className="journey-mock journey-mock-invite">
          <div className="journey-mock-chip"><LockKeyhole size={14} /> Invite-only</div>
          <div className="journey-invite-card">
            <span className="journey-mock-label">You were invited by</span>
            <strong>Maya K.</strong>
            <p>Trusted members grow the community one person at a time.</p>
            <div className="journey-invite-code">
              <span>beyara.app/join</span>
              <strong>••••-invite</strong>
            </div>
          </div>
          <ol className="journey-invite-steps">
            <li>
              <span className="journey-invite-step-num" aria-hidden="true">1</span>
              <div>
                <strong>A member invites you</strong>
                <p>Someone you trust shares a private link or code—like Maya above.</p>
              </div>
            </li>
            <li>
              <span className="journey-invite-step-num" aria-hidden="true">2</span>
              <div>
                <strong>You join with that invite</strong>
                <p>No open sign-up and no public browsing—only invited people.</p>
              </div>
            </li>
            <li>
              <span className="journey-invite-step-num" aria-hidden="true">3</span>
              <div>
                <strong>You can invite others later</strong>
                <p>The community grows one trusted introduction at a time.</p>
              </div>
            </li>
          </ol>
        </div>
      )}
      {stepIndex === 1 && (
        <div className="journey-mock journey-mock-profile">
          <div className="journey-profile-shell">
            <div className="journey-profile-photos">
              <span /><span /><span className="dim" />
            </div>
            <div className="journey-profile-fields">
              <span className="filled">Faith &amp; values</span>
              <span className="filled">Family &amp; service</span>
              <span className="filled">What you hope to build</span>
              <span>Preferences for pacing &amp; location</span>
            </div>
          </div>
          <p className="journey-mock-caption">Complete profiles unlock thoughtful matching rounds.</p>
        </div>
      )}
      {stepIndex === 2 && (
        <div className="journey-mock journey-mock-intro">
          <div className="journey-intro-badge"><Compass size={14} /> One introduction</div>
          <article className="journey-intro-card">
            <div className="journey-intro-avatar" />
            <div>
              <strong>Leila, 29</strong>
              <p>Values-aligned · Marriage-minded · Within your preferences</p>
            </div>
          </article>
          <div className="journey-intro-muted">
            <span className="ghost">No feed</span>
            <span className="ghost">No swipe stack</span>
            <span className="live">Full attention on one person</span>
          </div>
        </div>
      )}
      {stepIndex === 3 && (
        <div className="journey-mock journey-mock-choice">
          <div className="journey-choice-intro">
            <span className="journey-choice-intro-avatar" aria-hidden="true" />
            <div>
              <strong>Leila, 29</strong>
              <p>The introduction you&apos;re responding to</p>
            </div>
          </div>
          <div className="journey-choice-columns">
            <div className="journey-choice-panel">
              <span><LockKeyhole size={13} /> Your answer about this intro</span>
              <div className="journey-choice-actions" aria-hidden="true">
                <span className="ghost">Decline</span>
                <span className="primary">Accept</span>
              </div>
            </div>
            <div className="journey-choice-bridge" aria-hidden="true">
              <span className="journey-choice-pulse" />
              <HeartHandshake size={20} />
            </div>
            <div className="journey-choice-panel muted">
              <span><LockKeyhole size={13} /> Their answer about this intro</span>
              <p>Hidden until you both respond to the same introduction</p>
            </div>
          </div>
          <p className="journey-mock-caption">
            <span className="journey-choice-timer"><CirclePause size={13} /> Private response window</span>
            A no stays quiet. Chat opens only when interest is mutual.
          </p>
        </div>
      )}
      {stepIndex === 4 && (
        <div className="journey-mock journey-mock-chat">
          <div className="journey-chat-header">
            <span className="journey-chat-avatar" />
            <div>
              <strong>Leila</strong>
              <p>Connected · Full profile unlocked</p>
            </div>
          </div>
          <div className="journey-chat-thread">
            <div className="bubble theirs">Hi — I appreciated what you wrote about service and family.</div>
            <div className="bubble mine">Thanks. Me too. Are you okay staying on here for a bit before swapping numbers?</div>
            <div className="bubble theirs">Yes, that feels wise to me.</div>
          </div>
        </div>
      )}
      {stepIndex === 5 && (
        <div className="journey-mock journey-mock-forward">
          <div className="journey-forward-path">
            <div className="journey-forward-done">
              <CheckCircle2 size={16} />
              <div>
                <strong>Connected on Yara</strong>
                <p>You both chose to open chat. That is where Yara&apos;s job peaks.</p>
              </div>
            </div>
            <ol className="journey-forward-steps">
              <li>
                <span className="journey-forward-step-icon"><User size={15} /></span>
                <div>
                  <strong>Take next steps off-app</strong>
                  <p>Calls, video, or in person—only if you both want to. If you meet, choose a public place and tell someone you trust.</p>
                </div>
              </li>
              <li>
                <span className="journey-forward-step-icon"><BookOpen size={15} /></span>
                <div>
                  <strong>Consult people you trust</strong>
                  <p>Family, friends, or mentors who know you well.</p>
                </div>
              </li>
              <li>
                <span className="journey-forward-step-icon"><Heart size={15} /></span>
                <div>
                  <strong>Pray and discern</strong>
                  <p>The app is only a beginning. The real decision belongs to you, with consultation and care.</p>
                </div>
              </li>
            </ol>
            <div className="journey-forward-exit">
              <HeartHandshake size={18} />
              <div>
                <strong>Decide off-app</strong>
                <p>Yara does not pick your spouse. It opens a safer front door—then steps back.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function YaraJourney() {
  return (
    <div
      className="yara-journey yara-journey--compact"
      aria-label="How Yara moves from invite to real discernment"
    >
      <div className="yara-journey-header">
        <div className="section-kicker"><Compass size={18} /> The Yara Journey</div>
        <h2 className="yara-journey-title yara-journey-title--full">From invite to real discernment.</h2>
        <h2 className="yara-journey-title yara-journey-title--short">How Yara works</h2>
        <p className="yara-journey-lede yara-journey-lede--full">Join through trust, receive one introduction, answer privately, and chat only when the interest is mutual.</p>
        <p className="yara-journey-lede yara-journey-lede--short">Six steps from invite to a mutual conversation.</p>
      </div>

      <ol className="yara-journey-strip" aria-label="Yara journey steps">
        {LANDING_ROADMAP_STEPS.map((stepItem, index) => {
            const Icon = stepItem.icon;
            return (
              <li
                key={stepItem.title}
                className="yara-journey-step"
              >
                <span className="yara-journey-step-icon"><Icon size={17} /></span>
                <span className="yara-journey-step-copy">
                  <span className="yara-journey-step-meta">
                    <span className="yara-journey-step-num">{String(index + 1).padStart(2, "0")}</span>
                    <span className="yara-journey-step-phase">{stepItem.phase}</span>
                  </span>
                  <strong>{stepItem.title}</strong>
                  <span className="yara-journey-step-body">{stepItem.body}</span>
                </span>
              </li>
            );
        })}
      </ol>
    </div>
  );
}

function Welcome() {
  return (
    <GuestOnly>
      <main className="landing-shell landing-shell--welcome">
        <PublicNav />
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="eyebrow">{LANDING_HEADLINES[2]}</p>
            <h1>{LANDING_HEADLINES[0]}</h1>
            <p className="landing-lede landing-lede--full">
              Yara is a friend-of-a-friend introduction community for Baha&apos;is and friends of the Faith who want the search for marriage to feel calmer, clearer, and centered on character, family, and service.
            </p>
            <p className="landing-lede landing-lede--short">{LANDING_HERO_LEDE_SHORT}</p>
            <div className="landing-hero-audience-wrap">
              <LandingAudience />
            </div>
            <div className="landing-cta-row">
              <Link className="button primary" to="/signup">Join with invite code</Link>
              <a className="button ghost" href="#how">See how it works</a>
            </div>
            <p className="landing-proof landing-proof--hero">
              <CheckCircle2 size={16} aria-hidden />
              <span className="landing-proof-line landing-proof-line--full">{LANDING_HEADLINES[1]}</span>
              <span className="landing-proof-line landing-proof-line--short">{LANDING_HERO_PROOF_SHORT}</span>
            </p>
            <p className="landing-hero-secondary-cta"><Link className="text-link" to="/resources">Read resources</Link></p>
          </div>
          <LandingHeroMobileVisual />
          <LandingHeroVisual />
        </section>

        <section className="landing-band landing-band--tone-b" id="why">
          <div className="section-kicker"><Sparkles size={18} /> Why We Built Yara</div>
          <div className="landing-two-col">
            <div className="why-text-column copy-stack">
              <h2>Built for discernment, not dependency.</h2>
              <p>
                Most apps profit when you keep returning. Yara is designed for a better ending: one trusted introduction, private choice, and a conversation only if both people say yes.
              </p>
              <p>
                The experience is intentionally slower because character, family, service, and practical life need room to be seen clearly.
              </p>
            </div>
            <WhyYaraVisual />
          </div>
        </section>

        <LandingPrinciples />

        <section className="landing-how-roadmap" id="how">
          <YaraJourney />
        </section>

        <section className="landing-band landing-band--tone-a landing-safety-band landing-safety-band--flip" id="safety">
          <div className="section-kicker"><Shield size={18} /> Care and boundaries</div>
          <div className="safety-section-intro">
            <h2>A no can stay private. A yes can move with care.</h2>
            <p>Yara helps introductions stay honest without public pressure, while connected chats still have clear boundaries.</p>
          </div>
          <div className="safety-showcase">
            <LandingSafetyFeatures />
            <SafetyVisual />
          </div>
        </section>

        <section className="landing-band landing-band--tone-b landing-faq-band" id="faq">
          <div className="section-kicker"><Info size={18} /> Common questions</div>
          <h2 className="landing-section-title">Before you join</h2>
          <LandingFaq />
        </section>

        <section className="landing-resource-strip" id="resources">
          <div className="landing-resource-layout">
            <div className="landing-resource-head">
              <BookMarked size={24} />
              <div>
                <h2>Resources for becoming acquainted with character</h2>
                <p>Baha&apos;i references and prompts for character, family, service, and consultation.</p>
              </div>
            </div>
            <div className="landing-resource-chips">
              {LANDING_RESOURCE_CHIPS.map((chip) => (
                <Link
                  className="landing-resource-chip"
                  key={chip.label}
                  to={`/resources#${chip.slug}`}
                  aria-label={`Open resources for ${chip.label}`}
                >
                  {chip.label}
                </Link>
              ))}
            </div>
            <Link className="button primary landing-resource-cta" to="/resources">Open Resources</Link>
          </div>
        </section>

        <LandingCtaBand />

        <PublicFooter>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/login">Sign In</Link>
        </PublicFooter>
      </main>
    </GuestOnly>
  );
}

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () => login(email, password),
    onSuccess: async () => {
      navigate(await resolveAuthenticatedPath(), { replace: true });
    },
  });

  return (
    <GuestOnly>
      <AuthScreen title="Sign in" subtitle="Continue to your profile and introductions.">
        <form className="form auth-form" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
          <ErrorBanner error={mutation.error} />
          <Field label="Email"><input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" inputMode="email" required /></Field>
          <Field label="Password"><input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" required /></Field>
          <Link className="auth-forgot-link" to="/forgot-password">Forgot password?</Link>
          <div className="auth-form-actions">
            <Button loading={mutation.isPending} type="submit">Sign In</Button>
            <Link className="auth-switch-link" to="/signup">Join with code</Link>
          </div>
        </form>
      </AuthScreen>
    </GuestOnly>
  );
}

function PasswordInput({
  id,
  label,
  value,
  onChange,
  autoFocus = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="field">
      <label className="auth-password-label" htmlFor={id}>{label}</label>
      <span className="auth-password-control">
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          type={visible ? "text" : "password"}
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          maxLength={PASSWORD_MAX_LENGTH}
          autoFocus={autoFocus}
          required
        />
        <button
          type="button"
          className="auth-password-toggle"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={visible}
        >
          {visible ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.requestPasswordReset(email.trim().toLowerCase()),
  });

  if (mutation.isSuccess) {
    return (
      <GuestOnly>
        <AuthScreen title="Check your email" subtitle="If a Yara account exists for that address, a private reset link is on its way." backTo="/login">
          <div className="auth-recovery-state" role="status" aria-live="polite">
            <span className="auth-recovery-icon"><MailCheck size={25} aria-hidden="true" /></span>
            <div>
              <strong>We sent the next step privately</strong>
              <p>The link expires in 60 minutes and works once. Check spam or promotions if it does not arrive within a few minutes.</p>
            </div>
          </div>
          <div className="auth-form-actions auth-recovery-actions">
            <Link className="button primary" to="/login">Back to sign in</Link>
            <button className="auth-switch-link" type="button" onClick={() => mutation.reset()}>Use a different email</button>
          </div>
        </AuthScreen>
      </GuestOnly>
    );
  }

  return (
    <GuestOnly>
      <AuthScreen title="Reset your password" subtitle="Enter the email you use for Yara. We’ll send a secure reset link if an account exists." backTo="/login">
        <form className="form auth-form" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
          <ErrorBanner error={mutation.error} />
          <Field label="Email">
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              required
            />
          </Field>
          <p className="auth-privacy-note"><LockKeyhole size={15} aria-hidden="true" /> For privacy, Yara shows the same confirmation whether or not an account exists.</p>
          <div className="auth-form-actions">
            <Button loading={mutation.isPending} type="submit">Send reset link</Button>
            <Link className="auth-switch-link" to="/login">Back to sign in</Link>
          </div>
        </form>
      </AuthScreen>
    </GuestOnly>
  );
}

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const { isAuthenticated, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const verificationRequestRef = useRef<{
    key: string;
    promise: ReturnType<typeof api.verifyEmail>;
  } | null>(null);
  const refreshedUserRef = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"checking" | "verified" | "invalid" | "error">(token ? "checking" : "invalid");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    const requestKey = `${token}:${attempt}`;
    if (verificationRequestRef.current?.key !== requestKey) {
      verificationRequestRef.current = {
        key: requestKey,
        promise: api.verifyEmail(token),
      };
    }
    const request = verificationRequestRef.current.promise;
    let subscribed = true;
    setStatus("checking");
    setError(null);
    request
      .then(() => { if (subscribed) setStatus("verified"); })
      .catch(async (verifyError) => {
        if (!subscribed) return;
        if (verifyError instanceof ApiError && verifyError.statusCode === 400) {
          const alreadyVerified = await confirmSignedInEmailIsVerified(isAuthenticated, refreshUser);
          if (!subscribed) return;
          if (alreadyVerified) {
            refreshedUserRef.current = true;
            void queryClient.invalidateQueries({ queryKey: ["profile"] });
            setStatus("verified");
            return;
          }
          setStatus("invalid");
        } else {
          setError(formatApiError(verifyError));
          setStatus("error");
        }
    });
    return () => { subscribed = false; };
  }, [attempt, isAuthenticated, queryClient, refreshUser, token]);

  useEffect(() => {
    if (status !== "verified" || !isAuthenticated || refreshedUserRef.current) return;
    refreshedUserRef.current = true;
    void queryClient.invalidateQueries({ queryKey: ["profile"] });
    refreshUser()
      .catch(() => { refreshedUserRef.current = false; });
  }, [isAuthenticated, queryClient, refreshUser, status]);

  if (status === "checking") {
    return (
      <AuthScreen title="Verifying your email" subtitle="One moment while Yara checks this private link." backTo={isAuthenticated ? "/app/match" : "/login"}>
        <div className="auth-recovery-loading" role="status" aria-live="polite">
          <Loader2 className="spin" size={22} aria-hidden="true" />
          <span>Verifying securely…</span>
        </div>
      </AuthScreen>
    );
  }

  if (status === "verified") {
    return (
      <AuthScreen title="Email verified" subtitle="Your email is confirmed and this matching requirement is complete." backTo={isAuthenticated ? "/app/match" : "/login"}>
        <div className="auth-recovery-state is-success" role="status" aria-live="polite">
          <span className="auth-recovery-icon"><CheckCircle2 size={25} aria-hidden="true" /></span>
          <div>
            <strong>You’re verified</strong>
            <p>Yara can now include your profile in matching once your other readiness requirements are complete.</p>
          </div>
        </div>
        <div className="auth-form-actions auth-recovery-actions">
          <Link className="button primary" to={isAuthenticated ? "/app/match" : "/login"}>
            {isAuthenticated ? "Return to matching" : "Sign in to Yara"}
          </Link>
        </div>
      </AuthScreen>
    );
  }

  if (status === "error") {
    return (
      <AuthScreen title="We couldn’t verify this link" subtitle="Your email has not been verified yet." backTo={isAuthenticated ? "/settings/account" : "/login"}>
        <div className="auth-recovery-state is-warning" role="alert">
          <span className="auth-recovery-icon"><MailCheck size={24} aria-hidden="true" /></span>
          <div>
            <strong>The link may still be valid</strong>
            <p>{error || "Check your connection and try again."}</p>
          </div>
        </div>
        <div className="auth-form-actions auth-recovery-actions">
          <button className="button primary" type="button" onClick={() => setAttempt((current) => current + 1)}>Try again</button>
          <Link className="auth-switch-link" to={isAuthenticated ? "/settings/account" : "/login"}>
            {isAuthenticated ? "Open Account settings" : "Back to sign in"}
          </Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title="This verification link can’t be used" subtitle="It may have expired, already been used, or been replaced by a newer link." backTo={isAuthenticated ? "/settings/account" : "/login"}>
      <div className="auth-recovery-state is-warning" role="alert">
        <span className="auth-recovery-icon"><MailCheck size={24} aria-hidden="true" /></span>
        <div>
          <strong>Check your verification status</strong>
          <p>{isAuthenticated ? "Open Account settings to confirm your status or request a fresh link." : "Sign in to confirm your status or request a fresh link from Account settings."}</p>
        </div>
      </div>
      <div className="auth-form-actions auth-recovery-actions">
        <Link className="button primary" to={isAuthenticated ? "/settings/account" : "/login"}>
          {isAuthenticated ? "Request a new link" : "Sign in to continue"}
        </Link>
      </div>
    </AuthScreen>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const { clearSession } = useAuth();
  const [tokenStatus, setTokenStatus] = useState<"checking" | "valid" | "invalid" | "error">(token ? "checking" : "invalid");
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [completed, setCompleted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const requirements = passwordRequirements(password, confirmation);
  const validationError = validatePasswordResetInput(password, confirmation);

  useEffect(() => {
    if (!token || completed) {
      if (!completed) setTokenStatus("invalid");
      return;
    }
    let cancelled = false;
    setTokenStatus("checking");
    setTokenError(null);
    api.validatePasswordResetToken(token)
      .then(() => { if (!cancelled) setTokenStatus("valid"); })
      .catch((error) => {
        if (!cancelled) {
          if (error instanceof ApiError && error.statusCode === 400) {
            setTokenStatus("invalid");
          } else {
            setTokenError(formatApiError(error));
            setTokenStatus("error");
          }
        }
      });
    return () => { cancelled = true; };
  }, [completed, token, validationAttempt]);

  const mutation = useMutation({
    mutationFn: async () => {
      const error = validatePasswordResetInput(password, confirmation);
      if (error) throw new Error(error);
      return api.resetPassword(token, password);
    },
    onSuccess: async () => {
      // The reset endpoint already revoked the server-side subscription. Remove
      // the browser subscription locally without using invalidated credentials.
      await clearSession({ notifyPushServer: false });
      setCompleted(true);
      navigate("/reset-password", { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.statusCode === 400) {
        setTokenStatus("invalid");
      }
    },
  });

  if (completed) {
    return (
      <AuthScreen title="Password changed" subtitle="Your account is secure and every previously signed-in device has been signed out." backTo="/login">
        <div className="auth-recovery-state is-success" role="status" aria-live="polite">
          <span className="auth-recovery-icon"><CheckCircle2 size={25} aria-hidden="true" /></span>
          <div>
            <strong>Your new password is ready</strong>
            <p>Sign in again on the devices you trust. For added safety, check your email for a security confirmation.</p>
          </div>
        </div>
        <div className="auth-form-actions auth-recovery-actions">
          <Link className="button primary" to="/login">Sign in with new password</Link>
        </div>
      </AuthScreen>
    );
  }

  if (tokenStatus === "checking") {
    return (
      <AuthScreen title="Checking your link" subtitle="One moment while Yara verifies this private reset link." backTo="/login">
        <div className="auth-recovery-loading" role="status" aria-live="polite">
          <Loader2 className="spin" size={22} aria-hidden="true" />
          <span>Verifying securely…</span>
        </div>
      </AuthScreen>
    );
  }

  if (tokenStatus === "invalid") {
    return (
      <AuthScreen title="This link has expired" subtitle="Reset links work once and expire after 60 minutes. Request a fresh link to continue." backTo="/login">
        <div className="auth-recovery-state is-warning" role="alert">
          <span className="auth-recovery-icon"><LockKeyhole size={24} aria-hidden="true" /></span>
          <div>
            <strong>Your password has not changed</strong>
            <p>This can also happen when a newer reset email has replaced an older one.</p>
          </div>
        </div>
        <div className="auth-form-actions auth-recovery-actions">
          <Link className="button primary" to="/forgot-password">Request a new link</Link>
          <Link className="auth-switch-link" to="/login">Back to sign in</Link>
        </div>
      </AuthScreen>
    );
  }

  if (tokenStatus === "error") {
    return (
      <AuthScreen title="We couldn’t check this link" subtitle="Yara could not securely verify the reset link right now. Your password has not changed." backTo="/login">
        <div className="auth-recovery-state is-warning" role="alert">
          <span className="auth-recovery-icon"><LockKeyhole size={24} aria-hidden="true" /></span>
          <div>
            <strong>The link may still be valid</strong>
            <p>{tokenError || "Check your connection, then try again. If the problem continues, request a fresh link."}</p>
          </div>
        </div>
        <div className="auth-form-actions auth-recovery-actions">
          <button className="button primary" type="button" onClick={() => setValidationAttempt((attempt) => attempt + 1)}>Try again</button>
          <Link className="auth-switch-link" to="/forgot-password">Request a new link</Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title="Choose a new password" subtitle="Use a unique password you do not use for another account." backTo="/login">
      <form className="form auth-form" onSubmit={(event) => {
        event.preventDefault();
        const error = validatePasswordResetInput(password, confirmation);
        setLocalError(error);
        if (!error) mutation.mutate();
      }}>
        {(localError || mutation.error) && <div className="banner error" role="alert">{localError || formatApiError(mutation.error)}</div>}
        <PasswordInput id="new-password" label="New password" value={password} onChange={(value) => { setPassword(value); setLocalError(null); }} autoFocus />
        <PasswordInput id="confirm-password" label="Confirm new password" value={confirmation} onChange={(value) => { setConfirmation(value); setLocalError(null); }} />
        <ul className="auth-password-requirements" aria-label="Password requirements" aria-live="polite">
          {requirements.map((requirement) => (
            <li className={requirement.met ? "is-met" : ""} key={requirement.key}>
              <Check size={15} aria-hidden="true" />
              <span>{requirement.label}</span>
            </li>
          ))}
        </ul>
        <p className="auth-privacy-note"><LockKeyhole size={15} aria-hidden="true" /> Saving this password signs out every other device for your protection.</p>
        <div className="auth-form-actions">
          <Button loading={mutation.isPending} disabled={validationError !== null} type="submit">Save new password</Button>
        </div>
      </form>
    </AuthScreen>
  );
}

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const initialPrefill = parseSignupInvitePrefill(location.search);
  const [email, setEmail] = useState(initialPrefill.email);
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(initialPrefill.inviteCode);
  const [lockedPrefill, setLockedPrefill] = useState({
    email: initialPrefill.hasEmail,
    inviteCode: initialPrefill.hasInviteCode,
  });
  const [attestations, setAttestations] = useState<boolean[]>(() => ELIGIBILITY_ATTESTATIONS.map(() => false));
  const allAttested = attestations.every(Boolean);
  useEffect(() => {
    const prefill = parseSignupInvitePrefill(location.search);
    setEmail(prefill.email);
    setInviteCode(prefill.inviteCode);
    setLockedPrefill({
      email: prefill.hasEmail,
      inviteCode: prefill.hasInviteCode,
    });
  }, [location.search]);
  const mutation = useMutation({
    mutationFn: () => {
      if (!allAttested) throw new Error("Please confirm the eligibility statements before creating an account.");
      return signup(email, password, inviteCode);
    },
    onSuccess: async () => {
      navigate(await resolveAuthenticatedPath(), { replace: true });
    },
  });

  return (
    <GuestOnly>
      <AuthScreen title="Join with invite code" subtitle="Use a code from someone already in the community. That trusted path is how Yara grows.">
        <form className="form auth-form auth-form--signup" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
          <ErrorBanner error={mutation.error} />
          <Field label="Invite code"><input className={lockedPrefill.inviteCode ? "input--locked" : undefined} value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} readOnly={lockedPrefill.inviteCode} aria-readonly={lockedPrefill.inviteCode} /></Field>
          <Field label="Email">
            <input className={lockedPrefill.email ? "input--locked" : undefined} value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" inputMode="email" readOnly={lockedPrefill.email} aria-readonly={lockedPrefill.email} />
            <small className="field-note">We’ll send a private verification link. Your email must be verified before matching.</small>
          </Field>
          <Field label="Password"><input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" /></Field>
          <div className="attestation-card auth-attestation-card">
            <strong>Before joining</strong>
            {ELIGIBILITY_ATTESTATIONS.map((item, index) => (
              <label className="attestation-row" key={item}>
                <input
                  type="checkbox"
                  checked={attestations[index]}
                  onChange={(event) => setAttestations((current) => current.map((checked, currentIndex) => currentIndex === index ? event.target.checked : checked))}
                />
                <span>{item}</span>
              </label>
            ))}
            <p>{SIGNUP_COMMUNITY_NOTE}</p>
          </div>
          <div className="auth-form-actions">
            <Button loading={mutation.isPending} disabled={!allAttested} type="submit">Create account</Button>
            <Link className="auth-switch-link" to="/login">I already have an account</Link>
          </div>
        </form>
      </AuthScreen>
    </GuestOnly>
  );
}

function Tutorial() {
  return (
    <PublicShell>
      <AppHeader title="How Yara Works" backTo="/" />
      <div className="timeline">
        {HOW_YARA_WORKS_STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <article className={`timeline-card ${step.tone}`} key={step.title}>
              <div className="timeline-icon"><Icon size={22} /></div>
              <div>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{step.title}</strong>
                <p>{step.body}</p>
              </div>
            </article>
          );
        })}
      </div>
      <Link className="button primary" to="/signup">Get Started</Link>
    </PublicShell>
  );
}

function PublicContentShell({
  kicker,
  title,
  children,
  shellClass = "",
}: {
  kicker: React.ReactNode;
  title: string;
  children: React.ReactNode;
  shellClass?: string;
}) {
  return (
    <main className={`landing-shell public-content-shell${shellClass ? ` ${shellClass}` : ""}`}>
      <PublicNav />
      <section className="public-content-hero">
        <Link className="public-back-link" to="/"><ArrowLeft size={16} /> Back to home</Link>
        <div className="section-kicker">{kicker}</div>
        <h1>{title}</h1>
      </section>
      {children}
      <PublicFooter>
        <Link to="/">Home</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
      </PublicFooter>
    </main>
  );
}

function useInAppRailActiveSection(
  panelRef: RefObject<HTMLDivElement | null>,
  sectionIds: readonly string[],
) {
  const [activeSection, setActiveSection] = useState(sectionIds[0] ?? "");

  useEffect(() => {
    const root = panelRef.current?.closest(".settings-document-scroll");
    if (!root || !sectionIds.length) return undefined;

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section instanceof HTMLElement);
    if (!sections.length) return undefined;

    const lastSectionId = sections[sections.length - 1].id;

    const syncActiveSection = () => {
      const atBottom = root.scrollTop + root.clientHeight >= root.scrollHeight - 24;
      if (atBottom) {
        setActiveSection(lastSectionId);
        return;
      }

      const rail = root.querySelector(".resources-in-app-rail");
      const anchor = root.getBoundingClientRect().top + (rail instanceof HTMLElement ? rail.offsetHeight : 44) + 8;
      let nextId = sections[0].id;

      for (const section of sections) {
        if (section.getBoundingClientRect().top <= anchor) {
          nextId = section.id;
        }
      }

      setActiveSection(nextId);
    };

    syncActiveSection();
    root.addEventListener("scroll", syncActiveSection, { passive: true });

    const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(syncActiveSection) : null;
    resizeObserver?.observe(root);
    for (const section of sections) resizeObserver?.observe(section);

    return () => {
      root.removeEventListener("scroll", syncActiveSection);
      resizeObserver?.disconnect();
    };
  }, [panelRef, sectionIds]);

  return [activeSection, setActiveSection] as const;
}

function SafetyInAppPanel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useInAppRailActiveSection(scrollRef, SAFETY_SECTION_IDS);

  const jumpToSection = (targetId: string) => {
    setActiveSection(targetId);
    const root = scrollRef.current?.closest(".settings-document-scroll");
    scrollToResourceTarget(targetId, root instanceof HTMLElement ? root : null);
  };

  return (
    <div className="resources-in-app safety-in-app" ref={scrollRef}>
      <AppHero
        eyebrow="Safety Center"
        title="Move with care"
        description="Invite-only access, paced matching, and in-app chat are helpful—but they are not background checks or guarantees."
        icon={Shield}
        className="settings-sub-hero resources-in-app-hero"
      />
      <p className="resources-in-app-disclaimer">
        Use judgment, move carefully, and report anything that feels unsafe.
      </p>

      <div className="resources-in-app-rail-mount">
        <div className="resources-in-app-rail" role="navigation" aria-label="Jump to a section">
          {SAFETY_NAV_ITEMS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`resources-in-app-rail-btn${activeSection === item.id ? " is-active" : ""}`}
              aria-current={activeSection === item.id ? "true" : undefined}
              onClick={() => jumpToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <section className="resources-in-app-section" id="safety-limits" aria-labelledby="safety-limits-heading">
        <article className="resources-in-app-yara-card">
          <div className="resources-in-app-yara-head">
            <div className="resources-in-app-card-icon" aria-hidden="true">
              <Shield size={22} />
            </div>
            <div>
              <h2 id="safety-limits-heading">Yara can lower awkwardness, not remove risk</h2>
              <p>Helpful rails are not guarantees. Protect yourself the way you would in any new connection.</p>
            </div>
          </div>
          <ul className="resources-in-app-yara-list">
            {SAFETY_LIMIT_NOTES.map((note) => <li key={note}>{note}</li>)}
          </ul>
          <div className="safety-in-app-rails" aria-label="Built-in safety rails">
            {SAFETY_RAILS.map(({ label, icon: Icon }) => (
              <div className="safety-in-app-rail-chip" key={label}>
                <span className="safety-in-app-rail-chip-icon" aria-hidden="true"><Icon size={16} /></span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="resources-in-app-section" id="safety-habits" aria-labelledby="safety-habits-heading">
        <div className="resources-in-app-section-head">
          <h2 id="safety-habits-heading">Safety habits</h2>
          <span>{SAFETY_GUIDELINES.length}</span>
        </div>
        <p className="resources-in-app-section-lede">Practical habits for early conversations, privacy, and first meetings.</p>
        <div className="resources-in-app-stack">
          {SAFETY_GUIDELINES.map((item) => (
            <article className="resources-in-app-card" key={item.title}>
              <header className="resources-in-app-card-head">
                <div className="resources-in-app-card-icon" aria-hidden="true">
                  <Shield size={22} />
                </div>
                <div>
                  <h3>{item.title}</h3>
                </div>
              </header>
              <p className="resources-in-app-card-copy">{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="resources-in-app-section" id="safety-report" aria-labelledby="safety-report-heading">
        <article className="resources-in-app-yara-card">
          <div className="resources-in-app-yara-head">
            <div className="resources-in-app-card-icon" aria-hidden="true">
              <Flag size={22} />
            </div>
            <div>
              <h2 id="safety-report-heading">Use the report flow when something is wrong</h2>
              <p>Connected matches have a report option in chat. Use it for harassment, fake profiles, explicit messages, pressure, scams, or threats.</p>
            </div>
          </div>
          <ul className="resources-in-app-yara-list">
            <li>Disconnecting ends the current connection. Reporting creates a review item for moderation.</li>
            <li>For immediate danger or illegal conduct, contact local emergency services or the appropriate civil authority.</li>
          </ul>
        </article>
        <div className="settings-list resources-in-app-inline-link">
          <StackNavLink className="setting-row setting-row--rich" to="/settings/resources#safety-boundaries">
            <BookMarked />
            <span className="setting-copy">
              <strong>Boundary questions in Resources</strong>
              <small>Prompts for early conversations</small>
            </span>
            <ChevronRight />
          </StackNavLink>
        </div>
      </section>
    </div>
  );
}

function SafetyPanel() {
  return (
    <>
      <section className="landing-band">
        <div className="landing-two-col">
          <div>
            <h2>Yara can lower awkwardness, not remove risk.</h2>
            <p>Invite-only access, paced matching, and in-app chat are helpful, but they are not background checks or guarantees. Use judgment, move carefully, and report anything that feels unsafe.</p>
          </div>
          <div className="copy-stack">
            <p>Yara does not verify every claim a member makes about identity, faith status, relationship status, character, location, work, education, or intentions.</p>
            <p>Yara does not promise matches, relationships, compatibility, marriage, or offline safety.</p>
          </div>
        </div>
      </section>

      <section className="landing-grid-section">
        <div className="section-kicker"><CheckCircle2 size={18} /> Safety Habits</div>
        <div className="landing-card-grid safety-grid">
          {SAFETY_GUIDELINES.map((item) => (
            <article className="landing-feature" key={item.title}>
              <Shield size={22} />
              <h2>{item.title}</h2>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-band">
        <div className="landing-two-col">
          <div>
            <h2>Use the report flow when something is wrong.</h2>
            <p>Connected matches now have a report option in the web UI. Reports should be used for harassment, fake profiles, explicit or inappropriate messages, pressure, scams, threats, or behavior that violates the purpose of Yara.</p>
          </div>
          <div className="copy-stack">
            <p>Disconnecting ends the current connection. Reporting creates a review item for the future moderation workflow.</p>
            <p>For immediate danger or illegal conduct, contact local emergency services or the appropriate civil authority.</p>
          </div>
        </div>
      </section>
    </>
  );
}

function SafetyPage() {
  return (
    <PublicOrInAppRedirect inAppPath="/settings/safety">
      <PublicContentShell kicker={<><Shield size={18} /> Safety Center</>} title="A casual app still needs serious boundaries.">
        <SafetyPanel />
      </PublicContentShell>
    </PublicOrInAppRedirect>
  );
}

function AppSafetyPage() {
  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Safety Center" backTo="/settings" />
        <div className="settings-document-scroll safety-panel--in-app">
          <SafetyInAppPanel />
        </div>
      </AppShell>
    </Protected>
  );
}

function scrollToResourceTarget(targetId: string, scrollRoot?: HTMLElement | null) {
  const target = document.getElementById(targetId);
  if (!target) return;

  const container = scrollRoot ?? target.closest(".settings-document-scroll");
  const rail = container?.querySelector(".resources-in-app-rail");
  const railOffset = rail instanceof HTMLElement ? rail.offsetHeight + 10 : 12;

  if (container instanceof HTMLElement) {
    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      railOffset;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  } else {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const nextHash = `#${targetId}`;
  if (window.location.hash !== nextHash) {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
  }
}

function ResourceTopicNav() {
  const onJump = (event: MouseEvent<HTMLAnchorElement>, targetId: string) => {
    event.preventDefault();
    scrollToResourceTarget(targetId);
  };

  return (
    <nav className="resource-topic-nav" aria-label="Jump to a topic">
      <span className="resource-topic-nav-label">On this page</span>
      {RESOURCE_QUESTION_GROUPS.map((group) => (
        <a
          key={group.slug}
          className="resource-topic-link"
          href={`#${group.slug}`}
          onClick={(event) => onJump(event, group.slug)}
        >
          {group.title}
        </a>
      ))}
      <a className="resource-topic-link" href="#references" onClick={(event) => onJump(event, "references")}>
        Baha&apos;i references
      </a>
    </nav>
  );
}

function ResourcesInAppPanel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useInAppRailActiveSection(scrollRef, RESOURCE_SECTION_IDS);

  const jumpToSection = (targetId: string) => {
    setActiveSection(targetId);
    const root = scrollRef.current?.closest(".settings-document-scroll");
    scrollToResourceTarget(targetId, root instanceof HTMLElement ? root : null);
  };

  return (
    <div className="resources-in-app" ref={scrollRef}>
      <AppHero
        eyebrow="Acquainted with character"
        title="Meet with care"
        description="Use Yara as a doorway, not a decision-maker. Learn character over time and consult wisely."
        icon={BookMarked}
        className="settings-sub-hero resources-in-app-hero"
      />
      <p className="resources-in-app-disclaimer">
        Educational prompts for reflection and consultation—not legal, clinical, or institutional advice.
      </p>

      <div className="resources-in-app-rail-mount">
        <div className="resources-in-app-rail" role="navigation" aria-label="Jump to a section">
          {RESOURCE_NAV_ITEMS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`resources-in-app-rail-btn${activeSection === item.id ? " is-active" : ""}`}
              aria-current={activeSection === item.id ? "true" : undefined}
              onClick={() => jumpToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <section className="resources-in-app-section" id="resource-on-yara" aria-labelledby="resource-on-yara-heading">
        <article className="resources-in-app-yara-card">
          <div className="resources-in-app-yara-head">
            <div className="resources-in-app-card-icon" aria-hidden="true">
              <HeartHandshake size={22} />
            </div>
            <div>
              <h2 id="resource-on-yara-heading">While you&apos;re on Yara</h2>
              <p>Keep these in mind as you read introductions and decide whether to connect.</p>
            </div>
          </div>
          <ul className="resources-in-app-yara-list">
            {RESOURCE_YARA_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
          </ul>
          <div className="settings-list resources-in-app-inline-link">
            <StackNavLink className="setting-row setting-row--rich" to="/settings/how-yara-works">
              <Info />
              <span className="setting-copy">
                <strong>See how Yara works</strong>
                <small>Introductions, pacing, and privacy</small>
              </span>
              <ChevronRight />
            </StackNavLink>
          </div>
        </article>
      </section>

      <section className="resources-in-app-section" id="questions" aria-labelledby="resource-questions-heading">
        <div className="resources-in-app-section-head">
          <h2 id="resource-questions-heading">Questions worth asking</h2>
          <span>{RESOURCE_QUESTION_GROUPS.length}</span>
        </div>
        <p className="resources-in-app-section-lede">Concrete prompts for early conversations, grouped by theme.</p>
        <div className="resources-in-app-stack">
          {RESOURCE_QUESTION_GROUPS.map((group) => {
            const Icon = RESOURCE_THEME_ICONS[group.slug];
            return (
              <article className="resources-in-app-card" id={group.slug} key={group.slug}>
                <header className="resources-in-app-card-head">
                  <div className="resources-in-app-card-icon" aria-hidden="true">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3>{group.title}</h3>
                  </div>
                </header>
                <ul className="resources-in-app-bullets">
                  {group.questions.map((question) => <li key={question}>{question}</li>)}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section className="resources-in-app-section" id="references" aria-labelledby="resource-references-heading">
        <div className="resources-in-app-section-head">
          <h2 id="resource-references-heading">Baha&apos;i references</h2>
          <span>{RESOURCE_REFERENCES.length}</span>
        </div>
        <p className="resources-in-app-section-lede">
          Grouped by what each passage is helping you discern. The takeaway comes first; the quotation supports it.
        </p>
        <div className="resources-in-app-stack">
          {RESOURCE_REF_GROUPS.map((group) => (
            <section className="resources-in-app-ref-group" key={group.id} aria-labelledby={`resource-ref-group-${group.id}`}>
              <header className="resources-in-app-ref-group-head">
                <h3 id={`resource-ref-group-${group.id}`}>{group.title}</h3>
                <p>{group.lede}</p>
              </header>
              <div className="resources-in-app-ref-stack">
                {RESOURCE_REFERENCES.filter((item) => item.group === group.id).map((item) => (
                  <article className="resources-in-app-ref-card" key={item.principle}>
                    <p className="resources-in-app-ref-point">{item.guidance}</p>
                    <p className="resources-in-app-ref-theme">{item.principle}</p>
                    <blockquote className="resources-in-app-ref-quote">&ldquo;{item.quote}&rdquo;</blockquote>
                    <cite className="resources-in-app-ref-cite">
                      <a href={item.citationUrl} target="_blank" rel="noreferrer">{item.citation}</a>
                    </cite>
                    <p className="resources-in-app-ref-note">{item.resourceNote}</p>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="resources-in-app-section-head resources-in-app-section-head--sub">
          <h3>Read in context</h3>
        </div>
        <div className="settings-list resources-in-app-links">
          {RESOURCE_SOURCE_LINKS.map((link) => (
            <a
              className="setting-row setting-row--rich"
              href={link.href}
              key={link.href}
              rel="noreferrer"
              target="_blank"
            >
              <ExternalLink />
              <span className="setting-copy">
                <strong>{link.label}</strong>
                <small>bahai.org</small>
              </span>
              <ChevronRight />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

function ResourceReferenceList() {
  return (
    <div className="resource-ref-groups">
      {RESOURCE_REF_GROUPS.map((group) => (
        <section className="resource-ref-group" key={group.id} aria-labelledby={`resource-ref-group-${group.id}`}>
          <header className="resource-ref-group-head">
            <h3 id={`resource-ref-group-${group.id}`}>{group.title}</h3>
            <p>{group.lede}</p>
          </header>
          <div className="resource-ref-group-body">
            {RESOURCE_REFERENCES.filter((item) => item.group === group.id).map((item) => (
              <article className="resource-ref-passage" key={item.principle}>
                <p className="resource-ref-point">{item.guidance}</p>
                <p className="resource-ref-theme">{item.principle}</p>
                <figure className="resource-ref-source">
                  <blockquote>&ldquo;{item.quote}&rdquo;</blockquote>
                  <figcaption>
                    <a href={item.citationUrl} target="_blank" rel="noreferrer">{item.citation}</a>
                  </figcaption>
                </figure>
                <p className="resource-ref-note">{item.resourceNote}</p>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ResourcesPanel({ inApp = false }: { inApp?: boolean }) {
  if (inApp) return <ResourcesInAppPanel />;

  return (
    <>
      <section className="resource-page-intro landing-band landing-band--tone-b">
        <div className="resource-page-intro-copy">
          <h2>Use Yara as a doorway, not a decision-maker.</h2>
          <p>Meet with care, learn character over time, consult wisely, and consider how spiritual purpose, material life, family, and service could fit together.</p>
          <p className="resource-page-disclaimer">Educational prompts for reflection and consultation—not legal, clinical, or institutional advice.</p>
        </div>
        <ResourceTopicNav />
      </section>

      <section className="resource-section resource-section--questions landing-band landing-band--tone-a" id="questions" aria-labelledby="resource-questions-heading">
        <div className="resource-section-head">
          <div className="section-kicker" id="resource-questions-heading"><MessageCircle size={18} /> Questions worth asking</div>
          <p className="resource-section-lede">Start here: concrete prompts for early conversations, grouped by theme.</p>
        </div>
        <div className="resource-question-grid">
          {RESOURCE_QUESTION_GROUPS.map((group) => (
            <article className="resource-question-card" id={group.slug} key={group.slug}>
              <h3>{group.title}</h3>
              <ul>
                {group.questions.map((question) => <li key={question}>{question}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="resource-section resource-section--references landing-band landing-band--tone-b" id="references" aria-labelledby="resource-references-heading">
        <div className="resource-section-head">
          <div className="section-kicker" id="resource-references-heading"><BookOpen size={18} /> Baha&apos;i references</div>
          <p className="resource-section-lede">Grouped by what each passage is helping you discern. The takeaway comes first; the quotation supports it.</p>
        </div>
        <ResourceReferenceList />
        <div className="resource-source-row">
          <span className="resource-source-label">Read in context</span>
          {RESOURCE_SOURCE_LINKS.map((link) => (
            <a href={link.href} key={link.href} target="_blank" rel="noreferrer">
              <ExternalLink size={15} /> {link.label}
            </a>
          ))}
        </div>
      </section>

      <section className="resource-yara-callout" aria-labelledby="resource-yara-heading">
        <h2 id="resource-yara-heading">Using this while you are on Yara</h2>
        <ul className="resource-yara-list">
          <li>One introduction at a time—read and respond with care within your window.</li>
          <li>A quiet no is allowed; a yes is permission to begin conversation, not a promise of marriage.</li>
          <li>If interest grows, consult trusted people and take discernment off-app at a healthy pace.</li>
        </ul>
        <Link className="text-link resource-yara-link" to="/#how">See how Yara works</Link>
      </section>
    </>
  );
}

function ResourcesPage() {
  return (
    <PublicOrInAppRedirect inAppPath="/settings/resources">
      <PublicContentShell
        shellClass="public-content-shell--resources"
        kicker={<><BookMarked size={18} /> Acquainted with character</>}
        title="Resources for becoming acquainted with character"
      >
        <ResourcesPanel />
      </PublicContentShell>
    </PublicOrInAppRedirect>
  );
}

function AppResourcesPage() {
  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Resources" backTo="/settings" />
        <div className="settings-document-scroll resources-panel--in-app">
          <ResourcesPanel inApp />
        </div>
      </AppShell>
    </Protected>
  );
}

function CreateProfile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse, retry: false });
  const existingProfile = profileQuery.data?.profile;

  return (
    <Protected>
      <AppShell frameClassName="form-frame">
        <ProfileEditorScreen
          mode="create"
          profile={existingProfile}
          loading={profileQuery.isLoading && !profileQuery.isError}
          createSubmitLabel="Next"
          onSubmit={async (profile, photos) => {
            await saveProfileWithPhotoRollback(
              photos,
              () => (
                existingProfile
                  ? api.updateProfile(profile)
                  : api.createProfile(profile)
              ),
            );
            await queryClient.invalidateQueries({ queryKey: ["profile"] });
            queryClient.setQueryData(ONBOARDING_STATUS_QUERY_KEY, "preferences");
            navigate("/onboarding/preferences", { replace: true });
          }}
        />
      </AppShell>
    </Protected>
  );
}

function PreferenceStrictSwitch({
  checked,
  strictLabel,
  preferLabel,
  onChange,
}: {
  checked: boolean;
  strictLabel: string;
  preferLabel: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="preference-strict">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>
        <strong>{checked ? "Strict match" : "Prefer match"}</strong>
        <small>{checked ? strictLabel : preferLabel}</small>
      </span>
    </label>
  );
}

function buildPreferencesState(initial?: Partial<Preferences>): Preferences {
  return {
    ageYounger: initial?.ageYounger ?? 5,
    ageOlder: initial?.ageOlder ?? 10,
    ageStrictMatch: initial?.ageStrictMatch ?? false,
    locationPreference: initial?.locationPreference ?? "anywhere",
    minHeight: initial?.minHeight,
    maxHeight: initial?.maxHeight,
    heightStrictMatch: initial?.heightStrictMatch ?? false,
    smokingPreference: initial?.smokingPreference ?? "any",
    smokingStrictMatch: initial?.smokingStrictMatch ?? false,
    drinkingPreference: initial?.drinkingPreference ?? "any",
    drinkingStrictMatch: initial?.drinkingStrictMatch ?? false,
    minEducationLevel: initial?.minEducationLevel,
    educationStrictMatch: initial?.educationStrictMatch ?? false,
    wantsChildren: initial?.wantsChildren,
    wantsChildrenStrictMatch: initial?.wantsChildrenStrictMatch ?? false,
    okayWithChildren: initial?.okayWithChildren ?? true,
    okayWithChildrenStrictMatch: initial?.okayWithChildrenStrictMatch ?? false,
    religionPreference: initial?.religionPreference ?? "anyone_interested",
    religionStrictMatch: initial?.religionStrictMatch ?? false,
    preferredLanguages: initial?.preferredLanguages ?? [],
    languageStrictMatch: initial?.languageStrictMatch ?? false,
  };
}

function normalizedLanguageKeys(languages: readonly string[]) {
  return languages
    .map((language) => language.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US"))
    .filter(Boolean)
    .sort();
}

function preferencesEqual(a: Preferences, b: Preferences) {
  const aLanguages = normalizedLanguageKeys(a.preferredLanguages);
  const bLanguages = normalizedLanguageKeys(b.preferredLanguages);
  return (
    a.ageYounger === b.ageYounger &&
    a.ageOlder === b.ageOlder &&
    !!a.ageStrictMatch === !!b.ageStrictMatch &&
    a.locationPreference === b.locationPreference &&
    a.minHeight === b.minHeight &&
    a.maxHeight === b.maxHeight &&
    !!a.heightStrictMatch === !!b.heightStrictMatch &&
    (a.smokingPreference || "any") === (b.smokingPreference || "any") &&
    !!a.smokingStrictMatch === !!b.smokingStrictMatch &&
    (a.drinkingPreference || "any") === (b.drinkingPreference || "any") &&
    !!a.drinkingStrictMatch === !!b.drinkingStrictMatch &&
    (a.minEducationLevel || "") === (b.minEducationLevel || "") &&
    !!a.educationStrictMatch === !!b.educationStrictMatch &&
    a.wantsChildren === b.wantsChildren &&
    !!a.wantsChildrenStrictMatch === !!b.wantsChildrenStrictMatch &&
    !!a.okayWithChildren === !!b.okayWithChildren &&
    !!a.okayWithChildrenStrictMatch === !!b.okayWithChildrenStrictMatch &&
    a.religionPreference === b.religionPreference &&
    !!a.religionStrictMatch === !!b.religionStrictMatch &&
    aLanguages.length === bLanguages.length &&
    aLanguages.every((language, index) => language === bLanguages[index]) &&
    !!a.languageStrictMatch === !!b.languageStrictMatch
  );
}

function PreferencesForm({
  initial,
  onSubmit,
  submitLabel,
  userAge,
  userHeight,
  profileLanguages = [],
  variant = "setup",
  formId = "preferences-form",
  onboardingStep,
  onboardingBackTo,
  onDirtyChange,
  onSavingChange,
}: {
  initial?: Partial<Preferences>;
  onSubmit: (preferences: Preferences) => Promise<void>;
  submitLabel: string;
  userAge?: number;
  userHeight?: number;
  profileLanguages?: readonly Language[];
  variant?: "setup" | "edit";
  formId?: string;
  onboardingStep?: "profile" | "preferences" | "match";
  onboardingBackTo?: string;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [baseline, setBaseline] = useState<Preferences | null>(null);
  const [preferences, setPreferences] = useState<Preferences>(() => buildPreferencesState(initial));
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const update = <K extends keyof Preferences>(key: K, value: Preferences[K]) => setPreferences((current) => ({ ...current, [key]: value }));
  const patchPreferences = (patch: Partial<Preferences>) => setPreferences((current) => ({ ...current, ...patch }));

  const isDirty = useMemo(
    () => (baseline ? !preferencesEqual(preferences, baseline) : variant === "setup"),
    [baseline, preferences, variant],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [onSavingChange, saving]);

  const hasHeightPreference = preferences.minHeight != null || preferences.maxHeight != null;
  const hasSmokingPreference = (preferences.smokingPreference || "any") !== "any";
  const hasDrinkingPreference = (preferences.drinkingPreference || "any") !== "any";
  const hasChildrenPreference = preferences.wantsChildren !== undefined;
  const hasEducationPreference = !!preferences.minEducationLevel;
  const hasLanguagePreference = preferences.preferredLanguages.length > 0;
  const preferMobilePreferenceControls = variant === "setup";
  const availableLanguages = useMemo(() => [...new Map(
    profileLanguages
      .map(({ language }) => language.normalize("NFKC").trim().replace(/\s+/g, " "))
      .filter(Boolean)
      .map((language) => [language.toLocaleLowerCase("en-US"), language]),
  ).values()], [profileLanguages]);
  const selectedLanguageKeys = useMemo(
    () => new Set(normalizedLanguageKeys(preferences.preferredLanguages)),
    [preferences.preferredLanguages],
  );
  const togglePreferredLanguage = (language: string) => {
    const key = language.toLocaleLowerCase("en-US");
    const selected = selectedLanguageKeys.has(key);
    const preferredLanguages = selected
      ? preferences.preferredLanguages.filter(
          (candidate) => candidate.toLocaleLowerCase("en-US") !== key,
        )
      : [...preferences.preferredLanguages, language];
    patchPreferences({
      preferredLanguages,
      languageStrictMatch: preferredLanguages.length
        ? preferences.languageStrictMatch
        : false,
    });
  };

  useEffect(() => {
    if (!initial) return;
    const next = buildPreferencesState(initial);
    setPreferences(next);
    setBaseline(next);
  }, [initial]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(preferences);
      if (variant === "edit") {
        setBaseline(preferences);
      }
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const preferenceSections = (
    <>
      <section className="preferences-section">
        <div className="preference-group-head">
          <span>Age window</span>
          <p>Choose your preferred age range for matches.</p>
        </div>
        <div className="preferences-group">
          <AgeRangePicker
            userAge={userAge}
            ageYounger={preferences.ageYounger}
            ageOlder={preferences.ageOlder}
            preferMobileControls={preferMobilePreferenceControls}
            onChangeYounger={(value) => update("ageYounger", value)}
            onChangeOlder={(value) => update("ageOlder", value)}
          />
          <PreferenceStrictSwitch
            checked={!!preferences.ageStrictMatch}
            strictLabel="Only introduce people inside this age window."
            preferLabel="May include matches up to three years outside this window."
            onChange={(checked) => update("ageStrictMatch", checked)}
          />
        </div>
      </section>

      <section className="preferences-section">
        <div className="preference-group-head">
          <span>Place & reach</span>
          <p>Choose whether matching should stay nearby or include people open to distance.</p>
        </div>
        <div className="preferences-group">
          <SelectField label="Location" value={preferences.locationPreference} onChange={(value) => update("locationPreference", value)} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "same_city", label: "Same city" },
            { value: "same_country", label: "Same country" },
            { value: "anywhere", label: "Anywhere" },
          ] as const} />
        </div>
      </section>

      <section className="preferences-section">
        <div className="preference-group-head">
          <span>Communication</span>
          <p>Select every language you would be comfortable using together. Sharing any one selected language counts.</p>
        </div>
        <p className="preference-subhead">Which languages should they speak?</p>
        <div className="preferences-group">
          <div className="preference-language-picker">
            {availableLanguages.length ? (
              <div className="chip-grid preference-language-chips">
                {availableLanguages.map((language) => {
                  const selected = selectedLanguageKeys.has(language.toLocaleLowerCase("en-US"));
                  return (
                    <button
                      className={`chip ${selected ? "selected" : ""}`}
                      type="button"
                      key={language}
                      aria-pressed={selected}
                      onClick={() => togglePreferredLanguage(language)}
                    >
                      {selected ? <Check size={15} aria-hidden="true" /> : null}
                      {language}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="field-note">
                Add a language to your profile before setting a language preference.
              </p>
            )}
            <p className="field-note">
              {hasLanguagePreference
                ? `${preferences.preferredLanguages.length} selected`
                : "No language preference"}
            </p>
          </div>
          {hasLanguagePreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.languageStrictMatch}
              strictLabel="Only introduce people who speak at least one selected language."
              preferLabel="Language guides who Yara prioritizes."
              onChange={(checked) => update("languageStrictMatch", checked)}
            />
          )}
        </div>
      </section>

      <section className="preferences-section">
        <div className="preference-group-head">
          <span>Life & lifestyle</span>
        </div>
        <p className="preference-subhead">How tall can they be?</p>
        <div className="preferences-group">
          <HeightRangePicker
            minHeight={preferences.minHeight}
            maxHeight={preferences.maxHeight}
            userHeight={userHeight}
            preferMobileControls={preferMobilePreferenceControls}
            onChangeMin={(value) => update("minHeight", value)}
            onChangeMax={(value) => update("maxHeight", value)}
          />
          {hasHeightPreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.heightStrictMatch}
              strictLabel="Treat height range as a deal breaker."
              preferLabel="Height range guides who Yara prioritizes."
              onChange={(checked) => update("heightStrictMatch", checked)}
            />
          )}
        </div>
        <p className="preference-subhead">Is it okay if they smoke?</p>
        <div className="preferences-group">
          <SelectField label="Smoking" value={(preferences.smokingPreference || "any") as any} onChange={(value) => patchPreferences({
            smokingPreference: value as any,
            smokingStrictMatch: value === "any" ? false : preferences.smokingStrictMatch,
          })} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "any", label: "No preference" },
            { value: "ok", label: "Okay" },
            { value: "no", label: "No" },
          ] as any} />
          {hasSmokingPreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.smokingStrictMatch}
              strictLabel="Treat smoking preference as a deal breaker."
              preferLabel="Smoking preference guides who Yara prioritizes."
              onChange={(checked) => update("smokingStrictMatch", checked)}
            />
          )}
        </div>
        <p className="preference-subhead">Is it okay if they drink?</p>
        <div className="preferences-group">
          <SelectField label="Drinking" value={(preferences.drinkingPreference || "any") as any} onChange={(value) => patchPreferences({
            drinkingPreference: value as any,
            drinkingStrictMatch: value === "any" ? false : preferences.drinkingStrictMatch,
          })} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "any", label: "No preference" },
            { value: "ok", label: "Okay" },
            { value: "no", label: "No" },
          ] as any} />
          {hasDrinkingPreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.drinkingStrictMatch}
              strictLabel="Treat drinking preference as a deal breaker."
              preferLabel="Drinking preference guides who Yara prioritizes."
              onChange={(checked) => update("drinkingStrictMatch", checked)}
            />
          )}
        </div>
      </section>

      <section className="preferences-section">
        <div className="preference-group-head">
          <span>Family, faith & education</span>
          <p>These preferences shape the final compatibility review.</p>
        </div>
        <p className="preference-subhead">Do they want children?</p>
        <div className="preferences-group">
          <SelectField label="Wants children" value={preferences.wantsChildren === undefined ? "any" : preferences.wantsChildren ? "yes" : "no"} onChange={(value) => patchPreferences({
            wantsChildren: value === "any" ? undefined : value === "yes",
            wantsChildrenStrictMatch: value === "any" ? false : preferences.wantsChildrenStrictMatch,
          })} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "any", label: "No preference" },
            { value: "yes", label: "Wants children" },
            { value: "no", label: "Does not want children" },
          ] as const} />
          {hasChildrenPreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.wantsChildrenStrictMatch}
              strictLabel="Treat desire for children as a deal breaker."
              preferLabel="Children preference guides who Yara prioritizes."
              onChange={(checked) => update("wantsChildrenStrictMatch", checked)}
            />
          )}
        </div>
        <p className="preference-subhead">Is it okay if they already have children?</p>
        <div className="preferences-group">
          <SelectField label="Already has children" value={preferences.okayWithChildren ? "ok" : "no"} onChange={(value) => update("okayWithChildren", value === "ok")} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "ok", label: "Okay" },
            { value: "no", label: "Prefer not" },
          ] as const} />
          <PreferenceStrictSwitch
            checked={!!preferences.okayWithChildrenStrictMatch}
            strictLabel="Treat this children preference as a deal breaker."
            preferLabel="This preference guides who Yara prioritizes."
            onChange={(checked) => update("okayWithChildrenStrictMatch", checked)}
          />
        </div>
        <p className="preference-subhead">What&apos;s the minimum education?</p>
        <div className="preferences-group">
          <SelectField label="Minimum education" value={(preferences.minEducationLevel || "") as any} onChange={(value) => patchPreferences({
            minEducationLevel: value ? (value as Preferences["minEducationLevel"]) : undefined,
            educationStrictMatch: !value ? false : preferences.educationStrictMatch,
          })} preferMobileControls={preferMobilePreferenceControls} options={[{ value: "", label: "No minimum" }, ...MIN_EDUCATION_LEVELS] as any} />
          {hasEducationPreference && (
            <PreferenceStrictSwitch
              checked={!!preferences.educationStrictMatch}
              strictLabel="Treat education level as a deal breaker."
              preferLabel="Education guides who Yara prioritizes."
              onChange={(checked) => update("educationStrictMatch", checked)}
            />
          )}
        </div>
        <p className="preference-subhead">Who should Yara consider by faith?</p>
        <div className="preferences-group">
          <SelectField label="Faith Preference" value={preferences.religionPreference} onChange={(value) => update("religionPreference", value)} preferMobileControls={preferMobilePreferenceControls} options={[
            { value: "anyone_interested", label: "Anyone interested in the Faith" },
            { value: "only_bahai", label: "Only Baha'is" },
          ] as const} />
          <PreferenceStrictSwitch
            checked={!!preferences.religionStrictMatch}
            strictLabel="Treat faith preference as a deal breaker."
            preferLabel="Faith preference guides who Yara prioritizes."
            onChange={(checked) => update("religionStrictMatch", checked)}
          />
        </div>
      </section>
    </>
  );

  const formBody = (
    <>
      <ErrorBanner error={error} />
      {preferenceSections}
    </>
  );

  return (
    <form className="form preferences-form" id={formId} onSubmit={submit}>
      <div className="preferences-form-scroll">
        {onboardingStep ? (
          <OnboardingFormLayout step={onboardingStep} backTo={onboardingBackTo}>
            {formBody}
          </OnboardingFormLayout>
        ) : (
          formBody
        )}
      </div>
      {variant === "setup" && (
        <div className="preferences-form-footer">
          <Button className="preferences-form-submit" loading={saving} type="submit"><Check size={16} /> {submitLabel}</Button>
        </div>
      )}
    </form>
  );
}
function SetupPreferences() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [ready, setReady] = useState(false);
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse });
  const profile = profileQuery.data?.profile;
  const userAge = profile?.dateOfBirth
    ? ageFromDateOfBirth(profile.dateOfBirth)
    : undefined;
  const userHeight = profile?.height;
  const initialPreferences = useMemo(
    () => (
      profile && userAge !== undefined && userHeight !== undefined
        ? buildOnboardingPreferenceDefaults({
            gender: profile.gender,
            age: userAge,
            height: userHeight,
            languages: profile.languages ?? [],
          })
        : undefined
    ),
    [profile?.gender, profile?.languages, userAge, userHeight],
  );

  return (
    <Protected>
      <AppShell frameClassName="form-frame">
        <AppHeader title="Set Preferences" backTo="/onboarding/profile" mobileHidden desktopHidden />
        {profileQuery.isLoading ? (
          <BrandedLoading message="Preparing your preferences" />
        ) : profileQuery.isError || !profile || !initialPreferences ? (
          <AppStateScreen
            icon={AlertCircle}
            title="Preferences could not be prepared"
            body="We could not load the profile details needed to prepare your starting ranges."
            tone="sad"
          >
            <button className="button secondary" type="button" onClick={() => void profileQuery.refetch()}>
              Try again
            </button>
          </AppStateScreen>
        ) : ready ? (
          <OnboardingReadyMoment onContinue={() => navigate("/app/match", { replace: true })} />
        ) : (
          <>
            <AppHero
              eyebrow="Step 2 of 2"
              title="Set your preferences"
              description="Help us find your best matches. You can update these 3 times per month."
              icon={Heart}
              className="settings-sub-hero"
            />
            <PreferencesForm
              initial={initialPreferences}
              onboardingStep="preferences"
              onboardingBackTo="/onboarding/profile"
              userAge={userAge}
              userHeight={userHeight}
              profileLanguages={profile.languages ?? []}
              submitLabel="Complete Setup"
              onSubmit={async (preferences) => {
              await api.createPreferences(preferences);
              await queryClient.invalidateQueries({ queryKey: ["preferences"] });
              await queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
              setReady(true);
            }} />
          </>
        )}
      </AppShell>
    </Protected>
  );
}



function profileGalleryItems(profile: MatchProfile | Profile, showGallery = false) {
  const allPhotos = showGallery ? profilePhotoItems(profile) : [];
  const photos = profileGalleryPhotoItems(profile, allPhotos);

  if (showGallery && !allPhotos.length) {
    return Array.from({ length: 10 }, (_, index) => ({
      id: `preview-photo-${index + 1}`,
      index,
      isPreview: true,
    }));
  }

  return photos.map((photo, index) => ({
    id: photo.id || `photo-${index}`,
    index,
    isPreview: false,
  }));
}

function ProfileAvatar({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const location = useLocation();
  const photos = showGallery ? profilePhotoItems(profile) : [];
  const avatarPhotoId = profilePicturePhotoId(profile, photos);
  const viewerPhotos = photos.map((photo) => photo.id);
  const avatarViewerPhotos = avatarPhotoId && !viewerPhotos.includes(avatarPhotoId)
    ? [avatarPhotoId, ...viewerPhotos]
    : viewerPhotos;
  const contents = (
    <ProfilePhoto
      photoId={avatarPhotoId}
      variant="full"
      loading="eager"
    />
  );

  return avatarPhotoId ? (
    <Link
      className="profile-hero-avatar profile-hero-avatar--interactive"
      to={buildPhotoViewerUrl(avatarViewerPhotos, avatarPhotoId)}
      state={{ from: location.pathname }}
      aria-label={`Open ${profile.firstName}'s profile photo`}
    >
      {contents}
    </Link>
  ) : (
    <div className="profile-hero-avatar" aria-label={`${profile.firstName} profile placeholder`}>
      {contents}
    </div>
  );
}

function ProfileHero({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const location = useLocation();
  const photos = profileGalleryItems(profile, showGallery);
  const viewerPhotos = photos.filter((photo) => !photo.isPreview).map((photo) => photo.id);
  const photoCountClass = `photo-count-${Math.min(photos.length, 10)}`;
  const photoViewerState = { from: location.pathname };

  return (
    <div className={`profile-hero ${photos.length ? "has-gallery" : "is-placeholder"} ${photos.length > 1 ? "has-mosaic" : ""} ${photoCountClass}`}>
      <div
        className="profile-hero-gallery"
        role={viewerPhotos.length ? "region" : undefined}
        aria-roledescription={viewerPhotos.length ? "carousel" : undefined}
        aria-label={viewerPhotos.length > 1 ? `${profile.firstName}'s ${viewerPhotos.length} photos. Swipe horizontally to browse.` : viewerPhotos.length ? `${profile.firstName}'s photo` : undefined}
      >
        {photos.length ? photos.map((photo) => (
          photo.isPreview ? (
            <div className="profile-hero-slide profile-photo-preview" data-photo-index={photo.index + 1} key={photo.id} aria-hidden="true" />
          ) : (
            <Link to={buildPhotoViewerUrl(viewerPhotos, photo.id)} state={photoViewerState} className="profile-hero-slide" key={photo.id}>
              <ProtectedImage
                photoId={photo.id}
                variant={photo.index === 0 ? "full" : "thumbnail"}
                loading={photo.index === 0 ? "eager" : "lazy"}
                fetchPriority={photo.index === 0 ? "high" : "auto"}
                alt={`${profile.firstName} photo ${photo.index + 1}`}
              />
            </Link>
          )
        )) : <div className="profile-hero-placeholder" aria-hidden="true" />}
      </div>
      {viewerPhotos.length > 1 && (
        <span className="profile-hero-count" aria-hidden="true">
          <ImageIcon size={13} />
          Swipe · {viewerPhotos.length} photos
        </span>
      )}
    </div>
  );
}

function ProfileDesktopPhotoGrid({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const location = useLocation();
  const galleryPhotos = profileGalleryItems(profile, showGallery);
  const viewerPhotos = galleryPhotos.filter((photo) => !photo.isPreview).map((photo) => photo.id);
  const photos = galleryPhotos.slice(1, 10);
  const photoViewerState = { from: location.pathname };

  if (!photos.length) return null;

  return (
    <div className={`profile-desktop-photo-grid photo-count-${Math.min(photos.length, 9)}`} aria-label={`${profile.firstName}'s additional photos`}>
      {photos.map((photo) => (
        photo.isPreview ? (
          <div className="profile-desktop-photo-tile profile-photo-preview" data-photo-index={photo.index + 1} key={photo.id} aria-hidden="true" />
        ) : (
          <Link to={buildPhotoViewerUrl(viewerPhotos, photo.id)} state={photoViewerState} className="profile-desktop-photo-tile" key={photo.id}>
            <ProtectedImage photoId={photo.id} alt={`${profile.firstName} photo ${photo.index + 1}`} />
          </Link>
        )
      ))}
    </div>
  );
}

function updateProfileScrollAffordance(card: HTMLElement, scroller: HTMLElement = card) {
  const scrollBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  const hasScrollBelow = scrollBottom > 2;
  if (card.hasAttribute("data-profile-scroll-below") !== hasScrollBelow) {
    card.toggleAttribute("data-profile-scroll-below", hasScrollBelow);
  }
}

type ProfileCollapsePixelVar = readonly [name: string, fromVar: string, toVar: string, fromFallback: number, toFallback: number];

type ProfileCollapseMetrics = {
  collapseDistance: number;
  avatarExpandedSize: number;
  avatarCollapsedSize: number;
  avatarExpandedTop: number;
  avatarCollapsedTop: number;
  pixelVars: Array<readonly [name: string, from: number, to: number]>;
};

const PROFILE_COLLAPSE_PIXEL_VARS = [
  ["--profile-title-pad-left", "--profile-title-pad-left-expanded", "--profile-title-pad-left-collapsed", 140, 82],
  ["--profile-title-min-height", "--profile-title-min-height-expanded", "--profile-title-min-height-collapsed", 88, 56],
  ["--profile-title-padding-y", "--profile-title-padding-y-expanded", "--profile-title-padding-y-collapsed", 13, 6],
  ["--profile-title-name-size", "--profile-title-name-size-expanded", "--profile-title-name-size-collapsed", 25, 19],
  ["--profile-title-location-size", "--profile-title-location-size-expanded", "--profile-title-location-size-collapsed", 15, 12.5],
  ["--profile-kicker-height", "--profile-kicker-height-expanded", "--profile-kicker-height-collapsed", 14, 0],
  ["--profile-kicker-gap", "--profile-kicker-gap-expanded", "--profile-kicker-gap-collapsed", 5, 0],
] as const satisfies readonly ProfileCollapsePixelVar[];

const profileCollapseFrames = new WeakMap<HTMLElement, number>();
const profileCollapseMetrics = new WeakMap<HTMLElement, ProfileCollapseMetrics>();
const profileCollapseLastProgress = new WeakMap<HTMLElement, number>();

function getProfileCollapseMetrics(element: HTMLElement): ProfileCollapseMetrics {
  const cached = profileCollapseMetrics.get(element);
  if (cached) return cached;

  const styles = getComputedStyle(element);
  const cssNumber = (name: string, fallback: number) => {
    const parsed = Number.parseFloat(styles.getPropertyValue(name));
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const hero = element.querySelector<HTMLElement>(".profile-hero");
  const heroHeight = hero?.offsetHeight || 180;
  const metrics = {
    collapseDistance: Math.max(96, heroHeight - 52),
    avatarExpandedSize: cssNumber("--profile-avatar-size-expanded", 112),
    avatarCollapsedSize: cssNumber("--profile-avatar-size-collapsed", 52),
    avatarExpandedTop: cssNumber("--profile-avatar-top-expanded", -28),
    avatarCollapsedTop: cssNumber("--profile-avatar-top-collapsed", 2),
    pixelVars: PROFILE_COLLAPSE_PIXEL_VARS.map(([name, fromVar, toVar, fromFallback, toFallback]) => [
      name,
      cssNumber(fromVar, fromFallback),
      cssNumber(toVar, toFallback),
    ] as const),
  };

  profileCollapseMetrics.set(element, metrics);
  return metrics;
}

function resetProfileCollapseMetrics(element: HTMLElement) {
  profileCollapseMetrics.delete(element);
  profileCollapseLastProgress.delete(element);
}

function cancelProfileCollapseFrame(element: HTMLElement) {
  const frame = profileCollapseFrames.get(element);
  if (frame !== undefined) window.cancelAnimationFrame(frame);
  profileCollapseFrames.delete(element);
  profileCollapseLastProgress.delete(element);
  profileCollapseMetrics.delete(element);
}

function renderProfileCollapse(element: HTMLElement) {
  const metrics = getProfileCollapseMetrics(element);
  const progress = Math.min(1, Math.max(0, element.scrollTop / metrics.collapseDistance));
  const previousProgress = profileCollapseLastProgress.get(element);
  const shouldWriteProgress = previousProgress === undefined || Math.abs(previousProgress - progress) > 0.001;

  if (shouldWriteProgress) {
    const interpolate = (from: number, to: number) => from + ((to - from) * progress);
    const avatarScale = metrics.avatarExpandedSize > 0
      ? interpolate(1, metrics.avatarCollapsedSize / metrics.avatarExpandedSize)
      : 1;

    element.style.setProperty("--profile-avatar-scale", avatarScale.toFixed(4));
    element.style.setProperty("--profile-avatar-translate-y", `${interpolate(metrics.avatarExpandedTop, metrics.avatarCollapsedTop) - metrics.avatarExpandedTop}px`);

    for (const [name, from, to] of metrics.pixelVars) {
      element.style.setProperty(name, `${interpolate(from, to).toFixed(1)}px`);
    }

    element.style.setProperty("--profile-kicker-opacity", `${Math.max(0, 1 - (progress * 1.35)).toFixed(3)}`);
    profileCollapseLastProgress.set(element, progress);
  }

  const collapsed = progress > 0.72;
  if (element.hasAttribute("data-profile-collapsed") !== collapsed) {
    element.toggleAttribute("data-profile-collapsed", collapsed);
  }
  updateProfileScrollAffordance(element);
}

function scheduleProfileCollapse(element: HTMLElement) {
  if (profileCollapseFrames.has(element)) return;

  const frame = window.requestAnimationFrame(() => {
    profileCollapseFrames.delete(element);
    renderProfileCollapse(element);
  });
  profileCollapseFrames.set(element, frame);
}

function updateProfileCollapse(event: UIEvent<HTMLElement>) {
  scheduleProfileCollapse(event.currentTarget);
}

function updateProfileRegionScroll(event: UIEvent<HTMLDivElement>) {
  const card = event.currentTarget.closest<HTMLElement>(".match-profile-card");
  if (card) updateProfileScrollAffordance(card, event.currentTarget);
}

function useProfileScrollAffordance() {
  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const region = card.querySelector<HTMLElement>(".profile-scroll-region");
    const currentScroller = () => region && region.scrollHeight > region.clientHeight + 2 ? region : card;
    let frame: number | undefined;

    const sync = () => {
      resetProfileCollapseMetrics(card);
      scheduleProfileCollapse(card);
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => updateProfileScrollAffordance(card, currentScroller()));
    };

    sync();
    window.addEventListener("resize", sync);

    const observer = "ResizeObserver" in window ? new ResizeObserver(sync) : null;
    observer?.observe(card);
    if (region) observer?.observe(region);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      cancelProfileCollapseFrame(card);
      window.removeEventListener("resize", sync);
      observer?.disconnect();
    };
  }, []);

  return cardRef;
}

function scrollProfileCardToTop(event: MouseEvent<HTMLElement>) {
  const target = event.target as HTMLElement;
  if (target.closest("a, button, input, select, textarea, [role='button']")) return;

  const card = event.currentTarget.closest<HTMLElement>(".match-profile-card");
  card?.scrollTo({ top: 0, behavior: "smooth" });
  card?.querySelector<HTMLElement>(".profile-scroll-region")?.scrollTo({ top: 0, behavior: "smooth" });
}

function ProfileCard({
  profile,
  connected = false,
  matchId,
  onDisconnect,
}: {
  profile: MatchProfile;
  connected?: boolean;
  matchId?: string;
  onDisconnect?: () => void;
}) {
  const profileCardRef = useProfileScrollAffordance();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportType, setReportType] = useState<ReportType>("inappropriate_behavior");
  const [reportDescription, setReportDescription] = useState("");
  const report = useMutation({
    mutationFn: () => api.reportMatch(reportType, reportDescription),
    onSuccess: () => {
      setReportDescription("");
    },
  });

  return (
    <article ref={profileCardRef} className={`profile-card match-profile-card ${connected ? "connected" : "pending"}`} onScroll={updateProfileCollapse}>
      <ProfileHero profile={profile} showGallery={connected} />
      <div className={`profile-title ${connected ? "connected" : ""}`} onClick={scrollProfileCardToTop}>
        <ProfileAvatar profile={profile} showGallery={connected} />
        <div className="profile-title-copy">
          <span className="profile-card-kicker">{connected ? "Connected match" : "Introduction profile"}</span>
          <h2>{profile.firstName}, {profile.age}</h2>
          <p>{profile.city}, {profile.country}</p>
        </div>
        {connected && matchId && (
          <Link
            className="icon-button profile-chat-button"
            to={`/app/chat/${matchId}`}
            aria-label={`Chat with ${profile.firstName}`}
            onClick={(event) => event.stopPropagation()}
          >
            <MessageCircle size={21} />
          </Link>
        )}
      </div>
      {connected && <ProfileDesktopPhotoGrid profile={profile} showGallery />}
      <div className={`profile-scroll-region ${connected ? "profile-scroll-region--connected" : ""}`} onScroll={updateProfileRegionScroll}>
        <section><h3>About</h3><p>{profile.aboutMe}</p></section>
        <section><h3>Looking For</h3><p>{profile.lookingFor}</p></section>
        <ProfileDetails profile={profile} />
        {connected && (
          <div className="profile-footer-actions safety-actions">
            <Button variant="ghost" onClick={() => setReportOpen((current) => !current)}><Flag size={15} /> Report</Button>
            {onDisconnect && <Button variant="ghost" onClick={onDisconnect}><X size={15} /> Disconnect</Button>}
          </div>
        )}
        {connected && reportOpen && (
          <form className="report-card" onSubmit={(event) => { event.preventDefault(); report.mutate(); }}>
            <div>
              <strong>Report this connection</strong>
              <p>Use this for safety concerns, harassment, scams, fake profiles, or inappropriate behavior.</p>
            </div>
            <ErrorBanner error={report.error} />
            {report.isSuccess && <div className="banner success">Report submitted. Thank you for helping keep Yara safe.</div>}
            <SelectField label="Issue" value={reportType} onChange={setReportType} options={REPORT_CATEGORIES} />
            <Field label="What happened?"><textarea value={reportDescription} onChange={(event) => setReportDescription(event.target.value)} minLength={10} maxLength={2000} required /></Field>
            <div className="report-actions">
              <Button type="button" variant="secondary" onClick={() => setReportOpen(false)}>Cancel</Button>
              <Button type="submit" loading={report.isPending}><Flag size={16} /> Submit Report</Button>
            </div>
          </form>
        )}
      </div>
    </article>
  );
}

function MatchHome() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const canFetch = isAuthenticated && !authLoading;
  const matchQuery = useQuery({ queryKey: ["match"], queryFn: api.getCurrentMatch, enabled: canFetch });
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse, enabled: canFetch });
  const shouldLoadLaunchStatus = canFetch
    && matchQuery.data === null
    && profileQuery.data?.matchingEligibility.isEligible === true;
  const launchStatusQuery = useQuery({
    queryKey: ["initial-matching-launch-status"],
    queryFn: api.getMatchingLaunchStatus,
    enabled: shouldLoadLaunchStatus,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const shouldLoadInvites = shouldLoadLaunchStatus
    && launchStatusQuery.data?.phase === "collecting"
    && !launchStatusQuery.data.foundingMilestoneReached;
  const invitesQuery = useQuery({
    queryKey: ["invites"],
    queryFn: api.getInvites,
    enabled: shouldLoadInvites,
  });
  const refreshMatchAvailability = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["match"] }),
    queryClient.invalidateQueries({ queryKey: ["profile"] }),
    queryClient.invalidateQueries({ queryKey: ["initial-matching-launch-status"] }),
  ]);
  const respond = useMutation({
    mutationFn: ({ id, response, declineFeedback }: { id: string; response: "accept" | "reject"; declineFeedback?: { category?: DeclineFeedbackCategory; details?: string } }) => api.respondToMatch(id, response, declineFeedback),
    onSuccess: async () => {
      setDeclineOpen(false);
      setDeclineCategory("");
      setDeclineDetails("");
      await refreshMatchAvailability();
    },
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineCategory, setDeclineCategory] = useState<DeclineFeedbackCategory | "">("");
  const [declineDetails, setDeclineDetails] = useState("");
  const declineDialogRef = useRef<HTMLElement>(null);
  const declineTriggerRef = useRef<HTMLButtonElement>(null);
  const respondingRef = useRef(respond.isPending);
  useEffect(() => { respondingRef.current = respond.isPending; }, [respond.isPending]);
  useEffect(() => {
    if (!declineOpen) return;
    const dialog = declineDialogRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : declineTriggerRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusableSelector = "button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex='-1'])";
    const frame = requestAnimationFrame(() => dialog?.querySelector<HTMLElement>("select")?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !respondingRef.current) {
        event.preventDefault();
        setDeclineOpen(false);
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(focusableSelector)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      requestAnimationFrame(() => previouslyFocused?.focus());
    };
  }, [declineOpen]);

  const match = matchQuery.data;
  const eligibility = profileQuery.data?.matchingEligibility;
  const matchPageTitle = match && match.status !== "connected" ? "Introduction" : "Match";

  useEffect(() => {
    if (!matchQuery.isFetched || !user?.id) return;
    const currentPhotoAccessMatchId = match
      && ["pending_a", "pending_b", "connected"].includes(match.status)
      ? match.id
      : null;
    const shouldPurge = protectedPhotoAccessTracker.update(
      user.id,
      currentPhotoAccessMatchId,
    );
    if (!shouldPurge) return;

    // Revoking server access cannot erase bytes that a browser already
    // downloaded, but it should not leave a former introduction's blobs in
    // Yara's in-memory cache after access ends or the introduction changes.
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] === "protected-photo",
    });
  }, [match?.id, match?.status, matchQuery.isFetched, queryClient, user?.id]);

  useEffect(() => {
    if (!match || match.status !== "connected" || match.hasSeenConnected) return;
    setShowConfetti(true);
    api.markMatchSeen(match.id)
      .then(() => {
        queryClient.setQueryData<CurrentMatch | null>(["match"], (current) => (
          current?.id === match.id ? { ...current, hasSeenConnected: true } : current
        ));
      })
      .catch(() => undefined);
  }, [match?.id, match?.status, match?.hasSeenConnected, queryClient]);

  if (authLoading) {
    return <LoadingScreen message="Opening Yara" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (matchQuery.isLoading || profileQuery.isLoading || (
    shouldLoadLaunchStatus
    && launchStatusQuery.isLoading
  )) {
    return <LoadingScreen message="Opening Yara" help="Still waiting for your match data. If this does not finish, refresh once or sign in again." />;
  }
  return (
    <Protected>
      <AppShell frameClassName="match-frame">
        <ConnectedCelebration active={showConfetti} />
        <AppHeader title={matchPageTitle} desktopHidden />
        <ErrorBanner error={matchQuery.error || profileQuery.error || (shouldLoadLaunchStatus ? launchStatusQuery.error : null) || (!declineOpen ? respond.error : null)} />
        {!match && (
          <NoMatchView
            isEligible={eligibility?.isEligible}
            reasons={eligibility?.reasons}
            reasonKeys={eligibility?.reasonKeys}
            launchStatus={launchStatusQuery.data}
            invitationsRemaining={invitesQuery.data?.invitesRemaining}
          />
        )}
        {match?.showRejectionMessage && (
          <RejectedMatchView onAcknowledge={() => api.acknowledgeRejection(match.id).then(refreshMatchAvailability)} />
        )}
        {match?.waitingOnOther && (
          <WaitingMatchView expiresAt={match.expiresAt} />
        )}
        {match?.profile && (match.status === "pending_a" || match.status === "pending_b") && !match.waitingOnOther && (
          <>
            <PendingMatchHeader
              expiresAt={match.expiresAt}
              actions={(
                <>
                  <Button ref={declineTriggerRef} variant="secondary" loading={respond.isPending} onClick={() => setDeclineOpen(true)}><X size={18} /> Decline</Button>
                  <Button loading={respond.isPending} onClick={() => respond.mutate({ id: match.id, response: "accept" })}><Heart size={18} /> Accept</Button>
                </>
              )}
            />
            <ProfileCard profile={match.profile} />
            {declineOpen && <div className="decline-feedback-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !respond.isPending) setDeclineOpen(false); }}>
              <section ref={declineDialogRef} className="decline-feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="decline-feedback-title" aria-describedby="decline-feedback-description">
                <button className="decline-feedback-close" type="button" aria-label="Cancel decline" disabled={respond.isPending} onClick={() => setDeclineOpen(false)}><X size={20}/></button>
                <p className="eyebrow">Optional feedback</p><h2 id="decline-feedback-title">Help us improve your introductions</h2>
                <p id="decline-feedback-description">Sharing a reason helps us tune matching. It’s completely optional and will never be shared with this person.</p>
                <ErrorBanner error={respond.error} />
                <label className="field"><span>Which part felt least compatible?</span><select value={declineCategory} onChange={(event) => setDeclineCategory(event.target.value as DeclineFeedbackCategory | "")}><option value="">Choose a reason (optional)</option><option value="age">Age range</option><option value="location">Location or relocation</option><option value="height">Height</option><option value="education">Education</option><option value="smoking">Smoking</option><option value="drinking">Drinking</option><option value="children">Children or family plans</option><option value="religion">Faith or religion</option><option value="interests">Shared interests</option><option value="profile">Profile information</option><option value="chemistry">Chemistry or attraction</option><option value="other">Something else</option></select></label>
                <label className="field"><span>Anything else? <small>Optional</small></span><textarea maxLength={500} value={declineDetails} onChange={(event) => setDeclineDetails(event.target.value)} placeholder="A short note can help us understand the signal."/><small>{declineDetails.length}/500</small></label>
                <div className="decline-feedback-actions"><Button variant="secondary" disabled={respond.isPending} onClick={() => respond.mutate({ id: match.id, response: "reject" })}>Decline without feedback</Button><Button loading={respond.isPending} onClick={() => respond.mutate({ id: match.id, response: "reject", declineFeedback: { category: declineCategory || undefined, details: declineDetails.trim() || undefined } })}>Decline introduction</Button></div>
              </section>
            </div>}
          </>
        )}
        {match?.profile && match.status === "connected" && (
          <div className="connected-match-top">
            <ProfileCard profile={match.profile} connected matchId={match.id} onDisconnect={() => api.disconnectMatch("not_interested").then(refreshMatchAvailability)} />
          </div>
        )}
      </AppShell>
    </Protected>
  );
}

function Chat() {
  const { matchId = "" } = useParams();
  const { accessToken, refreshUser, user } = useAuth();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<WSChatMessage[]>(() => {
    const queryMessages = queryClient.getQueryData<WSChatMessage[]>(["chat", matchId]);
    return queryMessages?.length ? queryMessages : matchId ? readCachedChatMessages(matchId) : [];
  });
  const [text, setText] = useState("");
  const [replyingTo, setReplyingTo] = useState<WSChatMessage | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<"connecting" | "connected" | "disconnected">("connecting");
  const [hasContentAbove, setHasContentAbove] = useState(false);
  const [hasContentBelow, setHasContentBelow] = useState(false);
  const [canScrollToLatest, setCanScrollToLatest] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const composerSyncFrameRef = useRef<number | undefined>(undefined);
  const composerSyncTimersRef = useRef<number[]>([]);
  const scrollRestoredRef = useRef(false);
  const wasNearBottomRef = useRef(true);
  const previousMessageCountRef = useRef(0);
  const previousMatchIdRef = useRef(matchId);
  const matchQuery = useQuery({ queryKey: ["match"], queryFn: api.getCurrentMatch });
  const chatHistoryQuery = useQuery({
    queryKey: ["chat", matchId],
    queryFn: () => api.getChatMessages(matchId),
    enabled: !!matchId,
    initialData: () => {
      const queryMessages = queryClient.getQueryData<WSChatMessage[]>(["chat", matchId]);
      if (queryMessages?.length) return queryMessages;
      const cachedMessages = matchId ? readCachedChatMessages(matchId) : [];
      return cachedMessages.length ? cachedMessages : undefined;
    },
    retry: false,
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const matchName = matchQuery.data?.profile?.firstName || "Match";
  const startConversation = (prompt: string) => {
    setText(prompt);
    if (composerRef.current) {
      composerRef.current.textContent = prompt;
      composerRef.current.focus();
      scheduleComposerViewportSync();
    }
  };

  const updateChatScrollState = (chatList: HTMLDivElement) => {
    const scrollBottom = getScrollBottom(chatList);
    wasNearBottomRef.current = scrollBottom <= CHAT_BOTTOM_THRESHOLD;
    setHasContentAbove(chatList.scrollTop > CHAT_FADE_TOP_THRESHOLD);
    setHasContentBelow(scrollBottom > CHAT_FADE_BOTTOM_THRESHOLD);
    setCanScrollToLatest(scrollBottom > CHAT_SCROLL_LATEST_THRESHOLD);
  };

  const syncComposerViewport = () => {
    const chatList = chatListRef.current;
    const composer = composerRef.current;
    if (!chatList || !composer) return;

    if (document.activeElement !== composer && !wasNearBottomRef.current) return;

    chatList.scrollTop = chatList.scrollHeight;
    updateChatScrollState(chatList);
    if (matchId) writeChatScroll(matchId, chatList.scrollTop);
  };

  const scheduleComposerViewportSync = () => {
    if (composerSyncFrameRef.current !== undefined) window.cancelAnimationFrame(composerSyncFrameRef.current);
    composerSyncTimersRef.current.splice(0).forEach((timer) => window.clearTimeout(timer));

    composerSyncFrameRef.current = window.requestAnimationFrame(syncComposerViewport);
    [90, 210, 360, 540].forEach((delay) => {
      composerSyncTimersRef.current.push(window.setTimeout(syncComposerViewport, delay));
    });
  };

  useEffect(() => () => {
    if (composerSyncFrameRef.current !== undefined) window.cancelAnimationFrame(composerSyncFrameRef.current);
    composerSyncTimersRef.current.splice(0).forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (!matchId) return;
    const matchChanged = previousMatchIdRef.current !== matchId;
    previousMatchIdRef.current = matchId;
    setMessages((current) => {
      if (!matchChanged && current.length) return current;
      const queryMessages = queryClient.getQueryData<WSChatMessage[]>(["chat", matchId]);
      return queryMessages?.length ? queryMessages : readCachedChatMessages(matchId);
    });
    scrollRestoredRef.current = false;
    wasNearBottomRef.current = true;
    previousMessageCountRef.current = 0;
    setHasContentAbove(false);
    setHasContentBelow(false);
    setCanScrollToLatest(false);
    let isDisposed = false;
    let reconnectTimer: number | undefined;
    let retryCount = 0;
    let isRetrying = false;

    const connect = () => {
      if (isDisposed) return;
      setConnectionStatus("connecting");
      const socket = new WebSocket(getWebSocketUrl(matchId, accessToken));
      wsRef.current = socket;

      const retryAfterRefresh = () => {
        if (isDisposed || isRetrying) return;
        if (retryCount >= 2) {
          setConnectionStatus("disconnected");
          return;
        }

        isRetrying = true;
        retryCount += 1;
        refreshUser()
          .catch(() => undefined)
          .finally(() => {
            if (isDisposed) return;
            reconnectTimer = window.setTimeout(() => {
              isRetrying = false;
              connect();
            }, 350);
          });
      };

      socket.onopen = () => {
        retryCount = 0;
        isRetrying = false;
        setConnectionStatus("connected");
        socket.send(JSON.stringify({ type: "get_history" }));
      };
      socket.onerror = retryAfterRefresh;
      socket.onclose = retryAfterRefresh;
      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "history") {
          const history = Array.isArray(data.messages) ? data.messages as WSChatMessage[] : [];
          setMessages((current) => history.length || !current.length ? history : current);
          if (history.length) queryClient.setQueryData(["chat", matchId], history);
        }
        if (data.type === "message") {
          setMessages((current) => {
            const next = mergeChatMessages([...current, data as WSChatMessage]);
            queryClient.setQueryData(["chat", matchId], next);
            return next;
          });
        }
      };
    };

    connect();
    return () => {
      isDisposed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [accessToken, matchId, queryClient, refreshUser]);

  useEffect(() => {
    if (!chatHistoryQuery.data) return;
    setMessages((current) => chatHistoryQuery.data.length || !current.length ? chatHistoryQuery.data : current);
  }, [chatHistoryQuery.data]);

  useEffect(() => {
    if (!matchId || !messages.length) return;
    writeCachedChatMessages(matchId, messages);
    queryClient.setQueryData(["chat", matchId], messages);
  }, [matchId, messages, queryClient]);

  useLayoutEffect(() => {
    if (!messages.length) return;

    const chatList = chatListRef.current;
    if (!chatList) return;

    if (!scrollRestoredRef.current) {
      const savedScroll = readChatScroll(matchId);
      const restoredScroll = savedScroll === null ? chatList.scrollHeight : Number(savedScroll);
      chatList.scrollTop = Number.isFinite(restoredScroll) ? restoredScroll : chatList.scrollHeight;
      scrollRestoredRef.current = true;
    } else if (messages.length > previousMessageCountRef.current && wasNearBottomRef.current) {
      chatList.scrollTop = chatList.scrollHeight;
    }

    previousMessageCountRef.current = messages.length;
    updateChatScrollState(chatList);
  }, [matchId, messages.length]);

  const rememberChatScroll = () => {
    const chatList = chatListRef.current;
    if (!chatList || !matchId) return;
    updateChatScrollState(chatList);
    writeChatScroll(matchId, chatList.scrollTop);
  };

  const scrollToLatestMessages = () => {
    const chatList = chatListRef.current;
    if (!chatList) return;
    chatList.scrollTo({ top: chatList.scrollHeight, behavior: "smooth" });
    wasNearBottomRef.current = true;
    setHasContentBelow(false);
    setCanScrollToLatest(false);
    if (matchId) writeChatScroll(matchId, chatList.scrollHeight);
  };

  const updateComposerText = (element: HTMLDivElement) => {
    const content = element.textContent || "";
    if (!content && element.innerHTML) element.textContent = "";
    setText(content);
  };

  const focusComposer = () => {
    window.requestAnimationFrame(() => {
      const composer = composerRef.current;
      if (!composer) return;

      composer.focus({ preventScroll: true });
      scheduleComposerViewportSync();

      const selection = window.getSelection();
      if (!selection) return;
      const range = document.createRange();
      range.selectNodeContents(composer);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
  };

  const send = () => {
    const content = (composerRef.current?.textContent || text).replace(/[\r\n]+/g, " ").trim();
    if (!content || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", content, replyToId: replyingTo?.id }));
    setText("");
    if (composerRef.current) composerRef.current.textContent = "";
    setReplyingTo(null);
    focusComposer();
  };
  const hasVisibleThread = messages.length > 0;
  const chatLooksReady = connectionStatus === "connected" || hasVisibleThread;

  return (
    <Protected>
      <AppShell frameClassName={`chat-frame ${hasContentAbove ? "" : "chat-frame-top-fade-hidden"} ${hasContentBelow ? "" : "chat-frame-bottom-fade-hidden"}`}>
        <header className="chat-header">
          <Link className="chat-header-avatar" to="/app/match" aria-label="View match profile">
            <ProfilePhoto photoId={matchQuery.data?.profile?.profilePictureId} />
          </Link>
          <div>
            <strong>{matchName}</strong>
            <p>{chatLooksReady ? "Swipe to reply" : "Trying to reconnect securely"}</p>
          </div>
          <Link className="chat-header-profile" to="/app/match" aria-label="View profile"><User size={18} /></Link>
        </header>
        {connectionStatus !== "connected" && !hasVisibleThread && (
          <div className={`connection-state ${connectionStatus}`}>
            <AlertCircle size={16} />
            {connectionStatus === "connecting" ? "Reconnecting to chat..." : "Chat connection paused"}
          </div>
        )}
        <ChatMessageList
          messages={messages}
          viewerUserId={user?.id ?? ""}
          partnerName={matchName}
          listRef={chatListRef}
          onScroll={rememberChatScroll}
          onReply={setReplyingTo}
          emptyState={(
            <section className={`chat-empty ${connectionStatus}`}>
              <MessageCircle size={30} />
              <h2>{connectionStatus === "disconnected" ? "Connection paused" : "Begin gently"}</h2>
              <p>{connectionStatus === "disconnected" ? "Chat is not available from this session." : "A thoughtful first note can make the connection feel less like an app and more like a real introduction."}</p>
              {connectionStatus !== "disconnected" && (
                <div className="chat-prompts" aria-label="Conversation starters">
                  {[
                    "What made your week meaningful?",
                    "What kind of service project is close to your heart?",
                    "Which book or prayer has stayed with you lately?",
                  ].map((prompt) => <button type="button" key={prompt} onClick={() => startConversation(prompt)}>{prompt}</button>)}
                </div>
              )}
            </section>
          )}
        />
        {canScrollToLatest && (
          <button className="chat-scroll-latest" type="button" onClick={scrollToLatestMessages} aria-label="Jump to latest messages" title="Jump to latest messages">
            <ArrowDown size={19} strokeWidth={2.45} />
          </button>
        )}
        <div className={`composer ${replyingTo ? "replying" : ""}`}>
          {replyingTo && (
            <div className="reply-preview">
              <div>
                <span><Reply size={13} /> Replying to {replyingTo.senderId === user?.id ? "yourself" : matchName}</span>
                <p>{replyingTo.content}</p>
              </div>
              <button type="button" onClick={() => setReplyingTo(null)} aria-label="Cancel reply"><X size={16} /></button>
            </div>
          )}
          <div
            ref={composerRef}
            className="composer-editor"
            contentEditable="plaintext-only"
            role="textbox"
            aria-label="Message"
            aria-multiline="false"
            data-placeholder="Message"
            enterKeyHint="send"
            inputMode="text"
            spellCheck
            onFocus={scheduleComposerViewportSync}
            onBlur={scheduleComposerViewportSync}
            onInput={(event) => {
              updateComposerText(event.currentTarget);
              scheduleComposerViewportSync();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button
            type="button"
            onPointerDown={(event) => event.preventDefault()}
            onClick={send}
            disabled={connectionStatus !== "connected" || !text.trim()}
          >
            <Send size={18} />
          </Button>
        </div>
      </AppShell>
    </Protected>
  );
}

function MyProfile() {
  const profileCardRef = useProfileScrollAffordance();
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse, retry: false });
  const profile = profileQuery.data?.profile;

  return (
    <Protected>
      <AppShell frameClassName="match-frame profile-frame">
        <AppHeader title="Profile" desktopHidden />
        {profileQuery.isLoading && <LoadingScreen />}
        {!profileQuery.isLoading && !profile && (
          <AppStateScreen icon={User} title="Create your profile" body="Share who you are so Yara can introduce you thoughtfully." tone="neutral">
            <Link className="button primary" to="/onboarding/profile">Start profile</Link>
          </AppStateScreen>
        )}
        {profile && (
          <article ref={profileCardRef} className="profile-card match-profile-card self-profile-card" onScroll={updateProfileCollapse}>
            <ProfileHero profile={profile} showGallery />
            <div className="profile-title self-profile-title" onClick={scrollProfileCardToTop}>
              <ProfileAvatar profile={profile} showGallery />
              <div className="profile-title-copy">
                <span className="profile-card-kicker">Your profile</span>
                <h2>{profile.firstName}, {ageFromDateOfBirth(profile.dateOfBirth)}</h2>
                {profile.occupation ? <p className="profile-title-occupation">{profile.occupation}</p> : null}
                <p>{formatLocationLine(profile.city, profile.state, profile.country)}</p>
              </div>
              <StackNavLink
                className="icon-button profile-settings-link"
                to="/settings"
                aria-label="Settings"
                onClick={(event) => event.stopPropagation()}
              >
                <Settings size={21} />
              </StackNavLink>
            </div>
            <ProfileDesktopPhotoGrid profile={profile} showGallery />
            <div className="profile-scroll-region" onScroll={updateProfileRegionScroll}>
              <section><h3>About</h3><p>{profile.aboutMe}</p></section>
              <section><h3>Looking For</h3><p>{profile.lookingFor}</p></section>
              <ProfileDetails profile={profile} />
              <div className="self-profile-actions">
                <Link className="button secondary" to="/settings/edit-profile" state={{ from: "/app/profile" }}>Edit Profile</Link>
                <Link className="button ghost" to="/settings/edit-preferences" state={{ from: "/app/profile" }}>Edit Preferences</Link>
              </div>
            </div>
          </article>
        )}
      </AppShell>
    </Protected>
  );
}

function EditProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse });

  return (
    <Protected>
      <AppShell frameClassName="form-frame">
        <ProfileEditorScreen
          mode="edit"
          profile={data?.profile}
          onSubmit={async (profile, photos) => {
            await saveProfileWithPhotoRollback(
              photos,
              () => api.updateProfile(profile),
            );
            await queryClient.invalidateQueries({ queryKey: ["profile"] });
            navigateWithStackDirection(navigate, getNavBackTarget(location.state, "/settings"), "back");
          }}
        />
      </AppShell>
    </Protected>
  );
}

function EditPreferences() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["preferences"], queryFn: api.getPreferences, retry: false });
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: api.getProfileResponse });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const userAge = profileQuery.data?.profile?.dateOfBirth
    ? ageFromDateOfBirth(profileQuery.data.profile.dateOfBirth)
    : undefined;
  const userHeight = profileQuery.data?.profile?.height;
  return (
    <Protected>
      <AppShell frameClassName="form-frame">
        <AppHeader
          title="Edit Preferences"
          backTo="/settings"
          trailing={dirty ? (
            <button className="screen-header-action" type="submit" form="preferences-form" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          ) : undefined}
        />
        <PreferencesForm
          variant="edit"
          userAge={userAge}
          userHeight={userHeight}
          profileLanguages={profileQuery.data?.profile?.languages ?? []}
          initial={data}
          submitLabel="Save Preferences"
          onDirtyChange={setDirty}
          onSavingChange={setSaving}
          onSubmit={async (preferences) => {
          await api.updatePreferences(preferences);
          await queryClient.invalidateQueries({ queryKey: ["preferences"] });
          navigateWithStackDirection(navigate, getNavBackTarget(location.state, "/settings"), "back");
        }} />
      </AppShell>
    </Protected>
  );
}

function SettingsPage() {
  const { logout, user } = useAuth();
  const [dark, setDark] = useState(() => getAppliedTheme() === "dark");
  useEffect(() => subscribeToThemeChanges((theme) => setDark(theme === "dark")), []);

  const toggleTheme = () => {
    setThemePreference(dark ? "light" : "dark");
  };

  const links = [
    ...(user?.role === "admin" || user?.role === "moderator"
      ? [{ to: "/admin/users", label: "Moderation", description: "Review signups, reports, and suspensions", icon: Shield } as const]
      : []),
    ...(user?.role === "admin"
      ? [
          { to: "/admin/matching-launch", label: "Launch Readiness", description: "Monitor the founding pool and first-round projection", icon: Rocket } as const,
          { to: "/admin/manual-matches", label: "Manual Matching", description: "Queue or immediately introduce a specific eligible pair", icon: HeartHandshake } as const,
          { to: "/admin/match-quality", label: "Match Quality", description: "Monitor match outcomes, scores, retention, and feedback", icon: Activity } as const,
        ]
      : []),
    { to: "/settings/edit-profile", label: "Edit Profile", description: "Photos, bio, location, and life details", icon: User },
    { to: "/settings/edit-preferences", label: "Match Preferences", description: "Age, family, faith, lifestyle, and deal breakers", icon: Heart },
    { to: "/settings/notifications", label: "Notifications", description: "Introduction, response, and message alerts", icon: Bell },
    { to: "/settings/install", label: "Add Yara to Home Screen", description: "Make this PWA launch like a native app", icon: Smartphone },
    { to: "/settings/invites", label: "Invites", description: "Bring trusted friends into the community", icon: Send },
    { to: "/settings/feedback", label: "Feedback", description: "Share bugs, ideas, or ways to help", icon: MessageCircle },
    { to: "/settings/account", label: "Account", description: "Email, password, matching availability, deletion, and sign out", icon: User },
    { to: "/settings/how-yara-works", label: "How Yara Works", description: "Understand introductions and safety rails", icon: Shield },
    { to: "/settings/resources", label: "Resources", description: "Questions and references behind the product", icon: BookMarked },
    { to: "/settings/safety", label: "Safety Center", description: "Community expectations and reporting", icon: Shield },
    { to: "/settings/terms", label: "Terms of Service", description: "Rules for using Yara", icon: Menu },
    { to: "/settings/privacy", label: "Privacy Policy", description: "How your information is handled", icon: Shield },
  ] as const;

  return (
    <Protected>
      <AppShell frameClassName="settings-frame settings-home-frame">
        <AppHeader title="Settings" backTo="/app/profile" desktopHidden />
        <section className="settings-hero">
          <div className="settings-hero-orb"><Settings size={22} /></div>
          <div>
            <span>Yara home base</span>
            <h1>Settings</h1>
            <p>{user?.email || "Manage your profile, privacy, invites, and app experience."}</p>
          </div>
        </section>
        <div className="settings-list settings-list--home">
          <button className="setting-row setting-row--rich" onClick={toggleTheme}>
            {dark ? <Moon /> : <Sun />}
            <span className="setting-copy"><strong>Theme</strong><small>Switch between warm light and midnight mode</small></span>
            <em>{dark ? "Dark" : "Light"}</em>
          </button>
          {links.map(({ to, label, description, icon: Icon }) => (
            <StackNavLink className="setting-row setting-row--rich" to={to} key={to}>
              <Icon />
              <span className="setting-copy"><strong>{label}</strong><small>{description}</small></span>
              <ChevronRight />
            </StackNavLink>
          ))}
          <button className="setting-row setting-row--rich danger" onClick={logout}>
            <LogOut />
            <span className="setting-copy"><strong>Logout</strong><small>End this session on this device</small></span>
          </button>
        </div>
        <p className="settings-version">Yara v{APP_VERSION}</p>
      </AppShell>
    </Protected>
  );
}

function AccountSettings() {
  const { logout, refreshUser, user } = useAuth();
  const queryClient = useQueryClient();
  const [pauseConfirmed, setPauseConfirmed] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [verificationCooldownUntil, setVerificationCooldownUntil] = useState(0);
  const [verificationCooldownSeconds, setVerificationCooldownSeconds] = useState(0);
  const matchingPaused = user?.status === "deactivated";
  const verificationCooldownLabel = verificationCooldownSeconds >= 120
    ? `${Math.ceil(verificationCooldownSeconds / 60)}m`
    : `${verificationCooldownSeconds}s`;

  const startVerificationCooldown = (seconds: number) => {
    const boundedSeconds = Math.min(3_600, Math.max(1, Math.ceil(seconds)));
    setVerificationCooldownUntil(Date.now() + boundedSeconds * 1_000);
    setVerificationCooldownSeconds(boundedSeconds);
  };

  useEffect(() => {
    if (verificationCooldownUntil <= 0) return;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((verificationCooldownUntil - Date.now()) / 1_000));
      setVerificationCooldownSeconds(remaining);
      if (remaining === 0) setVerificationCooldownUntil(0);
    };
    const timer = window.setInterval(update, 1_000);
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", update);
    update();
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [verificationCooldownUntil]);

  const emailVerification = useMutation({
    mutationFn: () => api.resendEmailVerification(),
    onSuccess: async (result) => {
      if (result.isEmailVerified) {
        setVerificationCooldownUntil(0);
        setVerificationCooldownSeconds(0);
        await Promise.allSettled([
          refreshUser(),
          queryClient.invalidateQueries({ queryKey: ["profile"] }),
        ]);
        return;
      }
      startVerificationCooldown(60);
    },
    onError: (verificationError) => {
      if (!(verificationError instanceof ApiError) || verificationError.statusCode !== 429) return;
      const retryAfter = Number((verificationError.data as { retryAfter?: unknown } | undefined)?.retryAfter);
      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        startVerificationCooldown(retryAfter);
      }
    },
  });
  const passwordReset = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error("Your account email is unavailable. Refresh Yara and try again.");
      return api.requestPasswordReset(user.email.trim().toLowerCase());
    },
  });
  const deactivate = useMutation({
    mutationFn: async () => {
      await api.deactivateAccount();
      await refreshUser();
      setPauseConfirmed(false);
    },
  });
  const reactivate = useMutation({
    mutationFn: async () => {
      await api.reactivateAccount();
      await refreshUser();
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      await api.deleteAccount();
      await logout();
    },
  });
  const emailIsVerified = user?.isEmailVerified === true
    || emailVerification.data?.isEmailVerified === true;

  return (
    <Protected>
      <AppShell frameClassName="settings-frame account-settings-frame">
        <AppHeader title="Account" backTo="/settings" />
        <div className="settings-document-scroll account-settings-scroll">
          <AppHero
            eyebrow="Your account"
            title="Account controls"
            description="Manage your verified email, password, matching availability, and account access."
            icon={User}
            className="settings-sub-hero"
          />
          <div className="account-stack">
            <article className="account-card">
              <div className="account-card-head">
                {emailIsVerified ? <CheckCircle2 /> : <MailCheck />}
                <div>
                  <h2>{emailIsVerified ? "Email verified" : "Verify your email"}</h2>
                  <p>
                    {emailIsVerified
                      ? `${user?.email} is confirmed. This matching requirement is complete.`
                      : `Verification is required before matching. Yara will send a private, 24-hour link to ${user?.email}.`}
                  </p>
                </div>
              </div>
              {!emailIsVerified && (
                <>
                  <ErrorBanner error={emailVerification.error} />
                  {emailVerification.isSuccess && (
                    <div className="banner success" role="status" aria-live="polite">
                      Verification email sent. Check your inbox, spam, or promotions folder and use the newest link.
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    loading={emailVerification.isPending}
                    disabled={verificationCooldownSeconds > 0 || !user?.email}
                    onClick={() => {
                      emailVerification.reset();
                      emailVerification.mutate();
                    }}
                  >
                    <MailCheck size={16} />{
                      verificationCooldownSeconds > 0
                        ? `Send another email in ${verificationCooldownLabel}`
                        : emailVerification.isSuccess
                          ? "Send another verification email"
                          : "Send verification email"
                    }
                  </Button>
                </>
              )}
            </article>

            <article className="account-card">
              <div className="account-card-head">
                <LockKeyhole />
                <div>
                  <h2>Change password</h2>
                  <p>Yara will email a private, one-time reset link to {user?.email}. The link expires in 60 minutes, and saving a new password signs out every device.</p>
                </div>
              </div>
              <ErrorBanner error={passwordReset.error} />
              {passwordReset.isSuccess && (
                <div className="banner success" role="status" aria-live="polite">
                  Reset link sent. Check your inbox, spam, or promotions folder.
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                loading={passwordReset.isPending}
                disabled={passwordReset.isSuccess || !user?.email}
                onClick={() => passwordReset.mutate()}
              >
                <MailCheck size={16} /> {passwordReset.isSuccess ? "Reset link sent" : "Email password reset link"}
              </Button>
            </article>

            <article className="account-card">
              <div className="account-card-head">
                <CirclePause />
                <div>
                  <h2>{matchingPaused ? "Matching is paused" : "Pause matching"}</h2>
                  <p>
                    {matchingPaused
                      ? "You can keep using Yara, but you will not be included in new matching rounds."
                      : "Stay signed in and keep your current introduction while opting out of new matching rounds."}
                  </p>
                </div>
              </div>
              {!matchingPaused && (
                <label className="attestation-row">
                  <input type="checkbox" checked={pauseConfirmed} onChange={(event) => setPauseConfirmed(event.target.checked)} />
                  <span>I understand I will not be included in new matching rounds.</span>
                </label>
              )}
              <ErrorBanner error={deactivate.error || reactivate.error} />
              {matchingPaused ? (
                <Button variant="secondary" loading={reactivate.isPending} onClick={() => reactivate.mutate()}>
                  <Heart size={16} /> Resume matching
                </Button>
              ) : (
                <Button variant="secondary" loading={deactivate.isPending} disabled={!pauseConfirmed} onClick={() => deactivate.mutate()}>
                  <CirclePause size={16} /> Pause matching
                </Button>
              )}
            </article>

            <article className="account-card danger-zone">
              <div className="account-card-head">
                <Trash2 />
                <div>
                  <h2>Delete account</h2>
                  <p>Permanently remove your profile and photos, end any current introduction, and sign out.</p>
                </div>
              </div>
              <Field label="Type DELETE to confirm"><input value={deleteText} onChange={(event) => setDeleteText(event.target.value)} autoComplete="off" /></Field>
              <ErrorBanner error={remove.error} />
              <Button variant="danger" loading={remove.isPending} disabled={deleteText !== "DELETE"} onClick={() => remove.mutate()}><Trash2 size={16} /> Delete Account</Button>
            </article>
          </div>
        </div>
      </AppShell>
    </Protected>
  );
}

function InstallSettings() {
  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Add to Home Screen" backTo="/settings" />
        <InstallInstructionSteps />
      </AppShell>
    </Protected>
  );
}

function NotificationSettings() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const install = useInstallPrompt();
  const { data, isLoading, error } = useQuery({ queryKey: ["notification-settings"], queryFn: api.getNotificationSettings });
  const [busy, setBusy] = useState(false);
  const [pushError, setPushError] = useState<Error | null>(null);
  const update = useMutation({
    mutationFn: (settings: Partial<NotificationSettings>) => api.updateNotificationSettings(settings),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notification-settings"] }),
  });

  const togglePush = async () => {
    if (!("Notification" in window)) return;
    setBusy(true);
    setPushError(null);
    try {
      if (Notification.permission === "denied") {
        setPushError(new Error(notificationSettingsInstructions(install.platform)));
        return;
      }

      if (Notification.permission === "granted" || Notification.permission === "default") {
        const ok = await subscribeToWebPush();
        if (ok) {
          await queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
        } else {
          setPushError(new Error(notificationSettingsInstructions(install.platform)));
        }
      }
    } catch (eventError) {
      setPushError(eventError instanceof Error ? eventError : new Error("Could not update browser notifications"));
    } finally {
      setBusy(false);
    }
  };

  const needsHomeScreenForIosPush = !install.isInstalled && (install.platform === "ios-safari" || install.platform === "ios-other");
  const notificationsBlocked = "Notification" in window && Notification.permission === "denied";

  const notificationLabels: Record<keyof Pick<NotificationSettings, "enablePushNotifications" | "newMatchNotifications" | "matchResponseNotifications" | "messageNotifications" | "adminNewSignupEmail" | "adminNewSignupPush">, string> = {
    enablePushNotifications: "Enable push notifications",
    newMatchNotifications: "New introduction alerts",
    matchResponseNotifications: "Introduction response alerts",
    messageNotifications: "Message alerts",
    adminNewSignupEmail: "New signup email alerts",
    adminNewSignupPush: "New signup push alerts",
  };

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const toggleKeys = (["enablePushNotifications", "newMatchNotifications", "matchResponseNotifications", "messageNotifications"] as const);
  const staffToggleKeys = isStaff ? (["adminNewSignupEmail", "adminNewSignupPush"] as const) : [];

  if (isLoading) return <LoadingScreen />;
  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Notifications" backTo="/settings" />
        <AppHero
          eyebrow="Alerts"
          title="Notifications"
          description="Choose how Yara reaches you for introductions, responses, and messages."
          icon={Bell}
          className="settings-sub-hero"
        />
        <ErrorBanner error={pushError || error || update.error} />
        {notificationsBlocked && (
          <article className="install-notification-note">
            <div><Bell aria-hidden="true" size={18} /><strong>Notifications are off</strong></div>
            <p>{notificationSettingsInstructions(install.platform)}</p>
          </article>
        )}
        {!notificationsBlocked && needsHomeScreenForIosPush && (
          <article className="install-notification-note">
            <div><Bell aria-hidden="true" size={18} /><strong>For iPhone Notifications</strong></div>
            <p>Add Yara to your Home Screen first, then return here to turn on notifications.</p>
            <Link className="text-link" to="/settings/install">Show Home Screen steps</Link>
          </article>
        )}
        <div className="settings-list">
          <button className="setting-row" onClick={togglePush} disabled={busy || needsHomeScreenForIosPush}><Bell /> Browser Push <span>{needsHomeScreenForIosPush ? "add first" : notificationsBlocked ? "blocked" : "Notification" in window ? Notification.permission : "unsupported"}</span></button>
          {data && toggleKeys.map((key) => (
            <label className="setting-row" key={key}>
              <Bell /> {notificationLabels[key]}
              <input type="checkbox" checked={!!data[key]} onChange={(event) => update.mutate({ [key]: event.target.checked })} />
            </label>
          ))}
          {data && isStaff && (
            <>
              <p className="settings-section-label">Moderation alerts</p>
              {staffToggleKeys.map((key) => (
                <label className="setting-row" key={key}>
                  <Shield /> {notificationLabels[key]}
                  <input type="checkbox" checked={!!data[key]} onChange={(event) => update.mutate({ [key]: event.target.checked })} />
                </label>
              ))}
            </>
          )}
        </div>
      </AppShell>
    </Protected>
  );
}

function Invites() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [inviteNotice, setInviteNotice] = useState("");
  const isAdmin = user?.role === "admin";
  const invites = useQuery({ queryKey: ["invites"], queryFn: api.getInvites });
  const create = useMutation({
    mutationFn: () => api.createInvite(email.trim() || undefined),
    onMutate: () => setInviteNotice(""),
    onSuccess: (data) => {
      setEmail("");
      if (data.emailSent) {
        setInviteNotice(`Invite email sent to ${data.invite.inviteeEmail}.`);
      } else {
        setInviteNotice(`Invite code ${data.invite.code} created. Copy its private signup link below.`);
      }
      queryClient.invalidateQueries({ queryKey: ["invites"] });
    },
  });
  const resend = useMutation({
    mutationFn: (invite: { code: string }) => api.resendInvite(invite.code),
    onMutate: () => setInviteNotice(""),
    onSuccess: (data) => {
      if (data.emailSent) {
        setInviteNotice(`Invite email resent to ${data.invite.inviteeEmail}.`);
      }
      queryClient.invalidateQueries({ queryKey: ["invites"] });
    },
  });
  const cancelInvite = useMutation({
    mutationFn: (invite: { code: string }) => api.deleteInvite(invite.code),
    onMutate: () => setInviteNotice(""),
    onSuccess: () => {
      setInviteNotice("Invite canceled and returned to your available invites.");
      queryClient.invalidateQueries({ queryKey: ["invites"] });
    },
  });
  const resendingCode = resend.isPending ? resend.variables?.code : null;
  const cancelingCode = cancelInvite.isPending ? cancelInvite.variables?.code : null;
  const inviteCount = invites.data?.invites.length ?? 0;

  const copyInviteLink = async (code: string) => {
    const signupUrl = new URL("/signup", window.location.origin);
    signupUrl.searchParams.set("inviteCode", code);
    const value = signupUrl.toString();

    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(value);
      setInviteNotice(`Signup link for ${code} copied.`);
    } catch {
      window.prompt("Copy this private signup link:", value);
    }
  };

  useEffect(() => {
    if (!inviteNotice) return;
    const timer = window.setTimeout(() => setInviteNotice(""), 5000);
    return () => window.clearTimeout(timer);
  }, [inviteNotice]);

  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Invites" backTo="/settings" />
        <AppHero
          eyebrow="Invite access"
          title={invites.data ? (invites.data.invitesUnlimited ? "Unlimited invites" : `${invites.data.invitesRemaining} available`) : "..."}
          description={isAdmin
            ? "Create single-use invite codes for trusted people, with or without sending an email."
            : "Bring trusted people into Yara with a single-use email invitation."}
          icon={Send}
          className="settings-sub-hero"
        />
        {inviteNotice && <div className="banner success invite-notice">{inviteNotice}</div>}
        <form className="invite-composer" onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
          <ErrorBanner error={invites.error || create.error || resend.error || cancelInvite.error} />
          <Field label={isAdmin ? "Invitee Email (optional)" : "Invitee Email"}>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              inputMode="email"
              type="email"
              required={!isAdmin}
              autoComplete="email"
              placeholder={isAdmin ? "Leave blank to create a shareable code" : undefined}
            />
          </Field>
          {isAdmin && (
            <p className="invite-composer-note">
              Leave the email blank for an Instagram DM. The resulting private link works once and expires after 30 days.
            </p>
          )}
          <Button className="invite-send-button" loading={create.isPending}>
            <Send size={16} aria-hidden="true" />
            {isAdmin && !email.trim() ? "Create Invite Link" : "Send Invite"}
          </Button>
        </form>
        <div className="invite-section-head">
          <h2>Sent invites</h2>
          <span>{inviteCount}</span>
        </div>
        <div className="invite-list">
          {invites.data?.invites?.map((invite) => (
            <div className="list-row invite-list-row" key={invite.code}>
              <div className="invite-list-row-main">
                <strong>{invite.code}</strong>
                <div className="invite-row-meta">
                  <span className="invite-row-email">{invite.inviteeEmail ?? "Not tied to an email"}</span>
                  <span className={`invite-lifecycle invite-lifecycle--${invite.status}`}>{invite.status}</span>
                </div>
              </div>
              <div className="invite-list-row-footer">
                {invite.inviteeEmail ? (
                  <span className={`invite-email-status invite-email-status--${invite.emailDeliveryStatus}`}>
                    {inviteEmailStatusLabel(invite.emailDeliveryStatus)}
                  </span>
                ) : (
                  <span className="invite-email-status">Code only</span>
                )}
                {isAdmin && invite.status === "pending" && (
                  <button
                    className="invite-copy-button"
                    type="button"
                    disabled={!!resendingCode || !!cancelingCode}
                    onClick={() => void copyInviteLink(invite.code)}
                  >
                    <Copy size={14} aria-hidden="true" />
                    Copy link
                  </button>
                )}
                {inviteCanShowResend(invite) && (
                  <button
                    className="invite-resend-button"
                    type="button"
                    disabled={!invite.canResendEmail || !!resendingCode || !!cancelingCode}
                    onClick={() => resend.mutate({ code: invite.code })}
                  >
                    <Send size={14} aria-hidden="true" />
                    {resendingCode === invite.code ? "Sending" : inviteResendButtonLabel(invite)}
                  </button>
                )}
                {inviteCanCancel(invite) && (
                  <button
                    className="invite-cancel-button"
                    type="button"
                    disabled={!!cancelingCode || !!resendingCode}
                    onClick={() => {
                      const recipient = invite.inviteeEmail ? ` for ${invite.inviteeEmail}` : "";
                      if (window.confirm(`Cancel invite ${invite.code}${recipient}?`)) {
                        cancelInvite.mutate({ code: invite.code });
                      }
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    {cancelingCode === invite.code ? "Canceling" : "Cancel"}
                  </button>
                )}
              </div>
            </div>
          ))}
          {invites.data && inviteCount === 0 && (
            <AppStateScreen icon={Send} title="No invites sent yet" body="When you invite someone, their code and delivery status will appear here." tone="neutral" />
          )}
        </div>
      </AppShell>
    </Protected>
  );
}

function Feedback() {
  const [category, setCategory] = useState<(typeof FEEDBACK_CATEGORIES)[number]["value"]>("general_feedback");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const mutation = useMutation({ mutationFn: () => api.submitFeedback(category, subject, message) });
  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title="Feedback" backTo="/settings" />
        <AppHero
          eyebrow="Help Yara grow"
          title="Share feedback"
          description="Report bugs, suggest ideas, or tell us how you would like to help."
          icon={MessageCircle}
          className="settings-sub-hero"
        />
        <form className="form card-form feedback-card" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
          <ErrorBanner error={mutation.error} />
          {mutation.isSuccess && <div className="banner success">Feedback sent. Thank you.</div>}
          <div className="field">
            <span>Category</span>
            <div className="segmented-options">
              {FEEDBACK_CATEGORIES.map((item) => (
                <button
                  className={category === item.value ? "selected" : ""}
                  type="button"
                  key={item.value}
                  onClick={() => setCategory(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <Field label="Subject"><input value={subject} onChange={(event) => setSubject(event.target.value)} required maxLength={200} /></Field>
          <Field label="Message"><textarea value={message} onChange={(event) => setMessage(event.target.value)} required minLength={10} maxLength={5000} /></Field>
          <Button loading={mutation.isPending}>Send</Button>
        </form>
      </AppShell>
    </Protected>
  );
}

function PhotoViewer() {
  const location = useLocation();
  const { photoId, photoIds, index } = parsePhotoViewerParams(location.search);
  const backTo = getNavBackTarget(location.state, "/app/match");
  return (
    <Protected>
      <AppShell frameClassName="photo-viewer-frame">
        <PhotoViewerScreen photoId={photoId} photoIds={photoIds} photoIndex={index} backTo={backTo} />
      </AppShell>
    </Protected>
  );
}

const TERMS_SUMMARY_ITEMS = [
  {
    title: "Independent & invite-only",
    body: "Yara is unofficial, independent, invite-only, and shaped by Baha'i principles without being endorsed by any Baha'i institution.",
  },
  {
    title: "Who can join",
    body: "You must be at least 18, legally single, truthful, and using the account for yourself.",
  },
  {
    title: "Community standards",
    body: "Treat people with respect. Harassment, threats, spam, impersonation, explicit content, and scraping are not allowed.",
  },
  {
    title: "Your responsibility",
    body: "You are responsible for your conversations and for making safe choices when meeting anyone outside Yara.",
  },
  {
    title: "Enforcement",
    body: "Yara can remove content, limit features, suspend accounts, or delete accounts when needed to protect the community.",
  },
  {
    title: "No guarantees",
    body: "Yara is provided as-is and does not promise matches, relationships, or uninterrupted service.",
  },
] as const;

const PRIVACY_SUMMARY_ITEMS = [
  {
    title: "What we collect",
    body: "Yara collects the information needed to run matching, chat, safety, invites, feedback, notifications, and account support.",
  },
  {
    title: "Profile visibility",
    body: "Your full profile and chat become visible to a match only when the matching flow allows it.",
  },
  {
    title: "No selling your data",
    body: "Yara does not sell your personal information or build an advertising profile from it.",
  },
  {
    title: "Location use",
    body: "Location is used for profile location and matching context. Browser location access is requested only when you choose to verify it.",
  },
  {
    title: "Your controls",
    body: "You can update profile information, change notifications, deactivate your account, or request deletion through the app.",
  },
] as const;

const TERMS_SECTIONS = [
  {
    title: "1. Agreement And Purpose",
    body: "These Terms of Service govern your access to and use of Yara, an invite-only matching app and website for thoughtful, values-led introductions. By creating an account or using Yara, you agree to these terms. If you do not agree, do not use Yara.",
  },
  {
    title: "2. Eligibility",
    body: "You must be at least 18 years old, legally single, legally able to agree to these terms, invited or otherwise approved to join, and allowed to use the service under applicable law. You must use Yara for yourself, provide truthful information, and may not use Yara if your account has been suspended, deactivated, deleted, or removed.",
  },
  {
    title: "3. Account Responsibility",
    body: "Provide accurate, current information about your identity, background, location, faith or interest in the Faith, relationship status, intentions, photos, and preferences. Keep your login credentials private. You are responsible for activity on your account and should report suspicious access or misuse through the app when available.",
  },
  {
    title: "4. Profile, Photos, And Messages",
    body: "You own the content you submit, but you give Yara permission to host, store, display, transmit, and process it so the service can operate. Do not upload content you do not have the right to share. Do not share another person's private information without permission.",
  },
  {
    title: "5. Community Rules",
    body: "Use Yara for respectful, lawful, personal connection. Do not harass, threaten, bully, impersonate, mislead, spam, solicit commercially, scrape data, bypass security, upload explicit or hateful content, or use the app to harm others.",
  },
  {
    title: "6. Matching And Safety",
    body: "Yara may introduce potential matches one at a time and may limit, pause, or end matches. Yara does not conduct background checks, does not verify every claim a user makes, and does not guarantee identity, faith status, relationship status, compatibility, conduct, marriage, or offline safety. Use good judgment, protect your privacy, and meet safely if you choose to meet someone.",
  },
  {
    title: "7. Faith Context And Independence",
    body: `${FAITH_GUIDED_NOTE} ${UNOFFICIAL_DISCLAIMER} Yara does not speak for the Baha'i community, does not replace Local Spiritual Assemblies or other Baha'i institutions, and does not provide religious, legal, medical, financial, or clinical advice.`,
  },
  {
    title: "8. Enforcement",
    body: "Yara may review reports and take action at its discretion, including warnings, content removal, feature limits, suspension, account deletion, or cooperation with legal authorities when appropriate.",
  },
  {
    title: "9. Invites And Access",
    body: "Invites may be limited, revoked, tied to an email address, or changed over time. You are responsible for inviting people thoughtfully. Misuse of invites can affect your account.",
  },
  {
    title: "10. Payments",
    body: "Yara may offer free, paid, trial, promotional, or early-user access. If paid features are introduced, pricing, renewals, cancellation, and refund terms will be shown before purchase.",
  },
  {
    title: "11. Service Changes",
    body: "Yara may change, suspend, or discontinue features. We will try to give reasonable notice for material changes, but the service may also change quickly while the product is young.",
  },
  {
    title: "12. Disclaimers And Limits",
    body: "Yara is provided as-is and as-available. To the fullest extent allowed by law, Yara disclaims warranties and is not responsible for user behavior, relationship outcomes, offline interactions, lost data, interruption, or indirect damages.",
  },
  {
    title: "13. Updates",
    body: "These terms may be updated as Yara evolves. Material updates will be communicated in a reasonable way. Continued use of Yara after an update means the updated terms apply.",
  },
];

const PRIVACY_SECTIONS = [
  {
    title: "1. Scope",
    body: "This Privacy Policy explains how Yara collects, uses, shares, protects, and retains information when you use the web app, PWA, API-backed features, matching, chat, invites, feedback, and notifications.",
  },
  {
    title: "2. Information You Provide",
    body: "Yara collects account details, invite information, profile fields, preferences, photos, interests, location text, feedback, reports, and chat messages you choose to send.",
  },
  {
    title: "3. Information Collected By Features",
    body: "Yara may process browser location when you choose location verification, files when you upload photos, push subscription details when you enable browser notifications, and technical details such as device/browser information, logs, and request metadata needed to operate and secure the service.",
  },
  {
    title: "4. How Information Is Used",
    body: "Yara uses information to create accounts, verify invites, build profiles, power matching, open connected chat, deliver notifications, maintain safety, respond to feedback, debug issues, prevent abuse, and improve the product.",
  },
  {
    title: "5. Profile And Match Visibility",
    body: "Profile visibility depends on match state. Limited information may appear during matching, and fuller profile or chat access opens only when the app flow allows it. Messages are shared with the person in the connected match.",
  },
  {
    title: "6. Sharing",
    body: "Yara does not sell personal information. Information may be shared with matched users as part of the service, with infrastructure and service providers that help run the app, when required by law, or when needed to protect users, Yara, or the public.",
  },
  {
    title: "7. Security",
    body: "Yara uses practical safeguards such as HTTPS, protected authentication cookies, access controls, and platform storage controls. No online service can guarantee perfect security, so use strong passwords and report suspicious activity.",
  },
  {
    title: "8. Retention",
    body: "Yara keeps information while your account is active or as needed for service, safety, legal, audit, backup, and abuse-prevention purposes. Some data may remain for a limited time after deletion in backups or logs.",
  },
  {
    title: "9. Your Choices",
    body: "You can update profile and preference information in the app, change notification settings, disable browser permissions in your browser, deactivate your account, and request account deletion in the app.",
  },
  {
    title: "10. Age Limit",
    body: "Yara is for adults 18 and older. It is not intended for children, and Yara does not knowingly collect information from children under 18.",
  },
  {
    title: "11. Changes",
    body: "This policy may change as the app evolves. Material changes will be communicated in a reasonable way, and continued use of Yara after an update means the updated policy applies.",
  },
];

function HowYaraWorksPanel() {
  return (
    <div className="timeline app-timeline">
      {HOW_YARA_WORKS_STEPS.map((step, index) => {
        const Icon = step.icon;
        return (
          <article className={`timeline-card ${step.tone}`} key={step.title}>
            <div className="timeline-icon"><Icon size={22} /></div>
            <div>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{step.title}</strong>
              <p>{step.body}</p>
            </div>
          </article>
        );
      })}
      <article className="process-note">
        <Info size={20} />
        <div>
          <strong>No endless swiping</strong>
          <p>Yara is intentionally paced: one thoughtful introduction, private answers, and chat only when both people choose to connect.</p>
        </div>
      </article>
    </div>
  );
}

function LegalSummaryInAppPanel({
  kind,
  items,
  publicPath,
  linkText,
}: {
  kind: "terms" | "privacy";
  items: readonly { title: string; body: string }[];
  publicPath: string;
  linkText: string;
}) {
  const hero = kind === "terms"
    ? {
        eyebrow: "Terms of Service",
        title: "The rules",
        description: "How Yara works, what we expect from members, and what the service does not promise.",
        icon: FileText,
      }
    : {
        eyebrow: "Privacy Policy",
        title: "Your data",
        description: "What Yara collects, how it is used, and the choices you have in the app.",
        icon: Shield,
      };
  const HeroIcon = hero.icon;

  return (
    <div className="resources-in-app legal-in-app">
      <AppHero
        eyebrow={hero.eyebrow}
        title={hero.title}
        description={hero.description}
        icon={HeroIcon}
        className="settings-sub-hero resources-in-app-hero"
      />
      <p className="resources-in-app-disclaimer">
        Plain-English summary—not the legally controlling document.
      </p>

      <section className="resources-in-app-section" aria-labelledby={`legal-${kind}-summary-heading`}>
        <div className="resources-in-app-section-head">
          <h2 id={`legal-${kind}-summary-heading`}>At a glance</h2>
          <span>{items.length}</span>
        </div>
        <p className="resources-in-app-section-lede">
          {kind === "terms"
            ? "Core expectations for using Yara thoughtfully and safely."
            : "How your information is handled inside matching, chat, and account features."}
        </p>
        <div className="resources-in-app-stack">
          {items.map((item) => (
            <article className="resources-in-app-card" key={item.title}>
              <header className="resources-in-app-card-head">
                <div className="resources-in-app-card-icon" aria-hidden="true">
                  <HeroIcon size={22} />
                </div>
                <div>
                  <h3>{item.title}</h3>
                </div>
              </header>
              <p className="resources-in-app-card-copy">{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="resources-in-app-section" aria-labelledby={`legal-${kind}-full-heading`}>
        <div className="resources-in-app-section-head resources-in-app-section-head--sub">
          <h2 id={`legal-${kind}-full-heading`}>Full document</h2>
        </div>
        <div className="settings-list resources-in-app-links">
          <Link className="setting-row setting-row--rich" to={publicPath}>
            <ExternalLink />
            <span className="setting-copy">
              <strong>{linkText}</strong>
              <small>Complete legal text</small>
            </span>
            <ChevronRight />
          </Link>
        </div>
      </section>
    </div>
  );
}

function LegalPage({ kind }: { kind: "terms" | "privacy" | "how" }) {
  if (kind === "how") {
    return (
      <Protected>
        <AppShell frameClassName="settings-frame">
          <AppHeader title="How Yara Works" backTo="/settings" />
          <div className="settings-document-scroll legal-panel--in-app">
            <HowYaraWorksPanel />
          </div>
        </AppShell>
      </Protected>
    );
  }

  const copy = kind === "terms"
    ? {
      title: "Terms of Service",
      items: TERMS_SUMMARY_ITEMS,
      publicPath: "/terms",
      linkText: "Read full Terms of Service",
    }
    : {
      title: "Privacy Policy",
      items: PRIVACY_SUMMARY_ITEMS,
      publicPath: "/privacy",
      linkText: "Read full Privacy Policy",
    };

  return (
    <Protected>
      <AppShell frameClassName="settings-frame">
        <AppHeader title={copy.title} backTo="/settings" />
        <div className="settings-document-scroll legal-panel--in-app">
          <LegalSummaryInAppPanel
            kind={kind}
            items={copy.items}
            publicPath={copy.publicPath}
            linkText={copy.linkText}
          />
        </div>
      </AppShell>
    </Protected>
  );
}

function FullLegalPage({ kind }: { kind: "terms" | "privacy" }) {
  const isTerms = kind === "terms";
  const title = isTerms ? "Terms of Service" : "Privacy Policy";
  const intro = isTerms
    ? "These terms are written for Yara's current web app and PWA experience and describe the rules for using the service."
    : "This policy describes Yara's current web app and PWA data practices for accounts, profiles, matching, chat, safety, invites, feedback, and notifications.";
  const sections = isTerms ? TERMS_SECTIONS : PRIVACY_SECTIONS;
  return (
    <main className="public-shell legal-public-shell">
      <section className="public-panel legal-public-panel">
        <AppHeader title={title} backTo="/" />
        <article className="legal full-legal">
          <p className="legal-date">Last updated May 27, 2026</p>
          <p>{intro}</p>
          {sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </section>
          ))}
        </article>
      </section>
    </main>
  );
}

function PushRefreshBridge() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "yara:push-refresh") {
        queryClient.invalidateQueries({ queryKey: ["match"] });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["initial-matching-launch-status"] });
        queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, [queryClient]);
  return null;
}

export default function App() {
  return (
    <InstallPromptProvider>
      <NotificationOnboardingPrompt />
      <PushRefreshBridge />
      <HashScroll />
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/tutorial" element={<Tutorial />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/safety" element={<SafetyPage />} />
        <Route path="/terms" element={<FullLegalPage kind="terms" />} />
        <Route path="/privacy" element={<FullLegalPage kind="privacy" />} />
        <Route path="/onboarding/profile" element={<OnboardingOnly><CreateProfile /></OnboardingOnly>} />
        <Route path="/onboarding/preferences" element={<OnboardingOnly><SetupPreferences /></OnboardingOnly>} />
        <Route path="/app/match" element={<RequireOnboarding><MatchHome /></RequireOnboarding>} />
        <Route path="/app/chat/:matchId" element={<RequireOnboarding><Chat /></RequireOnboarding>} />
        <Route path="/app/profile" element={<RequireOnboarding><MyProfile /></RequireOnboarding>} />
        <Route path="/app/photo" element={<RequireOnboarding><PhotoViewer /></RequireOnboarding>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/edit-profile" element={<EditProfile />} />
        <Route path="/settings/edit-preferences" element={<EditPreferences />} />
        <Route path="/settings/install" element={<InstallSettings />} />
        <Route path="/settings/notifications" element={<NotificationSettings />} />
        <Route path="/settings/invites" element={<Invites />} />
        <Route path="/settings/feedback" element={<Feedback />} />
        <Route path="/settings/account" element={<AccountSettings />} />
        <Route path="/settings/how-yara-works" element={<LegalPage kind="how" />} />
        <Route path="/settings/resources" element={<AppResourcesPage />} />
        <Route path="/settings/safety" element={<AppSafetyPage />} />
        <Route path="/settings/terms" element={<LegalPage kind="terms" />} />
        <Route path="/settings/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
        <Route path="/admin/users" element={<AdminModerationListRoute><AdminUsersPage /></AdminModerationListRoute>} />
        <Route path="/admin/users/:id/matches/:matchId/chat" element={<AdminModerationChatRoute><ModerationChatReviewPage /></AdminModerationChatRoute>} />
        <Route path="/admin/users/:id" element={<AdminModerationDetailRoute><AdminUserDetailPage /></AdminModerationDetailRoute>} />
        <Route path="/admin/matching-launch" element={<AdminSettingsDashboardRoute><InitialMatchingLaunchPage /></AdminSettingsDashboardRoute>} />
        <Route path="/admin/manual-matches" element={<AdminSettingsDashboardRoute><ManualMatchingPage /></AdminSettingsDashboardRoute>} />
        <Route path="/admin/match-quality" element={<AdminSettingsDashboardRoute><MatchQualityPage /></AdminSettingsDashboardRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </InstallPromptProvider>
  );
}
